import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ListMinus,
  Loader2,
  Microscope,
  MoreVertical,
  Pencil,
  RotateCw,
  Share2,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import type { JSX, MouseEvent, ReactNode } from "react";
import {
  Children,
  Suspense,
  createContext,
  lazy,
  memo,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link, useOutletContext, useSearchParams } from "react-router";
import remarkGfm from "remark-gfm";

import type { ChatShellContext } from "@/components/app-shell";
import {
  AttachmentComposer,
  type ReadyAttachment,
  filesFromClipboard,
} from "@/components/chat/AttachmentComposer";
import { AttachmentList } from "@/components/chat/AttachmentList";
import { type Citation, SourcesBlock } from "@/components/chat/CitationCard";
import { CitationDetailContext, renderCitationTokens } from "@/components/chat/CitationToken";
import { DictationButton } from "@/components/chat/DictationButton";
import { FactProposalCard } from "@/components/chat/FactProposalCard";
import { MermaidBlock } from "@/components/chat/MermaidBlock";
import { MermaidStreamingSkeleton } from "@/components/chat/MermaidStreamingSkeleton";
import type { MinimapItem } from "@/components/chat/Minimap";
import { MinimapBottomSheet } from "@/components/chat/MinimapBottomSheet";
import { PipelineSteps, StepsTree, formatTotalElapsed } from "@/components/chat/PipelineSteps";
import { RefineKnowledgeBanner } from "@/components/chat/RefineKnowledgeBanner";
import {
  RefineKnowledgeCard,
  type RefineKnowledgeCardState,
} from "@/components/chat/RefineKnowledgeCard";
import { ResponseAssetGrid } from "@/components/chat/ResponseAssetGrid";
import { RightDock } from "@/components/chat/RightDock";
import {
  SessionContextSheet,
  SessionContextTrigger,
  sessionUsageQueryKey,
} from "@/components/chat/SessionContextSheet";
import { ShareTurnDialog } from "@/components/chat/ShareTurnDialog";
import { SourceDetailContent, SourceDetailSheet } from "@/components/chat/SourceDetailSheet";
import { UserQuestionsStreamingSkeleton } from "@/components/chat/UserQuestionsStreamingSkeleton";
import { VoiceTrainingButton } from "@/components/chat/VoiceTrainingButton";
import { ExpandedReportReader } from "@/components/reports/ExpandedReportReader";
import { ReportCard } from "@/components/reports/ReportCard";
import { ReportReader, reportRevisionQueryKey } from "@/components/reports/ReportReader";
import { RequireRole } from "@/components/require-role";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DraftDiff } from "@/components/ui/draft-diff";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/toast-provider";
import { ApiError } from "@/lib/api";
import {
  ACTIVE_SESSION_QUERY_KEY,
  type ActiveSession,
  type AnswerRating,
  type BackgroundProgressEvent,
  type ChatKnowledgeFeedback,
  type ChatMessageAttachment,
  type ChatMessageRead,
  type ChatSessionSummary,
  type ChatStreamEvent,
  type FactProposalState,
  MESSAGES_STALE_MS,
  REASONING_PREFERENCES,
  type ReasoningLevel,
  type ReasoningPreference,
  type StepState,
  type StepUsageWire,
  type ToolCall,
  type UserMemoryDraftState,
  type UserQuestions,
  approveUserMemoryDraft,
  assistantMetadataSchema,
  buildOptimisticSession,
  cancelBackgroundTask,
  cancelGeneration,
  cancelUserMemoryDraft,
  describeTool,
  generationSettled,
  getActiveSession,
  getGenerating,
  getMessages,
  grantStageAccess,
  knowledgeOptionsSchema,
  listStageGrants,
  messagesQueryKey,
  normalizeKnowledgeOptions,
  parentOf,
  parseStepUsage,
  prependOptimisticSession,
  rateAnswer,
  refineFactProposal,
  shouldShowGenerating,
  streamChat,
  streamSessionEvents,
  submitKnowledgeFeedback,
  subscribeSessionEvents,
  sumStepsUsage,
  unrateAnswer,
  userQuestionsSchema,
  withRefinedText,
} from "@/lib/chat";
import {
  CHAT_ALLOWED_FOR_USER_QUERY_KEY,
  type ChatAllowedForUser,
  chatAllowedModelsApi,
} from "@/lib/chat-allowed-models";
import type { ResponseAssetProjection } from "@/lib/chat-event-schemas";
import {
  DEFAULT_CHAT_MESSAGE_MAX_CHARS,
  MessageTooLongError,
  countCodePoints,
  isOverCap,
  messageTooLongMessage,
  shouldShowCounter,
  useChatMessageLimit,
} from "@/lib/chat-message-limit";
import { copyTextToClipboard } from "@/lib/clipboard";
import { formatTimestamp } from "@/lib/format";
import { formatCost } from "@/lib/format-cost";
import { LlmCostLimitError, type LlmCostScope, llmCostLimitMessage } from "@/lib/llm-cost-limits";
import {
  autoModelTooltip as buildAutoModelTooltip,
  isRoutingComposerActive,
  modelSelectorSentinelLabel,
  showEffortSelector as shouldShowEffortSelector,
  useModelRoutingMode,
} from "@/lib/model-routing-mode";
import { ModelSlot, modelSettingsApi } from "@/lib/model-settings";
import { parseRefineKnowledgeInput, parseRefineModeInput } from "@/lib/refine-mode";
import {
  type SessionBoundReportEdit,
  composerAfterReportEditCleanup,
  dockDetailForSession,
  reportRequestContextForSession,
} from "@/lib/report-session-context";
import type { ReportPointer, ReportRef, ReportRevisionRead } from "@/lib/reports";
import { type SourceFullResponse, getSourceFull, refineLlmSource } from "@/lib/sources";
import type { StoryPreparationRef } from "@/lib/story-preparation-ref";
import { Role } from "@/lib/supabase";
import { useIsXlViewport } from "@/lib/use-is-xl-viewport";
import { isInlineDockVisible, useRightDock } from "@/lib/use-right-dock";
import { cn } from "@/lib/utils";
import { isVoiceControlVisible, useVoiceMode } from "@/lib/voice-mode";
import type { VoiceTrainingEvent } from "@/lib/voice-training";

export interface TurnModel {
  provider: string;
  model_id: string;
  display_name?: string;
  /** True when a per-slot provider failover fallback leg served the turn.
   * Set live from the `answer` step's `usage.fallback_used` on
   * `step:answer:done` (see `resolveServedTurnModel`) and rehydrated from
   * `metadata.model.fallback_used` on reload — the meta strip badge renders
   * on both paths. Only present when true. */
  fallback_used?: boolean;
}

interface TurnUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd?: number;
  /** Per-turn input tokens served from the provider's prompt cache.
   * `cache_read_input_tokens` is the meaningful figure (surfaced on the
   * meta strip); `cache_creation_input_tokens` is ~0 outside Anthropic.
   * Carried on the top-level `usage` SSE event, not per-step usage. */
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

interface BackgroundReport {
  summary: string;
  fullReport: string;
  fullReportStatus: "complete" | "truncated";
  truncationReason?: string;
}

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
  thinking: string;
  citations: {
    id: string;
    title: string;
    preview: string;
    user_metadata?: string | null;
    source?: string;
    source_ref?: string;
    external_url?: string;
  }[];
  retrieval?: {
    vector: number;
    bm25: number;
    graph: number;
    // Optional (+`| undefined`) under exactOptionalPropertyTypes: older
    // persisted turns predate the sparse leg, so `meta.retrieval` may omit it.
    sparse?: number | undefined;
    fused: number;
  };
  usage?: TurnUsage;
  reasoningLevel?: ReasoningLevel;
  reasoningPreference?: ReasoningPreference;
  learnedFactId?: string;
  learnedFactAutoApproved?: boolean;
  factProposal?: FactProposalState;
  factProposals?: FactProposalState[];
  userMemoryDraft?: UserMemoryDraftState;
  userMemoryDrafts?: UserMemoryDraftState[];
  stageAccessRequest?: { stage: "demo" | "dev"; status: "pending" | "granted" };
  // Transient flag — true between the refine submit and the response.
  // Drives the inline "refinando…" indicator + disabled buttons on
  // the fact-proposal card so the user knows the request is in flight.
  refining?: boolean;
  // True while a server-side turn is still generating. This can be a synthetic
  // placeholder (before an assistant row exists) or a rehydrated partial
  // assistant row persisted with `metadata.generating=true`.
  generating?: boolean;
  // Voice (sandwich): true between the user's commit and their transcript, so
  // the user turn renders an "Analizando…" placeholder while STT runs.
  transcribing?: boolean;
  refineKnowledgeCard?: RefineKnowledgeCardState;
  error?: string;
  /** Machine-readable code paired with the live `error` SSE event (e.g.
   * `"generation_failed"`, `"turn_timeout"`, `"tool_iteration_cap_exceeded"`).
   * Drives whether the retry action renders — only codes in
   * `RETRYABLE_ERROR_CODES` do. See `recover-chat-turn-hard-failures`. */
  errorCode?: string;
  /** True when the orchestrator substituted a fallback message for what
   * would otherwise be an empty assistant row. The persisted `content` is
   * already the fallback text (rendered like any other answer); this flag
   * only drives the retry action shown alongside it. Rehydrated from
   * `metadata.generation_failed` on reload. See
   * `recover-chat-turn-hard-failures`. */
  generationFailed?: boolean;
  /** Failure cause paired with `generationFailed`. Rehydrated from
   * `metadata.reason`. Not currently rendered — reserved
   * for future diagnostics. */
  generationFailedReason?: string;
  /** Set when the send was rejected by the LLM cost limit (HTTP 429
   * `llm_cost_limit_exceeded`). Carries the breach scope so the turn renders a
   * scope-specific notice instead of a raw error string. */
  costLimitNotice?: LlmCostScope;
  /** Set when the send was rejected because the message exceeded the
   * effective `chat_message_max_chars` cap (HTTP 422 `message_too_long`).
   * Carries the backend-reported limit and actual length so the turn
   * renders a specific notice instead of a raw error string. */
  messageTooLong?: { maxChars: number; actualChars: number };
  /** True when the user cancelled this turn mid-stream (client abort). The
   * partial answer (if any) is kept; the UI shows a "cancelled" indicator
   * instead of an error. Rehydrated from `metadata.cancelled` on reload. */
  cancelled?: boolean;
  /** True on an assistant stub the reaper finalized after the plugin turn
   * was abandoned, or any other terminal failure. Empty content renders a
   * muted notice instead of an empty bubble; non-empty content keeps
   * rendering as today. Rehydrated from `metadata.failed` on reload. See
   * `surface-abandoned-plugin-turns`. */
  failed?: boolean;
  /** Machine-readable reason paired with `failed`, keyed by the UI to pick
   * notice copy. Rehydrated from `metadata.error` on reload — distinct from
   * `error` above, which carries a live client-side error message. See
   * `surface-abandoned-plugin-turns`. */
  failedError?: string;
  model?: TurnModel;
  toolCalls?: ToolCall[];
  // Citation markers (`[doc-N]` / `[self-N]`) the model fabricated —
  // backend strips them from the answer and surfaces the raw list
  // here so the UI can warn the user. Persisted with the assistant
  // message metadata so the chip survives a session reload.
  fabricatedCitations?: string[];
  /** Verbatim source spans the regurgitation filter redacted from this
   * answer because the user lacks `view_source`. Each entry is a line
   * count. Set live by the `source_redaction_warning` SSE event;
   * rehydrated from `metadata.source_redactions` on reload. */
  sourceRedactions?: number[];
  /** How many user-memory items were injected into this turn's context.
   * Shown in the details panel. Set live by the `user_memory_applied`
   * SSE event; rehydrated from `metadata.user_memory_applied` on reload. */
  memoryApplied?: number;
  /** The injected memory texts, exactly as injected — a deliberate
   * snapshot for historical fidelity (shown even if the items are later
   * edited or deleted). Absent on turns persisted before the snapshot
   * existed (those render the count-only row). */
  memoryAppliedTexts?: string[];
  /** Abnormal stop reason of the final generation round — the answer was cut
   * by the output token budget ("max_tokens") or the model refused
   * ("refusal"). Set live by the `truncated` SSE event; rehydrated from
   * `metadata.truncated` on reload. */
  truncated?: "max_tokens" | "refusal";
  steps: StepState[];
  /** `performance.now()` snapshot taken the instant the user submits.
   * Used by `PipelineSteps` to anchor the live elapsed counter so it
   * starts ticking immediately, even before the first SSE step event
   * arrives. Only set on the live turn — rehydrated turns leave it
   * undefined and the chip falls back to the backend-precise sum. */
  startedAt?: number;
  /** Attachments sent with this user turn. Rehydrated from message history. */
  attachments?: ChatMessageAttachment[];
  /** Minimap label generated asynchronously by the `generate_minimap_labels`
   * ARQ job (utility slot). `null` while pending / terminally failed / for
   * rows pre-dating the migration — the Minimap component shows a heuristic
   * fallback in those cases. Populated on session reload via `messageToTurns`;
   * live turns stay `null` until the user navigates away and back. */
  minimap_label?: string | null;
  /** ISO timestamp of the message. Rehydrated from `created_at` on reload;
   * live turns stamp the client clock at send time. Surfaced by the mobile
   * minimap sheet (the desktop rail ignores it). */
  created_at?: string;
  /** Persisted user quality signal for an assistant answer. */
  answerRating?: AnswerRating;
  /** Durable knowledge report state. It remains after the mutable rating is removed. */
  knowledgeFeedback?: ChatKnowledgeFeedback;
  /** Persisted `chat_messages.id` for this turn. Live streamed turns get a
   * client-side random `id`; this field carries the real row id (set by the
   * `done` SSE event live, or from `msg.id` on rehydrate) so per-message
   * endpoints like answer rating hit the right row without a reload. */
  messageId?: string;
  /** Set on the assistant message a background research task persists — the
   * id of the originating task. Used to link the report back to the turn that
   * delegated it (the trigger turn carries the same id as a `delegate_research`
   * tool-call `ref`). See `add-background-research-agent` Layer 4. */
  backgroundTaskId?: string;
  /** Terminal status of the background research task this report came from. */
  backgroundStatus?: "done" | "truncated" | "cancelled" | "failed";
  /** Split research payload: compact transcript summary plus full sidebar report. */
  backgroundReport?: BackgroundReport;
  /** Accumulated sub-agent LLM usage for the background report's model/cost
   * footer. Absent when the task failed before any LLM call. */
  backgroundUsage?: {
    model: string;
    promptTokens: number;
    completionTokens: number;
    costUsd: number;
  };
  /** Run wall-clock of the background task (seconds), for the final time on
   * the investigations bar. Absent on running tasks / before this field. */
  backgroundElapsedSeconds?: number;
  /** Stable pointer to an immutable executive report revision. */
  reportRef?: ReportRef;
  /** Required report turn finished without a durable revision (edit/create). */
  reportPublishFailed?: boolean;
  /** Pointer to a story preparation the `prepare_shortcut_story` tool created
   * or refined this turn, set from persisted metadata (mirrors `reportRef`). */
  storyPreparationRef?: StoryPreparationRef;
  /** Transient realtime voice status while the backend retrieves RAG context. */
  voiceSearch?: {
    status: "searching" | "done" | "stale" | "error";
    message: string;
  };
  /** Safe knowledge-asset projections for this assistant turn. A NEW sibling
   * field — never concatenated into `content`, which is what keeps
   * copy-answer (`CopyButton`, fed `turn.content`) and the markdown output
   * free of asset data. Set live by the `response_assets` SSE event;
   * rehydrated from `msg.response_assets` on reload (`messageToTurns`), so
   * the live turn and a reloaded session render the same grid. */
  responseAssets?: ResponseAssetProjection[];
}

export interface PersistedMessageIdBinding {
  userTurnId: string;
  assistantTurnId: string;
  userMessageId?: string;
  assistantMessageId?: string;
}

/** Attach database row ids without replacing optimistic render/scroll ids. */
export function bindPersistedMessageIds(
  turns: ChatTurn[],
  binding: PersistedMessageIdBinding,
): ChatTurn[] {
  if (binding.userMessageId === undefined && binding.assistantMessageId === undefined) {
    return turns;
  }
  return turns.map((turn) => {
    const messageId =
      turn.id === binding.userTurnId
        ? binding.userMessageId
        : turn.id === binding.assistantTurnId
          ? binding.assistantMessageId
          : undefined;
    if (messageId === undefined || turn.messageId === messageId) return turn;
    return { ...turn, messageId };
  });
}

// Lazy: the card (state badges, icons) renders only on a story preparation
// answer, and the entry chunk has almost no headroom under its budget.
const StoryPreparationCard = lazy(() =>
  import("@/components/chat/StoryPreparationCard").then((module) => ({
    default: module.StoryPreparationCard,
  })),
);

const ROLES = [Role.Viewer, Role.Contributor, Role.Admin] as const;

// How often to refresh while a "Respondiendo…" placeholder is shown, until the
// generating turn lands or its marker clears. See `add-chat-generating-indicator`.
const GENERATING_POLL_MS = 4000;
const BACKGROUND_CANCEL_POLL_MS = 2000;
const BACKGROUND_ACTIVE_POLL_MS = 5000;

/** A synthetic assistant turn that renders the "Respondiendo…" badge while a
 * server-side turn is still generating (its answer isn't persisted until the
 * stream completes). Replaced by the real turn on the next refresh. */
function makeGeneratingTurn(): ChatTurn {
  return {
    id: crypto.randomUUID(),
    role: "assistant",
    content: "",
    thinking: "",
    citations: [],
    steps: [],
    generating: true,
  };
}

export function makeVoiceTrainingTurn(
  id: string,
  role: "user" | "assistant",
  content: string,
): ChatTurn {
  return {
    id,
    role,
    content,
    thinking: "",
    citations: [],
    steps: [],
    created_at: new Date().toISOString(),
  };
}

/** Applies a live user transcript (delta or final) to the turn list. Empty
 * transcripts — VAD-committed audio the transcriber produced nothing for —
 * never render: no empty bubble is created, and a final that resolves to
 * empty removes the tentative bubble its deltas may have created. */
export function reduceVoiceUserTranscript(
  prev: ChatTurn[],
  update: { id: string; kind: "delta" | "final"; text: string },
): ChatTurn[] {
  const existing = prev.find((turn) => turn.id === update.id);
  const content =
    update.kind === "final" ? update.text : `${existing?.content ?? ""}${update.text}`;
  if (content.trim().length === 0) {
    return existing === undefined ? prev : prev.filter((turn) => turn.id !== update.id);
  }
  if (existing === undefined) {
    return [...prev, makeVoiceTrainingTurn(update.id, "user", content)];
  }
  // Real text arrived → drop the "Analizando…" placeholder state.
  return prev.map((turn) =>
    turn.id === update.id ? { ...turn, content, transcribing: false } : turn,
  );
}

/** Seeds the "Analizando…" placeholder user turn for a freshly committed
 * utterance. A new commit supersedes the previous turn (the relay cancels it),
 * so any still-empty placeholder it left behind — a pending user bubble or an
 * empty assistant "Respondiendo…" bubble — is dropped first, since it will now
 * receive no further events. Turns that already have content are kept. */
export function reduceVoiceSpeechCommitted(prev: ChatTurn[], id: string): ChatTurn[] {
  return [
    ...prev.filter(
      (turn) => !((turn.transcribing || turn.generating) && turn.content.length === 0),
    ),
    { ...makeVoiceTrainingTurn(id, "user", ""), transcribing: true },
  ];
}

/** Applies a voice search status to the turn list. Searches run concurrently
 * (only an explicit spoken cancellation discards one), so each status is
 * routed by `targetId` — the turn bound to the status' `search_ref`. Terminal
 * statuses without a known target fall back to the turn whose search is live
 * (events without a ref); if none exists they are dropped rather than
 * fabricating a turn. */
export function reduceVoiceSearchStatus(
  prev: ChatTurn[],
  update: { status: "searching" | "done" | "stale" | "error"; message: string },
  targetId: string | undefined,
): ChatTurn[] {
  const voiceSearch = { status: update.status, message: update.message };
  let id = targetId;
  if (id === undefined) {
    if (update.status === "searching") return prev;
    id = prev.find((turn) => turn.voiceSearch?.status === "searching")?.id;
    if (id === undefined) return prev;
  }
  const existing = prev.find((turn) => turn.id === id);
  // Keep `generating` set while a search runs: the turn is still on its way to
  // an answer, so the chat shows "Respondiendo…" — not the empty-turn "…"
  // fallback — until the first reply chunk lands and clears it.
  if (existing === undefined) {
    return [
      ...prev,
      { ...makeVoiceTrainingTurn(id, "assistant", ""), voiceSearch, generating: true },
    ];
  }
  return prev.map((turn) => (turn.id === id ? { ...turn, voiceSearch } : turn));
}

// ---------------------------------------------------------------------------
// ChatInputContext — passes setInput + setTurns down to deeply nested
// components (FactProposalCardWithContext) without prop-drilling through
// every AssistantTurn caller.
// ---------------------------------------------------------------------------

interface ChatInputContextValue {
  setInput: (value: string) => void;
  setTurns: (updater: (prev: ChatTurn[]) => ChatTurn[]) => void;
  /** Send an ordinary user chat message programmatically (e.g. a
   * `KnowledgeOptionsFence` chip click) without going through the composer
   * form. Mirrors what the form's submit handler does for a given message
   * string — same optimistic turn, same `streamChat` call. */
  sendMessage: (message: string) => Promise<void>;
  /** Active chat session; required for stage-grant mutations. */
  sessionId?: string | undefined;
  /** A refine-knowledge card stored `approvedText` as the item's text: what the
   * refine mode and the cards still pending compare with is now that text. */
  onRefineKnowledgeApproved?: ((itemId: string, approvedText: string) => void) | undefined;
}

export const ChatInputContext = createContext<ChatInputContextValue | null>(null);

function useChatInputContext(): ChatInputContextValue {
  const ctx = useContext(ChatInputContext);
  if (ctx === null) {
    throw new Error("ChatInputContext used outside provider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// MermaidStreamingContext — lets the markdown fence fallback (`MermaidFence`,
// the `className === "language-mermaid"` branch of `makeMarkdownComponents`'s
// `code` component) read the enclosing turn's live streaming flag without
// forcing `makeMarkdownComponents` to be rebuilt on every streamed chunk.
// Mirrors the `CitationDetailContext` pattern in `CitationToken.tsx`: the
// memoized component tree stays stable ([turn.citations, turn.id] deps
// unchanged), and only this small context consumer re-renders when
// `generating` flips. Default `generating: false` so a consumer rendered
// without a provider (tests, or history replay outside a turn) renders
// immediately rather than getting stuck on the streaming placeholder.
// ---------------------------------------------------------------------------

export const MermaidStreamingContext = createContext<{ generating: boolean }>({
  generating: false,
});

/** Sentinel value for the chat selector: "use the chat slot's bound model".
 * The streaming layer treats this as "no `model_override`" — the backend
 * resolves it server-side from `model_settings`. */
const AUTO_VALUE = "auto";
/** Native `<select>` sizes to the longest option. Cap so a long catalog
 * display name cannot stretch the composer toolbar and wrap the actions. */
export const CHAT_MODEL_SELECTOR_CLASSNAME =
  "min-w-0 max-w-[min(100%,16rem)] flex-1 truncate rounded-md border border-border bg-background px-2 py-1 text-body-3 sm:flex-none";
const SCROLL_FOLLOW_THRESHOLD_PX = 24;
/** Space above a jumped-to turn so sticky user bars / chrome do not cover it. */
export const MINIMAP_JUMP_TOP_OFFSET_PX = 56;

interface ScrollMetrics {
  scrollHeight: number;
  scrollTop: number;
  clientHeight: number;
}

interface ScrollFollowTargetMetrics {
  scrollHeight: number;
  spacerHeight: number;
  clientHeight: number;
}

export function isNearScrollBottom(
  metrics: ScrollMetrics,
  thresholdPx = SCROLL_FOLLOW_THRESHOLD_PX,
): boolean {
  return metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= thresholdPx;
}

export function getScrollFollowTarget(metrics: ScrollFollowTargetMetrics): number {
  const contentScrollHeight = metrics.scrollHeight - metrics.spacerHeight;
  return Math.max(0, contentScrollHeight - metrics.clientHeight);
}

/**
 * Mid-stream antigravity scroll while a new user prompt is pinned at the top.
 * - `pinScrollTop`: scroll position that puts the new user card at the top.
 * - `followTarget`: max scroll for the real content bottom (excludes spacer).
 *
 * While the answer still fits under the pin, stay pinned (never scroll *up*
 * into previous turns). Once the answer grows past the fold, follow the
 * content bottom downward.
 */
export function resolveAntigravityScrollTop(pinScrollTop: number, followTarget: number): number {
  return Math.max(0, Math.max(pinScrollTop, followTarget));
}

/**
 * Layout signature for the chat scroll pin effect. Intentionally omits
 * `answerRating` so optimistic thumbs votes / reason edits do not jump the
 * viewport to the bottom (regime 3 of the antigravity scroll effect).
 */
export function chatTurnsScrollSignature(turns: ChatTurn[]): string {
  return turns
    .map((turn) =>
      [
        turn.id,
        turn.role,
        String(turn.content.length),
        String(turn.thinking.length),
        String(turn.steps.length),
        String(turn.citations.length),
        turn.generating ? "1" : "0",
        turn.transcribing ? "1" : "0",
        turn.error ?? "",
        turn.cancelled ? "1" : "0",
        turn.truncated ?? "",
        turn.model ?? "",
        turn.backgroundTaskId ?? "",
      ].join("\u001f"),
    )
    .join("\u001e");
}

export function scrollTurnIntoView(container: HTMLElement, target: HTMLElement): void {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top = Math.max(
    0,
    container.scrollTop + targetRect.top - containerRect.top - MINIMAP_JUMP_TOP_OFFSET_PX,
  );
  container.scrollTo({ top, behavior: "smooth" });
}

/** The `[data-turn-id]` whose top edge is closest to `container`'s top edge —
 * "what is the user currently reading". Shared by the minimap sheet snapshot
 * and the continuous `?m=` mirroring (`add-session-urls-and-scroll-position`)
 * so both features agree on the same turn-visibility signal. */
export function closestVisibleTurnId(container: HTMLElement): string | null {
  const containerTop = container.getBoundingClientRect().top;
  let bestId: string | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const node of container.querySelectorAll<HTMLElement>("[data-turn-id]")) {
    const dist = Math.abs(node.getBoundingClientRect().top - containerTop);
    if (dist < bestDist) {
      bestDist = dist;
      bestId = node.dataset.turnId ?? null;
    }
  }
  return bestId;
}

/**
 * Pin `target` to the top of `container` (instant — used right after send so the
 * new user prompt lands at the top of the chat scroller).
 */
export function scrollElementToContainerTop(
  container: HTMLElement,
  target: HTMLElement,
  offsetPx = 0,
): number {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top = Math.max(0, container.scrollTop + targetRect.top - containerRect.top - offsetPx);
  container.scrollTop = top;
  return top;
}

/** Nearest ancestor that scrolls vertically (chat pane, sheet body, …). */
export function findVerticalScrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Smooth-scroll so the Q/A block starts at the top of the scroll parent.
 *
 * Measures `[data-turn-group]` (non-sticky), never the sticky card alone.
 * While stuck mid-answer the card's getBoundingClientRect is already at the
 * scroller top — using it would no-op. The group still has the real document Y
 * of the question start (often above the viewport).
 */
export function scrollUserTurnIntoFocus(target: HTMLElement, offsetPx = 0): boolean {
  const container = findVerticalScrollParent(target);
  if (!container) return false;
  const anchor = target.closest<HTMLElement>("[data-turn-group]") ?? target;
  const containerRect = container.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const top = Math.max(0, container.scrollTop + anchorRect.top - containerRect.top - offsetPx);
  container.scrollTo({ top, behavior: "smooth" });
  return true;
}

interface SessionSkeletonState {
  loadingSession: boolean;
  sessionListLoading: boolean;
  sessionId: string | undefined;
  shownSessionId: string | null;
  turnCount: number;
}

export function shouldShowSessionSkeleton(state: SessionSkeletonState): boolean {
  if (state.turnCount > 0) return false;
  if (state.loadingSession) return true;
  if (state.sessionListLoading && state.sessionId === undefined) return true;
  return state.sessionId !== undefined && state.shownSessionId !== state.sessionId;
}

interface ChatTurnInFlightState {
  sending: boolean;
  hasGeneratingTurn: boolean;
}

export function isChatTurnInFlight(state: ChatTurnInFlightState): boolean {
  return state.sending || state.hasGeneratingTurn;
}

interface CanSubmitChatState extends ChatTurnInFlightState {
  message: string;
  hasReadyAttachments: boolean;
  attachmentBlocked: boolean;
  /** Whether `message`'s code-point length exceeds the effective
   * `chat_message_max_chars` cap. Optional (defaults to `false`) so
   * existing call sites that don't care about the cap keep compiling.
   * Only gates a *non-empty* message — an attachment-only send (empty
   * message) stays exempt from the cap regardless of this flag. */
  messageOverCap?: boolean;
}

/** The refine-knowledge banner's current text is the item's own
 * `full_content` (its trimmed `raw_content`), verbatim — never a
 * reconstruction from `chunks[].text`, which is chunking's derived,
 * possibly-overlapping segmentation of the document and (independently of
 * any contextual-retrieval header) is not the same text as `raw_content`.
 * Falls back to the item's `title` only when `full_content` is `null`. */
export function deriveRefineKnowledgeCurrentText(full: SourceFullResponse): string {
  return full.item.full_content ?? full.item.title;
}

export function canSubmitChat(state: CanSubmitChatState): boolean {
  if (state.attachmentBlocked) return false;
  if (isChatTurnInFlight(state)) return false;
  if (state.message.length > 0 && state.messageOverCap) return false;
  return state.message.length > 0 || state.hasReadyAttachments;
}

function ChatInner(): JSX.Element {
  const {
    activeSessionId,
    setActiveSessionId,
    replaceActiveSessionId,
    hasSessions,
    sessionsLoading,
  } = useOutletContext<ChatShellContext>();
  const queryClient = useQueryClient();
  // Keep the per-session message cache coherent with `turns`: refresh paths that
  // rebuild turns from freshly-fetched persisted messages mirror them into the
  // cache, so a later cache-first switch back renders the up-to-date state
  // instead of a stale snapshot (a delivered background report / settled turn
  // would otherwise reappear stale for up to MESSAGES_STALE_MS). The
  // session-switch effect goes through `fetchQuery`, which already writes it.
  const cacheMessages = useCallback(
    (sid: string, messages: ChatMessageRead[]): void => {
      queryClient.setQueryData(messagesQueryKey(sid), messages);
    },
    [queryClient],
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [reportEditContext, setReportEditContext] = useState<SessionBoundReportEdit | null>(null);
  const [sending, setSending] = useState(false);
  // True while a voice-training session is connecting/listening; dictation
  // is disabled meanwhile (both capture the microphone).
  const [voiceTrainingActive, setVoiceTrainingActive] = useState(false);
  // Global voice mode (off | sandwich | e2e). Gates whether the voice control
  // is shown at all (hidden when off / still loading).
  const { data: voiceMode } = useVoiceMode();
  // Auto chat model routing mode (off | shadow | on). Shapes the composer:
  // when on/shadow, the sentinel option reads "Auto" and the effort selector
  // is hidden while Auto is selected. Failures / loading → treat as off.
  const { data: modelRoutingMode } = useModelRoutingMode();
  const routingComposerActive = isRoutingComposerActive(modelRoutingMode);
  // Effective `chat_message_max_chars` cap, via the authenticated
  // `/chat/limits` endpoint. Missing/errored query → the shipped default,
  // never blocks the composer on the network (see design.md §6 of
  // `raise-and-surface-chat-message-cap`).
  const { data: chatMessageLimit } = useChatMessageLimit();
  const chatMessageMaxChars = chatMessageLimit ?? DEFAULT_CHAT_MESSAGE_MAX_CHARS;
  // Unicode code-point length (not `.length`, which counts UTF-16 code
  // units and over-counts astral-plane characters such as emoji relative
  // to the backend's Python `len(str)`). Counted on the trimmed text because
  // `send` POSTs `input.trim()`, so a pasted trailing newline must not push
  // the composer over a cap the backend would accept. Drives the counter,
  // error state, and disabled-send gate below.
  const inputCodePointCount = countCodePoints(input.trim());
  const messageOverCap = isOverCap(inputCodePointCount, chatMessageMaxChars);
  const counterVisible = shouldShowCounter(inputCodePointCount, chatMessageMaxChars);
  // Sticky DOM node above the message list that VoiceTrainingButton portals
  // the active-session banner into. A callback ref so the portal target is in
  // state and re-renders the button once mounted.
  const [voiceBannerSlot, setVoiceBannerSlot] = useState<HTMLDivElement | null>(null);
  // Tracks which assistant turn is currently consuming the SSE stream so
  // `<PipelineSteps>` can stay expanded (and show a "preparando…" placeholder
  // before the first step event arrives) even during the initial Supabase
  // round-trips, when no step is yet `running`.
  const [streamingTurnId, setStreamingTurnId] = useState<string | null>(null);
  const sessionId = activeSessionId ?? undefined;
  // Correction, not user navigation — replace semantics (no history entry).
  // See `replaceActiveSessionId`'s docstring in app-shell.tsx for call sites.
  const setSessionId = useCallback(
    (id: string | undefined) => replaceActiveSessionId(id ?? null),
    [replaceActiveSessionId],
  );
  // Active refine-knowledge mode. `null` outside the mode. When set,
  // the chat renders the banner above the input, pre-fills the input
  // with the `[refinando knowledge_<id>] ` prefix, and routes the next
  // send to `refineLlmSource` instead of the streaming chat endpoint.
  const [refineKnowledgeMode, setRefineKnowledgeMode] = useState<{
    itemId: string;
    currentText: string;
  } | null>(null);
  const [refineKnowledgeError, setRefineKnowledgeError] = useState<string | null>(null);
  // The mode as of the latest render: a refinement still in flight when an approval
  // landed reads it when it answers, because the text the mode held when the
  // operator sent it is no longer what is stored.
  const refineKnowledgeModeRef = useRef(refineKnowledgeMode);
  refineKnowledgeModeRef.current = refineKnowledgeMode;
  // Tracks which session id the chat view is currently rendering, so the
  // hydration effect can tell "external change (sidebar click, new chat)"
  // from "we just received a session_id from our own SSE stream".
  const [shownSessionId, setShownSessionId] = useState<string | null>(null);
  // Synchronous counterpart to `shownSessionId` for the one URL transition
  // initiated by a brand-new chat's durable SSE `session` event. React Router
  // may commit the path before the state update, so the hydration effect
  // consumes this ref once to avoid reloading and clobbering the live turns.
  const locallyDurableSessionIdRef = useRef<string | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  // Unknown/foreign session id in the URL (the messages fetch 404s). Shows a
  // dismissible notice while the redirect to bare `/chat` takes effect — see
  // `add-session-urls-and-scroll-position`.
  const [sessionNotFoundNotice, setSessionNotFoundNotice] = useState(false);
  // True between the 404 redirect and the load of its target (the most
  // recent session), so that load skips the notice-clearing at the top of
  // the load effect — otherwise the banner would flash for one frame.
  const sessionNotFoundRedirectRef = useRef(false);
  // Right-side panels: a persistent dock on >= xl, lightbox sheets below xl.
  // `dock` holds the shared open + active-tab state (Contexto, Minimapa) with
  // localStorage persistence; `isXl` selects which container renders it.
  const isXl = useIsXlViewport();
  const dock = useRightDock(isXl);
  // Source/investigation detail is unified into the dock's `detail` layer: it
  // shows inside the dock on >= xl, or as a lightbox sheet on < xl / expanded.
  // Handlers are stable (dock.* are useCallback-backed) so memoized turns don't
  // re-render on every token.
  const openSourceDetail = useCallback(
    (id: string) => dock.openDetail({ kind: "source", id }),
    [dock.openDetail],
  );
  const openResearchDetail = useCallback(
    (taskId: string) => dock.openDetail({ kind: "research", taskId }),
    [dock.openDetail],
  );
  const dismissDetail = useCallback(() => {
    if (isXl && dock.detailExpanded) dock.collapseDetail();
    else dock.closeDetail();
  }, [isXl, dock.detailExpanded, dock.collapseDetail, dock.closeDetail]);
  // Turn the user was reading when the sheet opened (snapshot, see
  // `openMinimapSheet`). Drives the active-step highlight in the sheet.
  const [activeMinimapId, setActiveMinimapId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>(AUTO_VALUE);
  const [selectedReasoning, setSelectedReasoning] = useState<ReasoningPreference>("auto");
  const abortRef = useRef<AbortController | null>(null);
  const replayAbortRef = useRef<AbortController | null>(null);
  const voiceTrainingTurnIdsRef = useRef<{ userId: string | null; assistantId: string | null }>({
    userId: null,
    assistantId: null,
  });
  // Binds each live voice search (`search_ref` on `beflow.voice_search_status`
  // events) to the turn rendering its indicator, so concurrent searches route
  // their terminal statuses to the right turn.
  const voiceSearchTurnsRef = useRef<Map<string, string>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Index in `input` where the active dictation turn begins. Set on
  // `turn-start` and consumed by `turn-finalize` to splice the canonical
  // transcript over the tentative deltas.
  const dictationTurnStartRef = useRef<number | null>(null);
  // Antigravity scroll: captures the position (in scroll-coords) of the END
  // of the real content right before new turns are appended. The scroll effect
  // then pins scrollTop to that value so the new user message lands at the
  // very top of the viewport. The bottom spacer (rendered while antigravity is
  // active) provides the scroll room the browser otherwise would clamp away.
  // Prefer `pendingScrollToUserTurnIdRef` (exact node) over height math when set.
  const prevScrollHeightRef = useRef<number>(0);
  const pendingScrollToUserTurnIdRef = useRef<string | null>(null);
  /** Floor for scrollTop while antigravity is active (new user card at top). */
  const antigravityPinScrollTopRef = useRef<number>(0);
  const spacerRef = useRef<HTMLDivElement>(null);
  const [antigravityActive, setAntigravityActive] = useState(false);
  const autoFollowRef = useRef(true);
  const ignoreNextProgrammaticScrollRef = useRef(false);

  const PLACEHOLDERS = useMemo(
    () => [
      "Pregunta algo sobre BeFlow",
      "Pregunta algo sobre este sistema RAG",
      "Enseñale al RAG nuevo conocimiento",
      "Si no quieres escribir, usa el modo dictado",
      "Puedes adjuntar imágenes y documentos",
      "Puedes solicitar diagramas",
      "Prueba a consultar sobre el código fuente",
      "Puedes consultar sobre flujos, documentación, repositorios, Shortcut, Notion...",
    ],
    [],
  );
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);

  useEffect(() => {
    if (hasSessions || input.length > 0) return;
    const interval = setInterval(() => {
      setPlaceholderVisible(false);
      setTimeout(() => {
        setPlaceholderIndex((i) => (i + 1) % PLACEHOLDERS.length);
        setPlaceholderVisible(true);
      }, 150);
    }, 8000);
    return () => clearInterval(interval);
  }, [input, PLACEHOLDERS, hasSessions]);
  // Stable pending session id minted when there is no active session yet (new
  // chat). Both AttachmentComposer and streamChat use this id so uploads before
  // the first message land in the same session the backend will materialise on
  // POST /chat. The ref is reset whenever activeSessionId changes from null to a
  // real id (sidebar navigation or our own SSE session event) or back to null
  // (+ nuevo chat) so that each fresh new-chat view starts with a fresh UUID.
  const pendingSessionIdRef = useRef<string | null>(null);
  if (activeSessionId !== null) {
    // A real session is active — clear the pending id so that if the user later
    // clicks "+ nuevo chat" the next new-chat view gets a fresh UUID (not the one
    // that was used by the previous new-chat session).
    pendingSessionIdRef.current = null;
  }
  // The effective session id passed to AttachmentComposer and streamChat:
  //   - real id when a session is already active (sidebar pick or prior SSE turn)
  //   - minted pending id for a brand-new chat (stable across re-renders)
  if (activeSessionId === null && pendingSessionIdRef.current === null) {
    pendingSessionIdRef.current = crypto.randomUUID();
  }
  const effectiveSessionId: string | undefined =
    activeSessionId ?? pendingSessionIdRef.current ?? undefined;
  const visibleDockDetail = dockDetailForSession(dock.detail, effectiveSessionId);
  // Whether the dock renders as an inline fixed-width `<aside>` (vs an overlay
  // sheet). Filter report details synchronously so the first render after a
  // session change cannot pair an old report with the new session.
  const dockVisible = isInlineDockVisible({
    isXl,
    open: dock.open,
    detail: visibleDockDetail,
    detailExpanded: dock.detailExpanded,
  });
  const openReportDetail = useCallback(
    (pointer: ReportPointer): void => {
      if (effectiveSessionId === undefined) return;
      dock.openDetail({
        kind: "report",
        sessionId: effectiveSessionId,
        reportId: pointer.report_id,
        revisionNumber: pointer.revision_number,
      });
    },
    [dock.openDetail, effectiveSessionId],
  );
  const modifyReport = useCallback(
    (read: ReportRevisionRead): void => {
      if (effectiveSessionId === undefined) return;
      const base: ReportPointer = {
        report_id: read.revision.report_id,
        revision_number: read.revision.revision_number,
      };
      const draft = `Modifica el informe "${read.revision.definition.title}" v${base.revision_number}: `;
      setReportEditContext({ sessionId: effectiveSessionId, pointer: base, draft });
      setInput(draft);
      if (isXl && dock.detailExpanded) dock.collapseDetail();
      else dock.closeDetail();
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [dock.closeDetail, dock.collapseDetail, dock.detailExpanded, effectiveSessionId, isXl],
  );
  const previousEffectiveSessionIdRef = useRef(effectiveSessionId);
  useLayoutEffect(() => {
    if (previousEffectiveSessionIdRef.current === effectiveSessionId) return;
    previousEffectiveSessionIdRef.current = effectiveSessionId;
    dock.clearReportDetails();
    if (reportEditContext !== null && reportEditContext.sessionId !== effectiveSessionId) {
      setReportEditContext(null);
      setInput((current) => composerAfterReportEditCleanup(current, reportEditContext));
    }
  }, [dock.clearReportDetails, effectiveSessionId, reportEditContext]);

  // Attachment state lifted out of AttachmentComposer so the send handler
  // can read ready objects and the send button can check the blocked flag.
  const [readyAttachments, setReadyAttachments] = useState<ReadyAttachment[]>([]);
  const [attachmentBlocked, setAttachmentBlocked] = useState(false);
  // Imperative reset handle: called after a successful send to clear chips.
  const attachmentResetRef = useRef<(() => void) | null>(null);
  // Imperative handle to feed clipboard-pasted files through the composer's
  // upload pipeline (paste-to-attach).
  const attachmentAddFilesRef = useRef<((files: FileList) => void) | null>(null);

  const handleReadyAttachmentsChange = useCallback((attachments: ReadyAttachment[]) => {
    setReadyAttachments(attachments);
  }, []);

  const handleAttachmentBlocked = useCallback((blocked: boolean) => {
    setAttachmentBlocked(blocked);
  }, []);

  // Auto-grow the chat textarea so multi-line input doesn't trap the
  // user inside an internal scroll. Reset to `auto` first so
  // `scrollHeight` reflects the natural content height (not the
  // previously expanded one); the `min-h` / `max-h` Tailwind classes on
  // the element clamp the visible range.
  // biome-ignore lint/correctness/useExhaustiveDependencies: textarea must resize when input changes
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  // Focus the composer input when starting a new chat.
  useEffect(() => {
    if (activeSessionId === null) inputRef.current?.focus();
  }, [activeSessionId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateAutoFollow = () => {
      // Programmatic pins (new-user at top) must not be "corrected" away.
      if (ignoreNextProgrammaticScrollRef.current) {
        ignoreNextProgrammaticScrollRef.current = false;
        return;
      }
      const spacerHeight = spacerRef.current?.offsetHeight ?? 0;
      const followTarget = getScrollFollowTarget({
        scrollHeight: el.scrollHeight,
        spacerHeight,
        clientHeight: el.clientHeight,
      });
      // Ceiling: do not sit deep in the antigravity spacer. Floor: never
      // pull above the pinned new-user scroll (would re-show previous answer).
      const pin = antigravityPinScrollTopRef.current;
      const maxAllowed = Math.max(followTarget, pin);
      if (el.scrollTop > maxAllowed) {
        ignoreNextProgrammaticScrollRef.current = true;
        el.scrollTop = maxAllowed;
        autoFollowRef.current = true;
        return;
      }
      const effectiveScrollHeight = el.scrollHeight - spacerHeight;
      autoFollowRef.current = isNearScrollBottom({
        scrollHeight: effectiveScrollHeight,
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
      });
    };
    updateAutoFollow();
    el.addEventListener("scroll", updateAutoFollow, { passive: true });
    return () => el.removeEventListener("scroll", updateAutoFollow);
  }, []);

  // Antigravity scroll. Three regimes:
  //   1. A send just happened (prevScrollHeightRef > 0): jump to that position
  //      so the new user turn sits at the top of the viewport.
  //   2. Antigravity is active mid-stream: as tokens arrive, only scroll down
  //      enough to keep the real-content bottom inside the viewport (ignoring
  //      the spacer that lives below it). Short responses keep the user turn
  //      pinned to the top; long responses naturally follow the latest tokens.
  //   3. Otherwise (session hydration, edits to existing turns): pin to bottom.
  // useLayoutEffect (not useEffect): this pins scrollTop in response to the
  // same render that grows the stepper, which shrinks this scroll container
  // (flex-1 sibling of the stepper). Running after paint would let the
  // browser paint one frame with the new clientHeight but the stale
  // scrollTop, then correct it the next frame — a visible flicker on every
  // streamed token/step. Running before paint collapses it into one frame.
  // Depend on `scrollSignature` (not raw `turns`) so answer-rating votes do
  // not re-enter regime 3 and yank the viewport to the bottom.
  const scrollSignature = useMemo(() => chatTurnsScrollSignature(turns), [turns]);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `scrollSignature` is the intentional trigger; body reads `turnsRef` so rating-only turn mutations do not re-pin scroll.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const currentTurns = turnsRef.current;
    if (currentTurns.length === 0) {
      if (antigravityActive) setAntigravityActive(false);
      return;
    }
    // Regime 1a: after send, pin the brand-new user card to the scroller top
    // (exact DOM node — survives sticky/group layout better than height math).
    const pendingUserId = pendingScrollToUserTurnIdRef.current;
    if (pendingUserId) {
      const target = el.querySelector<HTMLElement>(
        `[data-user-turn="${CSS.escape(pendingUserId)}"]`,
      );
      if (target) {
        ignoreNextProgrammaticScrollRef.current = true;
        autoFollowRef.current = true;
        const pin = scrollElementToContainerTop(el, target, 0);
        antigravityPinScrollTopRef.current = pin;
        pendingScrollToUserTurnIdRef.current = null;
        prevScrollHeightRef.current = 0;
        return;
      }
      // Node not mounted yet — keep pending; fall through to height fallback once.
    }
    // Regime 1b: height snapshot fallback (refine / if selector missed).
    if (prevScrollHeightRef.current > 0) {
      ignoreNextProgrammaticScrollRef.current = true;
      autoFollowRef.current = true;
      el.scrollTop = prevScrollHeightRef.current;
      antigravityPinScrollTopRef.current = prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
      return;
    }
    if (antigravityActive) {
      if (!autoFollowRef.current) return;
      const spacerH = spacerRef.current?.offsetHeight ?? 0;
      const followTarget = getScrollFollowTarget({
        scrollHeight: el.scrollHeight,
        spacerHeight: spacerH,
        clientHeight: el.clientHeight,
      });
      // Floor at the pin so we never scroll back up into the previous turn
      // (that was re-showing old answer content above the new question).
      const next = resolveAntigravityScrollTop(antigravityPinScrollTopRef.current, followTarget);
      if (el.scrollTop !== next) {
        ignoreNextProgrammaticScrollRef.current = true;
        el.scrollTop = next;
      }
      return;
    }
    ignoreNextProgrammaticScrollRef.current = true;
    autoFollowRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [scrollSignature, antigravityActive]);

  // Restore scroll position from `?m=<message-id>` once per session view,
  // right after the messages render (`shownSessionId` catches up to
  // `sessionId`). Runs as a `useLayoutEffect` declared AFTER the bottom-pin
  // effect above so it overrides that effect's default bottom scroll within
  // the same commit — no visible jump from bottom to the restored turn. An
  // id absent from the session (stale/foreign `?m=`) is ignored silently;
  // the default bottom position stands. See
  // `add-session-urls-and-scroll-position`.
  const scrollRestoredForSessionRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally excludes `searchParams` from re-running mid-session (would fight the `?m=` mirroring effect below on every scroll); this restore is meant to fire once per session view.
  useLayoutEffect(() => {
    if (sessionId === undefined || sessionId !== shownSessionId) return;
    if (scrollRestoredForSessionRef.current === sessionId) return;
    scrollRestoredForSessionRef.current = sessionId;
    const m = searchParams.get("m");
    if (m === null) return;
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(`[data-turn-id="${CSS.escape(m)}"]`);
    if (!target) return; // unknown id — ignore, default (bottom) scroll stands
    scrollTurnIntoView(container, target);
  }, [sessionId, shownSessionId]);

  // Once the send (streaming or refine) finishes, collapse antigravity so the
  // bottom spacer goes away and the chat settles into its natural layout —
  // no large empty gap below the response.
  useEffect(() => {
    if (!sending && antigravityActive) {
      setAntigravityActive(false);
      antigravityPinScrollTopRef.current = 0;
    }
  }, [sending, antigravityActive]);

  // Rehydrate / clear turns when the active session id changes from outside
  // (sidebar click, `+ nuevo chat`, mount-time hydration). Skips when the
  // shown session already matches — that's the case during our own SSE
  // stream, where applyEvent already advanced both `activeSessionId` and
  // `shownSessionId` in lock-step.
  useEffect(() => {
    if (sessionId !== undefined && locallyDurableSessionIdRef.current === sessionId) {
      locallyDurableSessionIdRef.current = null;
      if (shownSessionId !== sessionId) setShownSessionId(sessionId);
      return;
    }
    if (sessionId === shownSessionId) return;
    // Switching to a different session: abort any in-flight chat stream for the
    // session we're leaving. The orchestrator runs detached and buffered
    // server-side (POST /chat survives client disconnect), so generation keeps
    // going and the generating-replay path re-attaches on return. We do NOT call
    // cancelGeneration — that would terminate the backend turn; we only drop the
    // client SSE. Without this abort, the orphaned stream keeps mutating shared
    // component state (turns/shownSessionId/sending) and races this rehydration,
    // leaving the returned-to session stuck on a loading skeleton.
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (sessionId === undefined) {
      setTurns([]);
      setShownSessionId(null);
      setLoadingSession(false);
      return;
    }
    let cancelled = false;
    // Clear a lingering not-found notice — except on the load immediately
    // after the 404 redirect: that load IS the "most recent session" the
    // banner refers to, so clearing here would reduce it to a one-frame
    // flash. It stays until dismissed or until the next session switch.
    if (sessionNotFoundRedirectRef.current) {
      sessionNotFoundRedirectRef.current = false;
    } else {
      setSessionNotFoundNotice(false);
    }
    const key = messagesQueryKey(sessionId);
    // Cache-first: if this session's messages are already cached (viewed
    // earlier this session, or warmed by hover prefetch in the sidebar),
    // render them immediately without blanking the view — returning to a
    // session feels instant. The background revalidation below still runs.
    // Only a session with no cached messages clears to the skeleton.
    const cached = queryClient.getQueryData<ChatMessageRead[]>(key);
    // A cached EMPTY list falls through to the skeleton + fetch path rather than
    // the instant branch: rendering 0 turns while `shownSessionId` still points
    // at the previous session would flash the skeleton (turnCount 0) for a
    // microtask until the `.then` advances it.
    if (cached !== undefined && cached.length > 0) {
      // Instant render from cache. Do NOT advance `shownSessionId` here: it is a
      // dependency of this effect, so setting it synchronously fires the cleanup
      // (`cancelled = true`) before the revalidation `.then` below resolves,
      // which would drop the fresh result (no `useQuery` observes this key, so a
      // cache write alone re-renders nothing). `shouldShowSessionSkeleton`
      // returns false as soon as `turnCount > 0`, so the cached turns already
      // suppress the skeleton without advancing `shownSessionId`; the `.then`
      // advances it once the revalidation lands.
      setTurns(cached.flatMap((msg) => messageToTurns(msg)));
      setLoadingSession(false);
    } else {
      setTurns([]);
      setLoadingSession(true);
    }
    // Fetch persisted messages (cache-first via `fetchQuery`: resolves instantly
    // from a fresh cache, refetches only when stale) + whether a turn is still
    // generating. If no assistant row exists yet (trailing unanswered user turn
    // + marker active), append a synthetic "Respondiendo…" placeholder. If a
    // partial assistant row already exists, `messageToTurns` rehydrates its
    // `metadata.generating=true` flag and the poll effect below swaps it for
    // the real answer once it lands. `getGenerating` failures degrade to "not
    // generating" so a hiccup never blocks rehydration.
    void Promise.all([
      queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => getMessages(sessionId),
        staleTime: MESSAGES_STALE_MS,
        retry: false,
      }),
      getGenerating(sessionId).catch(() => false),
    ])
      .then(([messages, generating]) => {
        if (cancelled) return;
        const base = messages.flatMap((msg) =>
          messageToTurns(msg, { rehydrateGenerating: generating }),
        );
        setTurns(
          shouldShowGenerating(messages, generating) ? [...base, makeGeneratingTurn()] : base,
        );
        setShownSessionId(sessionId);
        setLoadingSession(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Unknown or foreign session id: neutral not-found notice, redirect
          // to bare `/chat` (replace — this is a correction, not a user
          // navigation). Never render another user's session.
          setTurns([]);
          setShownSessionId(null);
          setLoadingSession(false);
          setSessionNotFoundNotice(true);
          sessionNotFoundRedirectRef.current = true;
          replaceActiveSessionId(null);
          return;
        }
        // Messages fetch failed. Establish the session anyway — advance
        // `shownSessionId` so a cold-load failure (turnCount 0, no cache) does
        // not leave the skeleton stuck on `shownSessionId !== sessionId` — and
        // keep any cache-first render already on screen.
        setShownSessionId(sessionId);
        setLoadingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, shownSessionId, queryClient, replaceActiveSessionId]);

  // While a generating turn is shown after a reload, keep the session updating
  // until the final assistant row lands. If this is still a synthetic placeholder
  // with no persisted content, replay the buffered SSE stream. If it is already
  // a partial assistant row, do NOT replay the buffer from offset 0 or tokens
  // already persisted in the row would duplicate in the UI; polling replaces the
  // row with the latest persisted partial/final content instead.
  const hasGeneratingTurn = turns.some((t) => t.generating);
  const turnInFlight = isChatTurnInFlight({ sending, hasGeneratingTurn });
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSessionId is a stable useState setter; Biome < 2 doesn't recognise it as stable. `turns` is intentionally excluded to prevent re-execution on every state update.
  useEffect(() => {
    if (sending) return;
    if (sessionId === undefined || sessionId !== shownSessionId) return;
    if (!hasGeneratingTurn) return;
    let cancelled = false;
    const controller = new AbortController();
    replayAbortRef.current = controller;

    // Capture the generating turn at effect start to avoid re-running the
    // effect on every turns update. A non-empty partial answer was already
    // persisted, so use polling only to avoid duplicating buffered tokens.
    const generatingTurn = turns.find((t) => t.generating);
    const generatingTurnId = generatingTurn?.id;
    const replayBufferedStream = generatingTurn !== undefined && generatingTurn.content === "";

    void (async () => {
      if (!generatingTurnId || !replayBufferedStream) return;
      try {
        for await (const event of streamSessionEvents(sessionId, { signal: controller.signal })) {
          if (cancelled) break;
          applyEvent(generatingTurnId, event, setSessionId, setTurns, chatCompatible);
        }
      } catch {
        // Stream error — fall back to the legacy polling path below.
      }
      // Stream ended (or errored). Refetch once to swap the synthetic turn
      // for the real persisted assistant row.
      if (!cancelled) {
        const [messages, generating] = await Promise.all([
          getMessages(sessionId),
          getGenerating(sessionId).catch(() => false),
        ]);
        cacheMessages(sessionId, messages);
        setTurns(
          messages.flatMap((msg) => messageToTurns(msg, { rehydrateGenerating: generating })),
        );
      }
    })();

    // Legacy polling as a safety net: if the SSE stream is blocked or
    // unavailable, polling still refreshes the session until settled.
    const interval = setInterval(() => {
      void Promise.all([getMessages(sessionId), getGenerating(sessionId).catch(() => false)]).then(
        ([messages, generating]) => {
          if (cancelled) return;
          if (!generationSettled(messages, generating)) {
            if (!replayBufferedStream) {
              setTurns(
                messages.flatMap((msg) => messageToTurns(msg, { rehydrateGenerating: generating })),
              );
            }
            return;
          }
          cacheMessages(sessionId, messages);
          setTurns(
            messages.flatMap((msg) => messageToTurns(msg, { rehydrateGenerating: generating })),
          );
        },
      );
    }, GENERATING_POLL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      if (replayAbortRef.current === controller) {
        replayAbortRef.current = null;
      }
      clearInterval(interval);
    };
  }, [cacheMessages, hasGeneratingTurn, sending, sessionId, shownSessionId]);

  // ---- Background-research live delivery (Layer 2 SSE → Layer 4 render) ----
  // A bump signals "a not-yet-shown background task finished"; the apply effect
  // below refetches the session messages (the report is already persisted) to
  // pull the pushed report into `turns`. Tracking the tick (rather than
  // refetching inline) lets us defer the refetch while a turn is streaming so
  // an in-flight turn isn't clobbered.
  const [bgEventTick, setBgEventTick] = useState(0);
  const [researchProgressByTask, setResearchProgressByTask] = useState<
    Map<string, BackgroundProgressEvent>
  >(() => new Map());
  const [cancelRequestedTaskIds, setCancelRequestedTaskIds] = useState<Set<string>>(
    () => new Set(),
  );
  const appliedBgTickRef = useRef(0);
  // The set of task ids whose report is already in `turns`, so catch-up replays
  // of already-shown reports don't trigger a needless refetch.
  const knownReportTaskIdsRef = useRef<Set<string>>(new Set());
  const knownReportRefsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ids = terminalBackgroundTaskIds(turns);
    knownReportTaskIdsRef.current = ids;
    knownReportRefsRef.current = new Set(
      turns.flatMap((turn) =>
        turn.reportRef ? [`${turn.reportRef.report_id}:${turn.reportRef.revision_number}`] : [],
      ),
    );
    setCancelRequestedTaskIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        if (next.delete(id)) changed = true;
      }
      return changed ? next : prev;
    });
  }, [turns]);
  const activeResearchTaskIds = useMemo(() => activeBackgroundTaskIds(turns), [turns]);

  // Hold a live subscription to this session's terminal-task notifications.
  // Reconnects with a fixed backoff if the stream drops; catch-up on (re)connect
  // recovers anything missed while disconnected.
  useEffect(() => {
    if (sessionId === undefined) return;
    const controller = new AbortController();
    let stopped = false;
    void (async () => {
      while (!stopped) {
        try {
          for await (const ev of subscribeSessionEvents(sessionId, {
            signal: controller.signal,
          })) {
            if (ev.type === "background_progress") {
              setResearchProgressByTask((prev) => {
                const next = new Map(prev);
                next.set(ev.task_id, ev);
                return next;
              });
            } else if (
              ev.type === "background_task" &&
              !knownReportTaskIdsRef.current.has(ev.task_id)
            ) {
              setCancelRequestedTaskIds((prev) => {
                if (!prev.has(ev.task_id)) return prev;
                const next = new Set(prev);
                next.delete(ev.task_id);
                return next;
              });
              setResearchProgressByTask((prev) => {
                if (!prev.has(ev.task_id)) return prev;
                const next = new Map(prev);
                next.delete(ev.task_id);
                return next;
              });
              setBgEventTick((t) => t + 1);
            }
          }
        } catch {
          // Network error or abort — fall through to the reconnect delay.
        }
        if (stopped) break;
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
    })();
    return () => {
      stopped = true;
      controller.abort();
    };
  }, [sessionId]);

  // Apply a pending notification by refetching the session messages. Deferred
  // while `sending` so a streaming turn (not yet persisted) isn't replaced;
  // runs once the turn settles. Guarded on `sessionId === shownSessionId` so it
  // never races the session-switch hydration above.
  useEffect(() => {
    if (sending) return;
    if (sessionId === undefined || sessionId !== shownSessionId) return;
    if (appliedBgTickRef.current === bgEventTick) return;
    appliedBgTickRef.current = bgEventTick;
    let cancelled = false;
    void getMessages(sessionId).then((messages) => {
      if (cancelled) return;
      cacheMessages(sessionId, messages);
      const nextTurns = messages.flatMap((msg) => messageToTurns(msg));
      setTurns(nextTurns);
      const newReport = [...nextTurns]
        .reverse()
        .find(
          (turn) =>
            turn.reportRef !== undefined &&
            !knownReportRefsRef.current.has(
              `${turn.reportRef.report_id}:${turn.reportRef.revision_number}`,
            ),
        )?.reportRef;
      if (newReport) {
        openReportDetail(newReport);
        void queryClient.invalidateQueries({
          queryKey: reportRevisionQueryKey(
            sessionId,
            newReport.report_id,
            newReport.revision_number,
          ),
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    bgEventTick,
    cacheMessages,
    openReportDetail,
    queryClient,
    sending,
    sessionId,
    shownSessionId,
  ]);

  // Safety net for missed terminal notifications: SSE is the primary path,
  // but active background tasks must still settle without requiring a reload.
  useEffect(() => {
    if (sending) return;
    if (sessionId === undefined || sessionId !== shownSessionId) return;
    if (activeResearchTaskIds.size === 0) return;
    let stopped = false;

    const refresh = async (): Promise<void> => {
      try {
        const messages = await getMessages(sessionId);
        if (stopped) return;
        const nextTurns = messages.flatMap((msg) => messageToTurns(msg));
        const terminalIds = terminalBackgroundTaskIds(nextTurns);
        const hasNewTerminal = [...activeResearchTaskIds].some((id) => terminalIds.has(id));
        if (!hasNewTerminal) return;
        cacheMessages(sessionId, messages);
        setTurns(nextTurns);
        setResearchProgressByTask((prev) => {
          if (prev.size === 0) return prev;
          let changed = false;
          const next = new Map(prev);
          for (const id of terminalIds) {
            if (next.delete(id)) changed = true;
          }
          return changed ? next : prev;
        });
      } catch {
        // SSE remains primary; the next fallback tick can try again.
      }
    };

    const interval = setInterval(() => {
      void refresh();
    }, BACKGROUND_ACTIVE_POLL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [activeResearchTaskIds, cacheMessages, sending, sessionId, shownSessionId]);

  useEffect(() => {
    if (sending) return;
    if (sessionId === undefined || sessionId !== shownSessionId) return;
    if (cancelRequestedTaskIds.size === 0) return;
    let stopped = false;

    const refresh = async (): Promise<void> => {
      const messages = await getMessages(sessionId);
      if (stopped) return;
      const nextTurns = messages.flatMap((msg) => messageToTurns(msg));
      const terminalIds = terminalBackgroundTaskIds(nextTurns);
      const hasRequestedTerminal = [...cancelRequestedTaskIds].some((id) => terminalIds.has(id));
      if (!hasRequestedTerminal) return;
      cacheMessages(sessionId, messages);
      setTurns(nextTurns);
      setCancelRequestedTaskIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const id of terminalIds) {
          if (next.delete(id)) changed = true;
        }
        return changed ? next : prev;
      });
    };

    void refresh();
    const interval = setInterval(() => {
      void refresh();
    }, BACKGROUND_CANCEL_POLL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [cacheMessages, cancelRequestedTaskIds, sending, sessionId, shownSessionId]);

  // Refine-knowledge entry point. Fires on every distinct
  // `?refine-knowledge=<id>` URL the chat is mounted at. Re-entering
  // the mode for the same id (user navigates away and back) is
  // intentional — refine state is ephemeral and the fresh fetch
  // refreshes the banner text from the current `raw_content`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSessionId is a stable useState setter; Biome < 2 doesn't recognise it as stable
  useEffect(() => {
    const param = searchParams.get("refine-knowledge");
    if (param === null) return;
    setRefineKnowledgeError(null);
    let cancelled = false;
    (async () => {
      try {
        const full = await getSourceFull(param);
        if (cancelled) return;
        const currentText = deriveRefineKnowledgeCurrentText(full);
        setRefineKnowledgeMode({ itemId: param, currentText });
        setInput(`[refinando knowledge_${param}] `);
        // Clear active session so the refine card mounts in a clean
        // view (welcome would otherwise show; once the user sends,
        // turns push the welcome away). Avoids the refine card landing
        // mid-conversation in an unrelated chat.
        setSessionId(undefined);
      } catch (err) {
        if (cancelled) return;
        setRefineKnowledgeError(
          err instanceof Error
            ? `ese conocimiento ya no está disponible (${err.message})`
            : "ese conocimiento ya no está disponible",
        );
        setRefineKnowledgeMode(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // After an approval the item's text is the approved one: the mode (the next
  // refinement's old side) and every other pending card of the item follow it,
  // so no diff compares with a text that has been replaced. The setters are
  // stable, so this callback is too and the context value keeps its identity.
  const handleRefineKnowledgeApproved = useCallback(
    (itemId: string, approvedText: string): void => {
      setRefineKnowledgeMode((mode) =>
        mode !== null && mode.itemId === itemId ? { ...mode, currentText: approvedText } : mode,
      );
      setTurns((prev) =>
        prev.map((turn) =>
          turn.refineKnowledgeCard?.status === "pending" &&
          turn.refineKnowledgeCard.itemId === itemId
            ? {
                ...turn,
                refineKnowledgeCard: { ...turn.refineKnowledgeCard, currentText: approvedText },
              }
            : turn,
        ),
      );
    },
    [],
  );

  function exitRefineKnowledgeMode(): void {
    setRefineKnowledgeMode(null);
    setRefineKnowledgeError(null);
    setInput("");
    if (searchParams.has("refine-knowledge")) {
      const next = new URLSearchParams(searchParams);
      next.delete("refine-knowledge");
      setSearchParams(next, { replace: true });
    }
  }

  // The chat selector reads from the curated allowlist (NOT the broader
  // catalog `list_enabled` endpoint). The allowlist guarantees every
  // returned row is chat-compatible by construction, so the previous
  // `compatible_slots` filter is unnecessary. See
  // `restrict-chat-slot-to-allowlist` change.
  const chatAllowedQuery = useQuery({
    queryKey: CHAT_ALLOWED_FOR_USER_QUERY_KEY,
    queryFn: () => chatAllowedModelsApi.listForChat(),
    staleTime: 60_000,
  });

  // Fetch slot bindings to surface "auto uses {router-model}" in the
  // reasoning selector tooltip. Cached for 60s, same as the allowlist.
  const slotsQuery = useQuery({
    queryKey: ["settings", "models", "list"],
    queryFn: () => modelSettingsApi.list(),
    staleTime: 60_000,
  });
  const reasoningRouterSlot = (slotsQuery.data ?? []).find(
    (s) => s.slot === ModelSlot.ReasoningRouter,
  );
  const reasoningAutoTooltip = reasoningRouterSlot
    ? `auto: usará ${reasoningRouterSlot.provider}: ${reasoningRouterSlot.model_identifier}`
    : "auto: usará el modelo del slot reasoning router";

  const chatCompatible = chatAllowedQuery.data ?? [];
  const chatDefault = chatCompatible.find((m) => m.is_default);
  const defaultModelLabel = chatDefault
    ? `${chatDefault.provider}: ${chatDefault.display_name ?? chatDefault.model_id}`
    : null;
  const defaultLabel = modelSelectorSentinelLabel(modelRoutingMode, defaultModelLabel);
  const autoModelTooltip = buildAutoModelTooltip(modelRoutingMode, defaultModelLabel);
  const showEffortSelector = shouldShowEffortSelector(modelRoutingMode, selectedModel, AUTO_VALUE);

  // While the SSE stream is in flight for an assistant turn, render its
  // pipeline steps just above the composer (pinned to the bottom of the
  // viewport) instead of inline under the answer. The chat scroll area
  // keeps growing as the answer streams in, so an inline stepper either
  // gets auto-scrolled offscreen or the user has to chase it. Once the
  // stream completes, `streamingTurnId` resets to null, this slot
  // disappears, and `<AssistantTurn>` renders the (now collapsed)
  // stepper at its rest position under the answer.
  const streamingTurn = streamingTurnId
    ? (turns.find((t) => t.id === streamingTurnId) ?? null)
    : null;
  const showSessionSkeleton = shouldShowSessionSkeleton({
    loadingSession,
    sessionListLoading: sessionsLoading,
    sessionId,
    shownSessionId,
    turnCount: turns.length,
  });

  const cancelActiveGeneration = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    replayAbortRef.current?.abort();
    setTurns((prev) =>
      prev.map((turn) => {
        if (!turn.generating) return turn;
        const { generating: _droppedGenerating, ...rest } = turn;
        return { ...rest, cancelled: true };
      }),
    );
    if (sessionId) {
      try {
        await cancelGeneration(sessionId);
      } catch {
        // Ignore: the backend may have already reached a terminal state.
      }
    }
  }, [sessionId]);

  const handleVoiceTrainingEvent = useCallback(
    (event: VoiceTrainingEvent): void => {
      if (event.kind === "user_speech_committed") {
        // Render an "Analizando…" placeholder user turn the moment the
        // utterance is committed; the transcript (or its empty removal) takes
        // its place when STT completes. Point userId at it so the upcoming
        // transcript resolves this turn, and drop any empty placeholder the
        // now-superseded previous turn left behind (see reduceVoiceSpeechCommitted).
        const id = crypto.randomUUID();
        voiceTrainingTurnIdsRef.current.userId = id;
        setTurns((prev) => reduceVoiceSpeechCommitted(prev, id));
        return;
      }
      if (event.kind === "user_transcript_delta" || event.kind === "user_transcript_final") {
        const id = voiceTrainingTurnIdsRef.current.userId ?? crypto.randomUUID();
        voiceTrainingTurnIdsRef.current.userId = event.kind === "user_transcript_final" ? null : id;
        // A rendered user turn starts a fresh assistant turn. Seed it as a
        // "generating" placeholder so the chat shows "Respondiendo…" while the
        // orchestrator works, until the first search/reply chunk replaces it.
        // Empty finals (transcriber produced nothing) create no turn.
        const isFinalWithText =
          event.kind === "user_transcript_final" && event.text.trim().length > 0;
        const assistantPlaceholderId = isFinalWithText ? crypto.randomUUID() : null;
        if (assistantPlaceholderId !== null) {
          voiceTrainingTurnIdsRef.current.assistantId = assistantPlaceholderId;
        }
        setTurns((prev) => {
          const next = reduceVoiceUserTranscript(prev, {
            id,
            kind: event.kind === "user_transcript_final" ? "final" : "delta",
            text: event.text,
          });
          if (assistantPlaceholderId === null) return next;
          return [
            ...next,
            { ...makeVoiceTrainingTurn(assistantPlaceholderId, "assistant", ""), generating: true },
          ];
        });
        return;
      }

      if (event.kind === "search_status") {
        const refMap = voiceSearchTurnsRef.current;
        let targetId = event.searchRef !== undefined ? refMap.get(event.searchRef) : undefined;
        if (targetId === undefined && event.status === "searching") {
          targetId = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = targetId;
          if (event.searchRef !== undefined) {
            refMap.set(event.searchRef, targetId);
          }
        }
        if (event.searchRef !== undefined && event.status !== "searching") {
          refMap.delete(event.searchRef);
        }
        setTurns((prev) =>
          reduceVoiceSearchStatus(prev, { status: event.status, message: event.message }, targetId),
        );
        return;
      }

      if (event.kind === "citation") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          const citation: Citation = {
            id: event.id,
            title: event.title,
            preview: event.preview,
            user_metadata: event.user_metadata ?? null,
            ...(event.source !== undefined ? { source: event.source } : {}),
          };
          if (existing === undefined) {
            return [
              ...prev,
              { ...makeVoiceTrainingTurn(id, "assistant", ""), citations: [citation] },
            ];
          }
          if (existing.citations.some((c) => c.id === event.id)) return prev;
          return prev.map((turn) =>
            turn.id === id ? { ...turn, citations: [...turn.citations, citation] } : turn,
          );
        });
        return;
      }

      if (
        event.kind === "assistant_transcript_delta" ||
        event.kind === "assistant_transcript_final"
      ) {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          const content =
            event.kind === "assistant_transcript_final"
              ? event.text
              : `${existing?.content ?? ""}${event.text}`;
          if (existing === undefined) {
            return [...prev, makeVoiceTrainingTurn(id, "assistant", content)];
          }
          // The first reply chunk clears the "Respondiendo…" placeholder.
          return prev.map((turn) =>
            turn.id === id ? { ...turn, content, generating: false } : turn,
          );
        });
        return;
      }

      if (event.kind === "tool_call") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          const toolCall: ToolCall = {
            id: event.id,
            name: event.name,
            input: event.input,
          };
          if (existing === undefined) {
            return [
              ...prev,
              {
                ...makeVoiceTrainingTurn(id, "assistant", ""),
                toolCalls: [toolCall],
              },
            ];
          }
          if ((existing.toolCalls ?? []).some((call) => call.id === event.id)) return prev;
          return prev.map((turn) =>
            turn.id === id ? { ...turn, toolCalls: [...(turn.toolCalls ?? []), toolCall] } : turn,
          );
        });
        return;
      }

      if (event.kind === "tool_result") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          const toolCall: ToolCall = {
            id: event.id,
            name: event.name,
            input: event.input,
            ok: event.ok,
            bytes: event.bytes,
            content_preview: event.content_preview,
            ...(event.error ? { error: event.error } : {}),
            ...(event.ref ? { ref: event.ref } : {}),
          };
          if (existing === undefined) {
            return [
              ...prev,
              {
                ...makeVoiceTrainingTurn(id, "assistant", ""),
                toolCalls: [toolCall],
              },
            ];
          }
          const current = existing.toolCalls ?? [];
          const nextCalls = current.some((call) => call.id === event.id)
            ? current.map((call) => (call.id === event.id ? { ...call, ...toolCall } : call))
            : [...current, toolCall];
          return prev.map((turn) => (turn.id === id ? { ...turn, toolCalls: nextCalls } : turn));
        });
        if (event.name === "approve_knowledge" && event.ok) {
          // The approval already persisted server-side; reflect it on the
          // (usually earlier) card and refresh the sidebar brain icon
          // without waiting for a page reload.
          const draftId = event.input.draft_id;
          if (typeof draftId === "string" && draftId) {
            setTurns((prev) => approveFactProposalAcrossTurns(prev, draftId));
            void queryClient.invalidateQueries({ queryKey: ["chat", "sessions"] });
          }
        }
        if (event.ok && event.knowledge_lifecycle) {
          // cancel_knowledge / refine_knowledge outcomes ride the result.
          const lifecycle = event.knowledge_lifecycle;
          setTurns((prev) => applyKnowledgeLifecycleAcrossTurns(prev, lifecycle));
        }
        return;
      }

      if (event.kind === "fact_proposal") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          const proposal: FactProposalState = {
            draft_id: event.draft_id,
            text: event.text,
            status: event.status,
            detected_at: event.detected_at,
            ...(event.kind_name ? { kind: event.kind_name } : {}),
            ...(event.scope ? { scope: event.scope } : {}),
          };
          if (existing === undefined) {
            return [
              ...prev,
              addFactProposalToTurn(makeVoiceTrainingTurn(id, "assistant", ""), proposal),
            ];
          }
          return prev.map((turn) =>
            turn.id === id ? addFactProposalToTurn(turn, proposal) : turn,
          );
        });
        return;
      }

      if (event.kind === "user_memory_draft") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const existing = prev.find((turn) => turn.id === id);
          // No assistant_message_id live: the relay persists the draft into
          // the assistant transcript message after the spoken turn, so the
          // card's direct click actions light up on the persisted turn (after
          // reload). Live, the user decides by voice.
          const draft: UserMemoryDraftState = {
            draft_id: event.draft_id,
            operation: event.operation,
            status: "pending",
            detected_at: event.detected_at,
            ...(event.text !== undefined ? { text: event.text } : {}),
            ...(event.target_id !== undefined ? { target_id: event.target_id } : {}),
            ...(event.target_text !== undefined ? { target_text: event.target_text } : {}),
          };
          if (existing === undefined) {
            return [
              ...prev,
              addUserMemoryDraftToTurn(makeVoiceTrainingTurn(id, "assistant", ""), draft),
            ];
          }
          return prev.map((turn) =>
            turn.id === id ? addUserMemoryDraftToTurn(turn, draft) : turn,
          );
        });
        return;
      }

      if (event.kind === "error") {
        setTurns((prev) => {
          const id = voiceTrainingTurnIdsRef.current.assistantId ?? crypto.randomUUID();
          voiceTrainingTurnIdsRef.current.assistantId = id;
          const message = `Voice training error: ${event.code}`;
          const existing = prev.find((turn) => turn.id === id);
          if (existing === undefined) {
            return [
              ...prev,
              {
                ...makeVoiceTrainingTurn(id, "assistant", ""),
                generating: false,
                error: message,
              },
            ];
          }
          return prev.map((turn) =>
            turn.id === id ? { ...turn, generating: false, error: message } : turn,
          );
        });
        return;
      }

      if (event.kind === "close") {
        const ids = voiceTrainingTurnIdsRef.current;
        voiceTrainingTurnIdsRef.current = { userId: null, assistantId: null };
        voiceSearchTurnsRef.current.clear();
        setTurns((prev) =>
          prev.map((turn) => (turn.id === ids.assistantId ? { ...turn, generating: false } : turn)),
        );
        const refreshSessionId = sessionId ?? effectiveSessionId;
        if (refreshSessionId !== undefined) {
          void getMessages(refreshSessionId)
            .then((messages) => {
              // Mark the session as already shown BEFORE adopting its id:
              // otherwise the rehydration effect sees a session change,
              // blanks the turns, shows the skeleton, and re-fetches the
              // history we just fetched — a multi-second flash on stop.
              setShownSessionId(refreshSessionId);
              setSessionId(refreshSessionId);
              queryClient.setQueryData(messagesQueryKey(refreshSessionId), messages);
              setTurns(messages.flatMap((message) => messageToTurns(message)));
              void queryClient.invalidateQueries({ queryKey: ["chat", "sessions"] });
              void queryClient.invalidateQueries({
                queryKey: sessionUsageQueryKey(refreshSessionId),
              });
            })
            .catch(() => {
              // Keep the live transcript if the refresh fails.
            });
        }
      }
    },
    [effectiveSessionId, queryClient, sessionId, setSessionId],
  );

  // Core send logic given a message string — shared by the composer form
  // handler (`send`, below) and any programmatic sender (e.g.
  // `KnowledgeOptionsFence` clicking a chip). Pulled out of `send` as a pure
  // extraction: the guard checks (`canSubmitChat`) stay in the form handler
  // since they gate the composer's submit affordance, not the underlying
  // send behavior itself.
  //
  // `fromComposer` marks the send as the composer's own submit. Only that
  // path may consume composer/session draft state: the typed input, staged
  // attachments, the report-edit context, and the refine-mode routing that
  // interprets `[refinando …]` shapes. A programmatic send (chip click) is an
  // ordinary standalone chat message — it must not clear the user's typed
  // draft, attach their staged files, consume their report-edit context, or
  // be routed to the refiner by a coincidentally-shaped label.
  async function sendMessage(
    message: string,
    { fromComposer = false }: { fromComposer?: boolean } = {},
  ): Promise<void> {
    // Concurrency guard for programmatic senders. The composer form gates
    // this via `canSubmitChat` before ever calling `sendMessage`, but a
    // `KnowledgeOptionsFence` chip calls `sendMessage` directly — and a chip
    // in an older, completed guided question stays clickable while a *new*
    // turn streams (that turn is `React.memo`'d and can't be re-disabled
    // reactively without defeating the H5 memoization). Re-check the same
    // in-flight predicate here so a stray chip click can't start a second
    // concurrent stream; a no-op is the same silent behavior the form has.
    if (isChatTurnInFlight({ sending, hasGeneratingTurn })) {
      return;
    }

    // Refine-knowledge takes precedence: when refine-knowledge mode is
    // active and the input carries the matching `[refinando
    // knowledge_<id>] <instructions>` shape, route to the LLM-only
    // refiner endpoint and surface the result as a `RefineKnowledgeCard`
    // pending state. The regular chat completion endpoint is NOT
    // invoked.
    const refineKnowledgeRoute = parseRefineKnowledgeInput(
      message,
      refineKnowledgeMode?.itemId ?? null,
    );
    if (
      fromComposer &&
      refineKnowledgeRoute.kind === "refine-knowledge" &&
      refineKnowledgeMode !== null
    ) {
      // The card keeps the item's full text as it is now: the mode can end
      // while the refinement is in flight, and the diff must not depend on it.
      const { currentText } = refineKnowledgeMode;
      setInput("");
      setSending(true);
      const assistantTurnId = crypto.randomUUID();
      const refineUserTurnId = crypto.randomUUID();
      // Push a placeholder assistant turn so the spinner / response
      // attaches to the same React node as the eventual card.
      if (turns.length > 0) {
        const sh = scrollRef.current?.scrollHeight ?? 0;
        const spacerH = spacerRef.current?.offsetHeight ?? 0;
        prevScrollHeightRef.current = sh - spacerH;
        pendingScrollToUserTurnIdRef.current = refineUserTurnId;
        setAntigravityActive(true);
      }
      setTurns((prev) => [
        ...prev,
        {
          id: refineUserTurnId,
          role: "user",
          content: message,
          thinking: "",
          citations: [],
          steps: [],
        },
        {
          id: assistantTurnId,
          role: "assistant",
          content: "",
          thinking: "",
          citations: [],
          steps: [],
        },
      ]);
      try {
        const { refined_text } = await refineLlmSource(
          refineKnowledgeRoute.itemId,
          refineKnowledgeRoute.instructions,
        );
        // An approval that landed while this refinement was in flight changed the
        // text the mode holds; a mode that has ended keeps the text captured above.
        const modeNow = refineKnowledgeModeRef.current;
        const storedText =
          modeNow !== null && modeNow.itemId === refineKnowledgeRoute.itemId
            ? modeNow.currentText
            : currentText;
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantTurnId
              ? {
                  ...t,
                  refineKnowledgeCard: {
                    itemId: refineKnowledgeRoute.itemId,
                    currentText: storedText,
                    refinedText: refined_text,
                    status: "pending",
                  },
                }
              : t,
          ),
        );
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : "no se pudo refinar";
        setTurns((prev) =>
          prev.map((t) => (t.id === assistantTurnId ? { ...t, error: errMsg } : t)),
        );
      } finally {
        setSending(false);
      }
      return;
    }

    const refineRoute = parseRefineModeInput(message, turns);
    if (fromComposer && refineRoute.kind === "refine") {
      // Defensive: never enter the refine branch while another mutation
      // is in flight (e.g. a chat stream is still being read). The outer
      // `sending` guard above already covers normal chat; this protects
      // against future async-state mistakes.
      setInput("");
      setSending(true);
      // Flip the target turn into the "refining" state so the card
      // surfaces the in-flight indicator + disables its own buttons.
      // Clear any stale error from a previous failed refine — the field
      // is omitted (not set to undefined) to satisfy
      // exactOptionalPropertyTypes.
      setTurns((prev) =>
        prev.map((t) => {
          if (t.id !== refineRoute.turnId) return t;
          const { error: _droppedError, ...rest } = t;
          return { ...rest, refining: true };
        }),
      );
      try {
        const updated = await refineFactProposal(
          refineRoute.messageId,
          refineRoute.instructions,
          refineRoute.draftId,
        );
        setTurns((prev) =>
          prev.map((t) => {
            if (t.id !== refineRoute.turnId) return t;
            const { error: _droppedError, ...rest } = t;
            const existing =
              t.factProposal?.draft_id === updated.draft_id
                ? t.factProposal
                : t.factProposals?.find((p) => p.draft_id === updated.draft_id);
            if (existing === undefined) return { ...rest, refining: false };
            return updateFactProposalInTurn(
              { ...rest, refining: false },
              withRefinedText(existing, updated.text),
            );
          }),
        );
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : "Unknown error";
        setTurns((prev) =>
          prev.map((t) =>
            t.id === refineRoute.turnId ? { ...t, refining: false, error: errMsg } : t,
          ),
        );
      } finally {
        setSending(false);
      }
      return;
    }

    const { activeReport: activeReportToSend, reportEditBase: reportEditBaseToSend } =
      reportRequestContextForSession({
        sessionId: effectiveSessionId,
        detail: dock.detail,
        edit: reportEditContext,
      });

    // Snapshot the ready attachments now — clear happens below after
    // successful stream, so ids and objects stay valid for the POST body
    // and the live user turn respectively. Composer-only: a chip send never
    // consumes the user's staged attachments.
    const attachmentsToSend = fromComposer ? [...readyAttachments] : [];
    const attachmentIdsToSend = attachmentsToSend.map((a) => a.id);

    const nowIso = new Date().toISOString();
    const userTurn: ChatTurn = {
      id: crypto.randomUUID(),
      role: "user",
      content: message,
      thinking: "",
      citations: [],
      steps: [],
      created_at: nowIso,
      // Include full attachment objects so <AttachmentList> renders immediately
      // without waiting for a session reload from the backend.
      ...(attachmentsToSend.length > 0 ? { attachments: attachmentsToSend } : {}),
    };
    const turnModel = resolveTurnModel(selectedModel, chatCompatible);
    const assistantTurn: ChatTurn = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      thinking: "",
      citations: [],
      steps: [],
      reasoningPreference: selectedReasoning,
      startedAt: performance.now(),
      created_at: nowIso,
      ...(turnModel ? { model: turnModel } : {}),
    };
    if (turns.length > 0) {
      const sh = scrollRef.current?.scrollHeight ?? 0;
      const spacerH = spacerRef.current?.offsetHeight ?? 0;
      prevScrollHeightRef.current = sh - spacerH;
      pendingScrollToUserTurnIdRef.current = userTurn.id;
      setAntigravityActive(true);
    } else {
      // First message in an empty chat: still pin once the user card mounts.
      pendingScrollToUserTurnIdRef.current = userTurn.id;
      setAntigravityActive(true);
    }
    setTurns((prev) => [...prev, userTurn, assistantTurn]);
    if (fromComposer) {
      setInput("");
      setReportEditContext(null);
      // Clear attachment chips — ids are now owned by the chat turn.
      attachmentResetRef.current?.();
    }
    setSending(true);
    setStreamingTurnId(assistantTurn.id);
    // Capture this turn's controller in a local const: `abortRef.current` is
    // reassigned on the next send, so reading the ref in the catch could see a
    // later turn's (non-aborted) controller and misclassify a cancel as an
    // error. The local binding is pinned to this turn.
    const controller = new AbortController();
    abortRef.current = controller;
    const modelOverride = parseSelectionToOverride(selectedModel);
    // tool_result SSE events carry only tool_use_id; remember each
    // tool_use's name/input so approve_knowledge results can be matched.
    const streamToolUses = new Map<string, { name: string; input: Record<string, unknown> }>();
    try {
      for await (const event of streamChat({
        message,
        sessionId: effectiveSessionId,
        signal: controller.signal,
        reasoningLevel: selectedReasoning,
        ...(modelOverride ? { modelOverride } : {}),
        ...(attachmentIdsToSend.length > 0 ? { attachmentIds: attachmentIdsToSend } : {}),
        ...(fromComposer && activeReportToSend ? { activeReport: activeReportToSend } : {}),
        ...(fromComposer && reportEditBaseToSend ? { reportEditBase: reportEditBaseToSend } : {}),
      })) {
        if (event.type === "session") {
          // The backend has now durably created the session. Publish its URL
          // and optimistic sidebar row only at this boundary: doing it before
          // POST /chat reaches ensure_session lets the URL hydration GET win
          // the race, 404, and erase the optimistic turn.
          if (activeSessionId === null) {
            locallyDurableSessionIdRef.current = event.session_id;
            const optimisticSession = buildOptimisticSession({
              id: event.session_id,
              message,
              nowIso,
            });
            queryClient.setQueriesData<ChatSessionSummary[]>(
              { queryKey: ["chat", "sessions", "active"] },
              (old) => prependOptimisticSession(old, optimisticSession),
            );
          } else {
            setShownSessionId(event.session_id);
          }
          setTurns((prev) =>
            bindPersistedMessageIds(prev, {
              userTurnId: userTurn.id,
              assistantTurnId: assistantTurn.id,
              ...(event.user_message_id !== undefined
                ? { userMessageId: event.user_message_id }
                : {}),
              ...(event.assistant_message_id !== undefined
                ? { assistantMessageId: event.assistant_message_id }
                : {}),
            }),
          );
        }
        if (event.type === "tool_use") {
          streamToolUses.set(event.id, { name: event.name, input: event.input });
        }
        if (event.type === "tool_result" && event.ok) {
          // A model-driven approve_knowledge decides a proposal whose card
          // lives in an earlier turn; flip it live (the sidebar brain icon
          // refreshes via the sessions invalidation in `finally`).
          const call = streamToolUses.get(event.tool_use_id);
          if (call?.name === "approve_knowledge") {
            const draftId = call.input.draft_id;
            if (typeof draftId === "string" && draftId) {
              setTurns((prev) => approveFactProposalAcrossTurns(prev, draftId));
            }
          }
          if (event.knowledge_lifecycle) {
            // cancel_knowledge / refine_knowledge outcomes ride the event.
            const lifecycle = event.knowledge_lifecycle;
            setTurns((prev) => applyKnowledgeLifecycleAcrossTurns(prev, lifecycle));
          }
        }
        applyEvent(assistantTurn.id, event, setSessionId, setTurns, chatCompatible);
        if (event.type === "report_ready") {
          const pointer: ReportPointer = {
            report_id: event.report_id,
            revision_number: event.revision_number,
          };
          openReportDetail(pointer);
          if (effectiveSessionId !== undefined) {
            // Invalidate every cached revision of this report so the v1
            // reader learns about newer_revision and the selector updates.
            void queryClient.invalidateQueries({
              queryKey: ["reports", effectiveSessionId, pointer.report_id],
            });
            void queryClient.invalidateQueries({
              queryKey: reportRevisionQueryKey(
                effectiveSessionId,
                pointer.report_id,
                pointer.revision_number,
              ),
            });
          }
        }
      }
    } catch (err) {
      if (controller.signal.aborted) {
        // User cancelled the turn (stop control aborted the fetch). Keep the
        // partial answer already applied via `applyEvent` and mark the turn
        // cancelled — not an error. The backend persists the same partial row
        // flagged `cancelled`, so a reload matches this state.
        setTurns((prev) =>
          prev.map((t) => (t.id === assistantTurn.id ? { ...t, cancelled: true } : t)),
        );
      } else if (err instanceof LlmCostLimitError) {
        // LLM cost-limit breach (429): the request never streamed, so the
        // assistant turn is empty. Render a scope-specific notice instead of a
        // raw error string.
        setTurns((prev) =>
          prev.map((t) => (t.id === assistantTurn.id ? { ...t, costLimitNotice: err.scope } : t)),
        );
      } else if (err instanceof MessageTooLongError) {
        // Message-too-long rejection (422): the request never streamed, so
        // the assistant turn is empty. Render a notice naming the limit and
        // the actual length instead of a raw error string.
        setTurns((prev) =>
          prev.map((t) =>
            t.id === assistantTurn.id
              ? {
                  ...t,
                  messageTooLong: { maxChars: err.maxChars, actualChars: err.actualChars },
                }
              : t,
          ),
        );
      } else {
        const message = err instanceof Error ? err.message : "Unknown error";
        setTurns((prev) =>
          prev.map((t) => (t.id === assistantTurn.id ? { ...t, error: message } : t)),
        );
      }
    } finally {
      // Refresh the sidebar + usage panel after every settled turn —
      // including a cancelled one, whose partial assistant row the backend
      // persisted — so the session's last_message_at / usage don't go stale.
      void queryClient.invalidateQueries({ queryKey: ["chat", "sessions"] });
      // Invalidate this session's cached messages so returning to it after
      // switching away re-fetches the freshly persisted turn — otherwise the
      // cache-first switch would serve the pre-send snapshot until it goes
      // stale. The session just chatted is not the painful switch case, so a
      // refetch on return is fine.
      if (effectiveSessionId !== undefined) {
        void queryClient.invalidateQueries({ queryKey: messagesQueryKey(effectiveSessionId) });
      }
      void queryClient.invalidateQueries({
        queryKey: sessionUsageQueryKey(effectiveSessionId ?? null),
      });
      setSending(false);
      setStreamingTurnId(null);
    }
  }

  async function send(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const message = input.trim();
    // Allow send when there are ready attachments even if message is empty.
    const hasReadyAttachments = readyAttachments.length > 0;
    if (
      !canSubmitChat({
        message,
        hasReadyAttachments,
        sending,
        hasGeneratingTurn,
        attachmentBlocked,
        messageOverCap,
      })
    ) {
      return;
    }
    await sendMessage(message, { fromComposer: true });
  }

  // Minimap items derived from the current turns. Keys + role + content
  // + label, nothing else — the rail doesn't need the streaming metadata.
  const minimapItems: MinimapItem[] = turns.map((t) => ({
    id: t.id,
    role: t.role,
    content: t.content,
    minimap_label: t.minimap_label ?? null,
    created_at: t.created_at ?? null,
  }));

  // Bidirectional links between a background report and the turn that
  // delegated it, keyed by task id. Recomputed when turns change.
  const researchLinks = useMemo(() => deriveResearchLinks(turns), [turns]);

  // The session's investigations, for the index bar above the composer.
  const sessionInvestigations = useMemo(() => deriveSessionInvestigations(turns), [turns]);

  const cancelResearch = useCallback(
    async (taskId: string): Promise<void> => {
      // Best-effort: the worker may have already finished (terminal state), in
      // which case the cancel is a benign no-op. The report still arrives via SSE.
      try {
        const result = await cancelBackgroundTask(taskId);
        setCancelRequestedTaskIds((prev) => {
          if (prev.has(taskId)) return prev;
          const next = new Set(prev);
          next.add(taskId);
          return next;
        });
        if (result.status !== "cancel_requested" && sessionId !== undefined) {
          const messages = await getMessages(sessionId);
          cacheMessages(sessionId, messages);
          setTurns(messages.flatMap((msg) => messageToTurns(msg)));
        }
      } catch {
        // Swallow — the chip stays until the report lands and replaces it.
      }
    },
    [cacheMessages, sessionId],
  );

  // Single active conversational session: if the user has a research running
  // in a session other than the one on screen, offer to resume it. The endpoint
  // can also return a heartbeat-only subscription, but that is not a
  // research-in-progress signal. `refetchOnWindowFocus` is enabled here (the
  // global default is off) so returning to the tab re-checks for active work.
  const activeSessionQuery = useQuery({
    queryKey: ACTIVE_SESSION_QUERY_KEY,
    queryFn: getActiveSession,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const [dismissedResumeId, setDismissedResumeId] = useState<string | null>(null);
  const activeSession = activeSessionQuery.data ?? null;
  const showResume = shouldShowResumeBanner(activeSession, sessionId, dismissedResumeId);

  // Scroll the chat container so the message with the given local turn id
  // is in view. Used by both the desktop rail (Minimap) and the mobile
  // bottom sheet.
  function jumpToMessage(turnId: string): void {
    const container = scrollRef.current;
    if (!container) return;
    const target = container.querySelector<HTMLElement>(`[data-turn-id="${turnId}"]`);
    if (!target) return;
    scrollTurnIntoView(container, target);
  }

  // Footer shortcut for the mobile sheet — smooth-scroll to the latest turn.
  function jumpToEnd(): void {
    const container = scrollRef.current;
    if (!container) return;
    autoFollowRef.current = true;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }

  // Snapshot which turn the user is currently reading the instant the sheet
  // opens, so the mobile minimap can mark it active (the sheet covers the
  // chat, so a snapshot beats tracking continuously just for this). The
  // scroll effect below keeps `activeMinimapId` fresh anyway; this just
  // guarantees it's current at the exact moment the sheet opens.
  function openMinimapSheet(): void {
    const container = scrollRef.current;
    if (container) setActiveMinimapId(closestVisibleTurnId(container));
    dock.openTab("minimapa");
  }

  // Mirror the turn currently in view into `?m=<message-id>` (replace
  // semantics — never a history entry — throttled to ~2/sec), reusing the
  // same closest-to-top heuristic as the minimap sheet snapshot above. Only
  // persisted message ids are written: a streaming turn's durable id isn't
  // known until the SSE `session` event binds `turn.messageId` (or a reload,
  // where every turn's `id` already equals its persisted `messageId`). See
  // `add-session-urls-and-scroll-position`.
  const lastWrittenMRef = useRef<string | null>(null);
  const mThrottleTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const handleScroll = (): void => {
      if (mThrottleTimerRef.current !== null) return;
      mThrottleTimerRef.current = window.setTimeout(() => {
        mThrottleTimerRef.current = null;
        const turnId = closestVisibleTurnId(container);
        if (turnId === null) return;
        setActiveMinimapId(turnId);
        const messageId = turnsRef.current.find((t) => t.id === turnId)?.messageId;
        if (messageId === undefined || messageId === lastWrittenMRef.current) return;
        lastWrittenMRef.current = messageId;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("m", messageId);
            return next;
          },
          { replace: true },
        );
      }, 500);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (mThrottleTimerRef.current !== null) window.clearTimeout(mThrottleTimerRef.current);
    };
  }, [setSearchParams]);

  // `sendMessage` closes over per-render state (readyAttachments, turns,
  // sessionId, ...) so its identity changes every render — unlike the
  // `useState` setters below. Route calls through a "latest ref" (same
  // pattern as `turnsRef` above) so the context value's `sendMessage` field
  // stays referentially stable while always invoking the current closure.
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const stableSendMessage = useCallback((message: string) => sendMessageRef.current(message), []);

  // Stable provider value so context consumers (AssistantTurn and its
  // fact/refine cards) don't re-render on every streaming token. The
  // setters are referentially stable, so this memo computes once — without
  // it, a fresh `{ setInput, setTurns }` object each render would defeat the
  // `React.memo` on the turn components (see audit H5).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `stableSendMessage` and `handleRefineKnowledgeApproved` never change identity (empty-deps useCallbacks over stable setters), same as the useState setters; `sessionId` is intentionally included so grant cards see the active session.
  const chatInputContextValue = useMemo<ChatInputContextValue>(
    () => ({
      setInput,
      setTurns,
      sendMessage: stableSendMessage,
      sessionId,
      onRefineKnowledgeApproved: handleRefineKnowledgeApproved,
    }),
    [sessionId],
  );

  // Pair user+assistant so sticky has a tall parent: while that Q/A group is
  // in the scrollport, its user card pins at the top (Cursor-style). A new
  // send still jumps to the latest card via pendingScrollToUserTurnIdRef.
  const turnGroups = useMemo(() => groupChatTurns(turns), [turns]);

  // The session-context ring + minimap buttons. On narrow screens they float
  // horizontally in the textarea top-right with a `max-md:pr-16` gutter; from
  // `md` up they sit inline in the action row (no extra textarea padding).
  // Same fragment in both call sites, toggled with `md:hidden` / `hidden md:flex`.
  const navButtons = (
    <>
      {sessionId !== undefined ? (
        <SessionContextTrigger sessionId={sessionId} onClick={() => dock.openTab("contexto")} />
      ) : null}
      <button
        type="button"
        onClick={openMinimapSheet}
        aria-label="Abrir minimapa"
        className="rounded-full border border-border bg-card/80 p-1.5 text-muted-foreground shadow-sm backdrop-blur hover:cursor-pointer hover:bg-muted hover:text-foreground"
      >
        <ListMinus size={16} />
      </button>
    </>
  );

  return (
    <div className="flex h-full flex-col">
      {sessionNotFoundNotice ? (
        <SessionNotFoundBanner onDismiss={() => setSessionNotFoundNotice(false)} />
      ) : null}
      {showResume && activeSession ? (
        <ResumeBanner
          onResume={() => setActiveSessionId(activeSession.session_id)}
          onDismiss={() => setDismissedResumeId(activeSession.session_id)}
        />
      ) : null}
      {/* Portal target for the active voice-session banner. Always mounted so
          VoiceTrainingButton can portal into it; `empty:hidden` keeps it out
          of the layout until the banner fills it. */}
      <div ref={setVoiceBannerSlot} className="sticky top-0 z-40 px-3 pt-2 empty:hidden sm:px-6" />
      <div
        className={cn(
          "relative flex flex-1 overflow-hidden",
          voiceTrainingActive && "voice-active",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            {showSessionSkeleton ? (
              <MessagesSkeleton />
            ) : turns.length === 0 ? (
              <Welcome />
            ) : (
              <ChatInputContext.Provider value={chatInputContextValue}>
                <div className="mx-auto max-w-3xl space-y-8 px-3 py-4 sm:px-6 sm:py-6">
                  {turnGroups.map((group) => (
                    // Parent of sticky UserTurn must wrap the answer too — sticky
                    // only pins while this box remains in the scrollport.
                    <div key={group.key} className="space-y-4" data-turn-group={group.key}>
                      {group.user ? <UserTurn turn={group.user} sticky /> : null}
                      {group.assistants.map((turn) => (
                        <div key={turn.id} data-turn-id={turn.id}>
                          <AssistantTurn
                            turn={turn}
                            streaming={turn.id === streamingTurnId}
                            isLatestTurn={turn.id === turns[turns.length - 1]?.id}
                            onSourceClick={openSourceDetail}
                            onOpenResearchDetail={openResearchDetail}
                            onOpenReport={openReportDetail}
                            {...(effectiveSessionId !== undefined
                              ? { sessionId: effectiveSessionId }
                              : {})}
                            {...(group.user?.content !== undefined
                              ? { originatingQuestion: group.user.content }
                              : {})}
                            {...(() => {
                              const triggerId = turn.backgroundTaskId
                                ? researchLinks.triggerTurnIdByTask.get(turn.backgroundTaskId)
                                : undefined;
                              return triggerId
                                ? { onJumpToResearchTrigger: () => jumpToMessage(triggerId) }
                                : {};
                            })()}
                          />
                          <ResearchTurnExtras
                            turn={turn}
                            links={researchLinks}
                            onJump={jumpToMessage}
                            onCancel={cancelResearch}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Antigravity scroll room. Only rendered while a recent send
                   * is being followed so an idle chat keeps its compact layout. */}
                  {antigravityActive && (
                    <div ref={spacerRef} aria-hidden="true" className="min-h-[100dvh] shrink-0" />
                  )}
                </div>
              </ChatInputContext.Provider>
            )}
          </div>
          <MinimapBottomSheet
            open={!isXl && dock.open && visibleDockDetail === null && dock.tab === "minimapa"}
            onOpenChange={(open) => {
              if (!open) dock.close();
            }}
            items={minimapItems}
            activeItemId={activeMinimapId}
            onJumpTo={jumpToMessage}
            onJumpToEnd={jumpToEnd}
          />
          <SessionContextSheet
            open={!isXl && dock.open && visibleDockDetail === null && dock.tab === "contexto"}
            onOpenChange={(open) => {
              if (!open) dock.close();
            }}
            sessionId={sessionId ?? null}
          />
          <ChatInputContext.Provider value={chatInputContextValue}>
            <ResearchDetailSheet
              open={visibleDockDetail?.kind === "research" && (!isXl || dock.detailExpanded)}
              onOpenChange={(open) => {
                if (!open) dismissDetail();
              }}
              taskId={visibleDockDetail?.kind === "research" ? visibleDockDetail.taskId : null}
              turns={turns}
              links={researchLinks}
              progressByTask={researchProgressByTask}
              onSourceClick={openSourceDetail}
              wide={isXl && dock.detailExpanded}
            />
          </ChatInputContext.Provider>
          <SourceDetailSheet
            open={visibleDockDetail?.kind === "source" && (!isXl || dock.detailExpanded)}
            onOpenChange={(open) => {
              if (!open) dismissDetail();
            }}
            sourceId={visibleDockDetail?.kind === "source" ? visibleDockDetail.id : null}
            wide={isXl && dock.detailExpanded}
          />
          {visibleDockDetail?.kind === "report" ? (
            <ExpandedReportReader
              expanded={isXl && dock.detailExpanded}
              onModify={modifyReport}
              onOpenChange={(open) => {
                if (!open) dismissDetail();
              }}
              onOpenRevision={openReportDetail}
              open={!isXl || dock.detailExpanded}
              pointer={{
                report_id: visibleDockDetail.reportId,
                revision_number: visibleDockDetail.revisionNumber,
              }}
              sessionId={visibleDockDetail.sessionId}
            />
          ) : null}

          <div className="relative bg-background">
            {turns.length > 0 ? (
              <div
                aria-hidden="true"
                data-testid="chat-composer-gradient"
                className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-b from-background/0 via-background/80 to-background"
              />
            ) : null}
            {streamingTurn && (
              <div className="mx-auto max-w-3xl px-3 sm:px-6">
                <PipelineSteps
                  steps={streamingTurn.steps}
                  streaming={true}
                  citationCount={streamingTurn.citations.length}
                  {...(streamingTurn.toolCalls ? { toolCalls: streamingTurn.toolCalls } : {})}
                  {...(streamingTurn.startedAt !== undefined
                    ? { turnStartedAt: streamingTurn.startedAt }
                    : {})}
                  {...(streamingTurn.reasoningLevel
                    ? { reasoningLevel: streamingTurn.reasoningLevel }
                    : {})}
                />
              </div>
            )}
            {refineKnowledgeMode || refineKnowledgeError ? (
              <div className="mx-auto max-w-3xl px-3 pt-3 sm:px-6 sm:pt-4">
                {refineKnowledgeMode ? (
                  <RefineKnowledgeBanner
                    itemId={refineKnowledgeMode.itemId}
                    currentText={refineKnowledgeMode.currentText}
                    onExit={exitRefineKnowledgeMode}
                  />
                ) : null}
                {refineKnowledgeError ? (
                  <p
                    role="alert"
                    className="mb-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-body-3 text-destructive"
                    data-testid="refine-knowledge-error"
                  >
                    {refineKnowledgeError}
                  </p>
                ) : null}
              </div>
            ) : null}
            <InvestigationsBar items={sessionInvestigations} onOpenDetail={openResearchDetail} />
            {sessionId ? <SessionStageGrantsBadges sessionId={sessionId} /> : null}
            <form onSubmit={send} className="relative mx-auto max-w-3xl px-3 pb-3 sm:px-6 sm:pb-4">
              <div className="relative">
                {turns.length > 0 ? (
                  <div className="absolute right-1.5 top-1.5 z-10 flex gap-1.5 md:hidden">
                    {navButtons}
                  </div>
                ) : null}
                {input.length === 0 && (
                  <span
                    className={cn(
                      "pointer-events-none absolute left-4 top-2.5 select-none text-body-2 text-muted-foreground opacity-90",
                      !hasSessions && "transition-all duration-150",
                      !hasSessions &&
                        (placeholderVisible
                          ? "translate-y-0 opacity-100"
                          : "-translate-y-1 opacity-0"),
                    )}
                  >
                    {hasSessions ? "Pregunta algo" : PLACEHOLDERS[placeholderIndex]}
                  </span>
                )}
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  onPaste={(e) => {
                    // Paste-to-attach: if the clipboard carries files (a pasted
                    // screenshot or copied files), route them through the
                    // composer's upload pipeline and don't also paste the bytes
                    // as text. Text-only pastes fall through to default behaviour.
                    const files = filesFromClipboard(e.clipboardData);
                    if (files) {
                      e.preventDefault();
                      attachmentAddFilesRef.current?.(files);
                    }
                  }}
                  placeholder=""
                  disabled={turnInFlight}
                  aria-label="Chat message"
                  aria-invalid={messageOverCap}
                  rows={2}
                  className={cn(
                    // Use pl/pr separately so a mobile-only right gutter cannot
                    // fight `px-*` utility order on larger breakpoints.
                    "block max-h-[12rem] min-h-[3.5rem] w-full resize-none overflow-y-auto rounded-xl border border-border/50 bg-muted py-2.5 pl-4 pr-4 text-body-2 placeholder:text-muted-foreground focus:outline-none disabled:opacity-50",
                    turns.length > 0 && "max-md:pr-16",
                    messageOverCap && "border-destructive focus:outline-destructive",
                    counterVisible && "pb-6",
                  )}
                />
                {counterVisible && (
                  <span
                    data-testid="chat-message-counter"
                    className={cn(
                      "pointer-events-none absolute bottom-1.5 right-3 select-none text-label-3 tabular-nums text-muted-foreground",
                      messageOverCap && "text-destructive",
                    )}
                  >
                    {inputCodePointCount} / {chatMessageMaxChars}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <select
                    value={selectedModel}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSelectedModel(next);
                      // Option B: entering Auto or leaving Auto for a concrete
                      // model both reset effort to auto (hidden under Auto).
                      if (routingComposerActive) {
                        setSelectedReasoning("auto");
                      }
                    }}
                    aria-label="Model selector"
                    title={autoModelTooltip}
                    disabled={turnInFlight}
                    data-testid="chat-model-selector"
                    className={CHAT_MODEL_SELECTOR_CLASSNAME}
                  >
                    <option value={AUTO_VALUE} title={autoModelTooltip}>
                      {defaultLabel}
                    </option>
                    {(routingComposerActive
                      ? chatCompatible
                      : chatCompatible.filter((m) => !m.is_default)
                    ).map((m) => (
                      <option
                        key={`${m.provider}:${m.model_id}`}
                        value={`${m.provider}:${m.model_id}`}
                      >
                        {m.provider}: {m.display_name ?? m.model_id}
                      </option>
                    ))}
                  </select>
                  {showEffortSelector ? (
                    <select
                      value={selectedReasoning}
                      onChange={(e) => setSelectedReasoning(e.target.value as ReasoningPreference)}
                      aria-label="Reasoning level"
                      title="razonamiento"
                      disabled={turnInFlight}
                      className="rounded-md border border-border bg-background px-2 py-1 text-body-3"
                    >
                      {REASONING_PREFERENCES.map((pref) => (
                        <option
                          key={pref}
                          value={pref}
                          title={pref === "auto" ? reasoningAutoTooltip : ""}
                        >
                          {pref}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
                {turns.length > 0 ? (
                  <div className="hidden items-center gap-2 md:flex">{navButtons}</div>
                ) : null}
                <AttachmentComposer
                  sessionId={effectiveSessionId}
                  onReadyAttachmentsChange={handleReadyAttachmentsChange}
                  onBlockingChange={handleAttachmentBlocked}
                  resetRef={attachmentResetRef}
                  addFilesRef={attachmentAddFilesRef}
                  disabled={turnInFlight}
                />
                <DictationButton
                  onTranscript={(text, event) => {
                    // After applying the dictation update, scroll the
                    // textarea to the bottom on the next frame so the
                    // latest tokens stay visible even past the visible
                    // rows. Keying off `requestAnimationFrame` ensures
                    // React has committed the new value before we read
                    // `scrollHeight`.
                    requestAnimationFrame(() => {
                      const el = inputRef.current;
                      if (el) el.scrollTop = el.scrollHeight;
                    });
                    setInput((prev) => {
                      if (event === "turn-append") {
                        // Mid-turn deltas: concatenate verbatim. The model
                        // encodes word boundaries inside the delta (leading
                        // space for new words, none for sub-word token
                        // fragments), so inserting a separator would corrupt
                        // words like "habrán" → "hab rán".
                        return prev + text;
                      }
                      if (event === "turn-finalize") {
                        // Replace the tentative delta text with the
                        // canonical transcript the model produced after
                        // re-evaluating the turn with full context.
                        const start = dictationTurnStartRef.current ?? prev.length;
                        return prev.slice(0, start) + text;
                      }
                      // event === "turn-start" — remember where this turn
                      // begins so `turn-finalize` can replace it.
                      const cleanText = text.trimStart();
                      if (prev.length === 0) {
                        dictationTurnStartRef.current = 0;
                        return cleanText;
                      }
                      if (prev.endsWith(" ")) {
                        dictationTurnStartRef.current = prev.length;
                        return prev + cleanText;
                      }
                      dictationTurnStartRef.current = prev.length + 1;
                      return `${prev} ${cleanText}`;
                    });
                  }}
                  disabled={turnInFlight || voiceTrainingActive}
                />
                {isVoiceControlVisible(voiceMode) ? (
                  <VoiceTrainingButton
                    sessionId={effectiveSessionId ?? null}
                    voiceMode={voiceMode}
                    onVoiceEvent={handleVoiceTrainingEvent}
                    disabled={sending}
                    onActiveChange={setVoiceTrainingActive}
                    bannerSlot={voiceBannerSlot}
                  />
                ) : null}
                {turnInFlight ? (
                  // While a turn streams, the send button becomes a stop control:
                  // it sets the backend cancellation flag and aborts the active local stream.
                  <button
                    type="button"
                    onClick={() => {
                      void cancelActiveGeneration();
                    }}
                    aria-label="Detener respuesta"
                    className="shrink-0 rounded-md bg-brand-extra-2-40 px-3 py-1.5 text-body-3 font-semibold text-white hover:opacity-90 sm:px-4"
                  >
                    ◼ detener
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={
                      !canSubmitChat({
                        message: input.trim(),
                        hasReadyAttachments: readyAttachments.length > 0,
                        sending,
                        hasGeneratingTurn,
                        attachmentBlocked,
                        messageOverCap,
                      })
                    }
                    className="shrink-0 rounded-md bg-brand-extra-2-40 px-3 py-1.5 text-body-3 font-semibold text-white hover:opacity-90 disabled:opacity-50 sm:px-4"
                  >
                    enviar ↵
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        {dockVisible ? (
          <aside
            aria-label="Paneles de sesión"
            className="flex w-[340px] shrink-0 flex-col overflow-hidden border-l border-border"
          >
            <RightDock
              tab={dock.tab}
              onTabChange={dock.setTab}
              onClose={dock.close}
              sessionId={sessionId ?? null}
              minimapItems={minimapItems}
              activeMinimapId={activeMinimapId}
              onJumpTo={jumpToMessage}
              detail={dock.detailExpanded ? null : visibleDockDetail}
              detailContent={
                visibleDockDetail?.kind === "source" ? (
                  <SourceDetailContent sourceId={visibleDockDetail.id} />
                ) : visibleDockDetail?.kind === "research" ? (
                  <ChatInputContext.Provider value={chatInputContextValue}>
                    <ResearchDetailContent
                      taskId={visibleDockDetail.taskId}
                      turns={turns}
                      links={researchLinks}
                      progressByTask={researchProgressByTask}
                      onSourceClick={openSourceDetail}
                    />
                  </ChatInputContext.Provider>
                ) : visibleDockDetail?.kind === "report" ? (
                  <ReportReader
                    mode="dock"
                    onModify={modifyReport}
                    onOpenRevision={openReportDetail}
                    pointer={{
                      report_id: visibleDockDetail.reportId,
                      revision_number: visibleDockDetail.revisionNumber,
                    }}
                    sessionId={visibleDockDetail.sessionId}
                  />
                ) : null
              }
              onDetailBack={dock.closeDetail}
              onDetailExpand={dock.expandDetail}
            />
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function Welcome(): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center sm:px-6">
      <h1 className="text-heading-2 font-bold sm:text-heading-1">beflow / knowledge</h1>
      <p className="text-body-2 text-muted-foreground">
        pregúntale algo sobre el código, los flujos o la documentación
      </p>
    </div>
  );
}

/** Inline, non-scrolling banner offering to resume the user's active research
 * session when they are viewing a different / new chat. */
function ResumeBanner({
  onResume,
  onDismiss,
}: {
  onResume: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-3xl px-3 pt-3 sm:px-6">
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-body-3">
        <span
          aria-hidden="true"
          className="inline-block size-2 shrink-0 animate-pulse rounded-full bg-brand-extra-2-40"
        />
        <span className="flex-1 text-muted-foreground">
          Tienes una investigación en curso en otra sesión.
        </span>
        <button
          type="button"
          onClick={onResume}
          className="shrink-0 rounded-md bg-brand-extra-2-40 px-2.5 py-1 text-label-2 font-semibold text-white hover:opacity-90"
        >
          retomar
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Descartar"
          className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/** Shown when a `/chat/<id>` URL names a session that doesn't exist or
 * doesn't belong to the caller (404 on the messages fetch). The chat route
 * has already redirected to bare `/chat` by the time this renders — this is
 * just the neutral explanation. */
function SessionNotFoundBanner({ onDismiss }: { onDismiss: () => void }): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-3xl px-3 pt-3 sm:px-6">
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-body-3">
        <span className="flex-1 text-muted-foreground">
          Esa sesión no existe o no tienes acceso. Volviste a tu conversación más reciente.
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Descartar"
          className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/** Index of the session's background investigations, pinned above the
 * composer (à la Claude Code's commit list). Collapsible; each row shows a
 * live seconds counter while running and the final time once delivered, and
 * opens the read-only investigation detail sheet on click. */
function InvestigationsBar({
  items,
  onOpenDetail,
}: {
  items: SessionInvestigation[];
  onOpenDetail: (taskId: string) => void;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const startRef = useRef<Map<string, number>>(new Map());
  const hasRunning = items.some((i) => i.status === "running");

  // Capture each running task's start once (created_at on reload, else now).
  useEffect(() => {
    for (const it of items) {
      if (it.status === "running" && !startRef.current.has(it.taskId)) {
        const fromCreated = it.startedAtMs !== undefined && !Number.isNaN(it.startedAtMs);
        startRef.current.set(it.taskId, fromCreated ? (it.startedAtMs as number) : Date.now());
      }
    }
  }, [items]);

  // Tick once per second only while something is running.
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasRunning]);

  if (items.length === 0) return null;
  const runningCount = items.filter((i) => i.status === "running").length;

  return (
    <div className="mx-auto w-full max-w-3xl px-3 pt-2 sm:px-6">
      <div className="rounded-lg border border-border bg-muted/30">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full items-center gap-1.5 px-3 py-1.5 text-label-2 text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <ChevronDown aria-hidden="true" className="size-3.5 shrink-0" />
          ) : (
            <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
          )}
          <Microscope aria-hidden="true" className="size-3.5 shrink-0" />
          <span className="font-medium">Investigaciones ({items.length})</span>
          {runningCount > 0 ? (
            <span className="text-muted-foreground">· {runningCount} en curso</span>
          ) : null}
        </button>
        {expanded ? (
          <ul className="flex flex-col gap-px border-t border-border/50 p-1">
            {items.map((it) => {
              const start = startRef.current.get(it.taskId);
              const timeText =
                it.status === "running"
                  ? start !== undefined
                    ? formatTotalElapsed(Math.max(0, nowMs - start))
                    : "…"
                  : it.elapsedSeconds !== undefined
                    ? formatTotalElapsed(it.elapsedSeconds * 1000)
                    : null;
              return (
                <li key={it.taskId}>
                  <button
                    type="button"
                    onClick={() => onOpenDetail(it.taskId)}
                    aria-label={`Abrir detalle de investigacion: ${it.label}`}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-body-3 hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                  >
                    {it.status === "running" ? (
                      <Loader2
                        aria-hidden="true"
                        className="size-3.5 shrink-0 animate-spin text-muted-foreground"
                      />
                    ) : it.status === "cancelled" || it.status === "failed" ? (
                      <X aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <Check
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-brand-feedback-success-00"
                      />
                    )}
                    <span className="flex-1 truncate text-muted-foreground">{it.label}</span>
                    {timeText !== null ? (
                      <span className="shrink-0 tabular-nums text-label-2 text-muted-foreground">
                        {timeText}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function MessagesSkeleton(): JSX.Element {
  return (
    <div
      aria-busy="true"
      aria-label="Cargando conversación"
      className="mx-auto max-w-3xl space-y-6 px-3 py-4 sm:space-y-8 sm:px-6 sm:py-6"
    >
      <div className="flex justify-end">
        <div className="h-12 w-2/3 animate-pulse rounded-lg bg-muted sm:w-1/2" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-20 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
      </div>
      <div className="flex justify-end">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted sm:w-2/3" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-20 animate-pulse rounded bg-muted" />
        <div className="h-4 w-full animate-pulse rounded bg-muted" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

function RoleLabel({ label, accent }: { label: string; accent?: boolean }): JSX.Element {
  return (
    <p
      className={`mb-2 text-label-2 uppercase tracking-wider ${
        accent ? "text-brand-extra-2-40" : "text-muted-foreground"
      }`}
    >
      {label}
    </p>
  );
}

function CopyButton({ text }: { text: string }): JSX.Element {
  const { showToast } = useToast();
  async function copy(): Promise<void> {
    const ok = await copyTextToClipboard(text);
    showToast({
      kind: ok ? "success" : "error",
      message: ok ? "Copiado al portapapeles." : "No se pudo copiar al portapapeles.",
    });
  }
  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => void copy()}
        title="Copiar al portapapeles"
        aria-label="Copiar al portapapeles"
        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

type TurnIdKind = "question" | "answer";

function TurnIdMenu({
  kind,
  messageId,
}: {
  kind: TurnIdKind;
  messageId: string | undefined;
}): JSX.Element {
  const { showToast } = useToast();
  const isQuestion = kind === "question";
  const menuLabel = isQuestion ? "Opciones de pregunta" : "Opciones de respuesta";
  const copyLabel = isQuestion ? "Copiar ID de pregunta" : "Copiar ID de respuesta";

  async function copyId(): Promise<void> {
    if (messageId === undefined) return;
    const copied = await copyTextToClipboard(messageId);
    showToast({
      kind: copied ? "success" : "error",
      message: copied
        ? `ID de ${isQuestion ? "pregunta" : "respuesta"} copiado.`
        : `No se pudo copiar el ID de ${isQuestion ? "pregunta" : "respuesta"}.`,
    });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={menuLabel}
          title={menuLabel}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <MoreVertical size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <button
          type="button"
          disabled={messageId === undefined}
          onClick={() => void copyId()}
          className="w-full rounded-sm px-2 py-1.5 text-left text-body-3 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          {copyLabel}
        </button>
      </PopoverContent>
    </Popover>
  );
}

/** Share action for an assistant turn — colocated with `TurnIdMenu`. Opens
 * `ShareTurnDialog` (audience + scope → create → copy URL; lists/revokes
 * existing shares). `add-chat-turn-sharing`. */
function ShareTurnButton({
  sessionId,
  messageId,
  responseAssets,
}: {
  sessionId: string | undefined;
  messageId: string | undefined;
  responseAssets: ResponseAssetProjection[] | undefined;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (sessionId === undefined || messageId === undefined) return null;
  return (
    <>
      <button
        type="button"
        aria-label="Compartir respuesta"
        title="Compartir respuesta"
        onClick={() => setOpen(true)}
        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Share2 size={14} />
      </button>
      {open && (
        <ShareTurnDialog
          sessionId={sessionId}
          assistantMessageId={messageId}
          assets={responseAssets ?? []}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** Date + time for turn meta rows (hover). Always day/month/year + local time. */
function formatTurnWhen(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatTimestamp(iso);
}

function TurnWhen({ iso }: { iso: string | undefined }): JSX.Element | null {
  const when = formatTurnWhen(iso);
  if (!when || !iso) return null;
  return (
    <time dateTime={iso} className="select-none text-[11px] tabular-nums text-muted-foreground">
      {when}
    </time>
  );
}

/** Char length (or any newline) above which the user bar is treated as collapsible. */
export const USER_TURN_COLLAPSE_CHAR_THRESHOLD = 100;

/** True when the prompt should offer expand/collapse even before layout measure. */
export function userTurnLikelyCollapsible(content: string): boolean {
  return content.includes("\n") || content.length > USER_TURN_COLLAPSE_CHAR_THRESHOLD;
}

/**
 * One Q/A block for sticky user prompts: the sticky bar only stays pinned while
 * its *parent* is in the scrollport. Pairing user + following assistant(s) makes
 * that parent tall enough to cover the answer (Cursor / Grok Build behaviour).
 */
export interface ChatTurnGroup {
  key: string;
  user: ChatTurn | null;
  assistants: ChatTurn[];
}

export function groupChatTurns(turns: ChatTurn[]): ChatTurnGroup[] {
  const groups: ChatTurnGroup[] = [];
  let i = 0;
  while (i < turns.length) {
    const current = turns[i];
    if (!current) break;
    if (current.role === "user") {
      const assistants: ChatTurn[] = [];
      let j = i + 1;
      while (j < turns.length) {
        const next = turns[j];
        if (!next || next.role !== "assistant") break;
        assistants.push(next);
        j += 1;
      }
      groups.push({ key: current.id, user: current, assistants });
      i = j;
      continue;
    }
    // Orphan assistant (rehydration edge cases) — own group, no sticky user bar.
    groups.push({ key: current.id, user: null, assistants: [current] });
    i += 1;
  }
  return groups;
}

/**
 * Cursor / Grok Build-style user prompt: sticky card at the top of its Q/A
 * group while the answer scrolls underneath; one-line collapse with ellipsis
 * when long. Expand/collapse is only via the chevron to the left of the
 * question text (single-click on the card body is reserved for text
 * selection). Double-click scrolls to the question start and expands when
 * collapsible — never collapses. Expand state is local UI only.
 *
 * The root node itself is `position: sticky` (when `sticky`) so the containing
 * block is the parent turn-group (user + assistant). Every Q/A group uses
 * sticky so scrolling up/down pins the prompt of the answer you're reading.
 */
export const UserTurn = memo(function UserTurn({
  turn,
  sticky = true,
}: {
  turn: ChatTurn;
  sticky?: boolean;
}): JSX.Element {
  const showCopy = !turn.transcribing && turn.content.length > 0;
  const isTranscribingEmpty = Boolean(turn.transcribing && turn.content.length === 0);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // Measure one-line overflow while collapsed. Expanded always offers collapse.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when prompt text changes
  useLayoutEffect(() => {
    if (isTranscribingEmpty) {
      setOverflows(false);
      return;
    }
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;
    const measure = (): void => {
      setOverflows(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    // Re-measure after fonts/layout settle (narrow ↔ wide).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [turn.content, expanded, isTranscribingEmpty]);

  // New content (e.g. STT final) should start collapsed again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when prompt text changes
  useEffect(() => {
    setExpanded(false);
  }, [turn.content]);

  const canToggle =
    !isTranscribingEmpty && (overflows || expanded || userTurnLikelyCollapsible(turn.content));

  function toggleExpanded(): void {
    if (!canToggle) return;
    setExpanded((v) => !v);
  }

  function handleMouseDown(event: MouseEvent<HTMLDivElement>): void {
    // Second click of a double-click starts a word selection in browsers and
    // surfaces "Traducir" / selection chrome. Kill selection at mousedown.
    if (event.detail >= 2) {
      const target = event.target;
      if (target instanceof Element && target.closest("button")) return;
      event.preventDefault();
    }
  }

  function handleDoubleClick(event: MouseEvent<HTMLDivElement>): void {
    // Let copy/expand buttons keep their own interaction model.
    const target = event.target;
    if (target instanceof Element && target.closest("button")) return;
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    scrollUserTurnIntoFocus(event.currentTarget, 0);
    // Expand only (never collapse on double-click) so the full prompt is
    // visible after jumping to the question start.
    if (canToggle) setExpanded(true);
  }

  const body = isTranscribingEmpty ? (
    <p className="animate-pulse text-body-2 italic text-muted-foreground">Analizando…</p>
  ) : (
    <p
      ref={textRef}
      className={cn(
        "min-w-0 text-body-2 text-foreground",
        expanded ? "whitespace-pre-wrap break-words" : "truncate whitespace-nowrap",
      )}
      title={
        !expanded && (overflows || userTurnLikelyCollapsible(turn.content))
          ? turn.content
          : undefined
      }
    >
      {turn.content}
    </p>
  );

  return (
    <div
      // Match AssistantTurn horizontal bleed (`-mx-3 sm:-mx-4`) so both
      // columns share the same content width inside max-w-3xl.
      className={cn("group/user-turn -mx-3 w-auto pb-2 sm:-mx-4", sticky && "sticky top-0 z-30")}
      data-user-turn={turn.id}
      data-turn-id={turn.id}
      data-user-sticky={sticky ? "true" : "false"}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      title="Doble clic para ir al inicio de la pregunta y expandirla"
    >
      {/* Card shell so the prompt reads as a distinct component vs the answer.
          Everything that pins lives inside it: the card is the only opaque
          surface, so an attachment rendered outside would float transparently
          over the answer while the group scrolls. */}
      <div
        data-testid="user-turn-card"
        className={cn(
          "w-full rounded-xl border border-border/80 bg-muted/95 px-3 py-2.5 shadow-sm backdrop-blur-md sm:px-4",
          "supports-[backdrop-filter]:bg-muted/90",
          "ring-1 ring-inset ring-white/5",
        )}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-label-2 uppercase tracking-wider text-brand-extra-2-40">tú</span>
          <div
            data-testid="user-turn-actions"
            className={cn(
              "flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity",
              "group-hover/user-turn:opacity-100 group-focus-within/user-turn:opacity-100",
            )}
          >
            {showCopy ? <TurnWhen iso={turn.created_at} /> : null}
            {showCopy ? <CopyButton text={turn.content} /> : null}
            {showCopy ? <TurnIdMenu kind="question" messageId={turn.messageId} /> : null}
          </div>
        </div>
        {/* Chevron shares the body-2 line box (14/20) so it sits on the text
            baseline; when expanded, stay top-aligned to the first line. */}
        <div className={cn("flex min-w-0 gap-1", expanded ? "items-start" : "items-center")}>
          {canToggle ? (
            <button
              type="button"
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-label={expanded ? "Contraer mensaje" : "Expandir mensaje"}
              title={expanded ? "Contraer" : "Expandir"}
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-background/60 hover:text-foreground"
            >
              {expanded ? (
                <ChevronDown size={14} strokeWidth={2} />
              ) : (
                <ChevronRight size={14} strokeWidth={2} />
              )}
            </button>
          ) : null}
          <div className="min-w-0 flex-1 leading-5">{body}</div>
        </div>
        {turn.attachments && turn.attachments.length > 0 ? (
          // Compact while collapsed: this card pins, so a full-size thumbnail
          // is charged against the viewport on every scroll. Expanding is the
          // deliberate "show me the question" gesture — grow them there.
          <div className={cn(expanded ? "mt-2" : "mt-1.5")}>
            <AttachmentList attachments={turn.attachments} compact={!expanded} />
          </div>
        ) : null}
      </div>
    </div>
  );
});

/** Walk children produced by react-markdown and swap any inline string
 * with chip-rendered output. Non-string nodes (already-typed JSX from
 * nested markdown like `<strong>`, `<em>`, `<code>`) pass through
 * untouched — chips only matter where the backend emits the raw bracket
 * token, which the markdown parser leaves as a string between siblings. */
/** Fast non-cryptographic 53-bit hash (cyrb53). Used to disambiguate
 * mermaid block ids across identical fence bodies within the same turn,
 * so `mermaid.render` doesn't clobber one SVG with another. */
function cyrb53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

function withCitationChips(children: ReactNode, citations: Citation[]): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === "string") {
      return renderCitationTokens(child, citations);
    }
    return child;
  });
}

/** Consumer of `MermaidStreamingContext` for the markdown fence fallback
 * (the `className === "language-mermaid"` branch of `makeMarkdownComponents`'s
 * `code` component). Reading `generating` from context — rather than closing
 * over it inside `makeMarkdownComponents` — keeps that function's
 * memoization on `[turn.citations, turn.id]` intact: only this small leaf
 * re-renders when the enclosing turn's streaming flag flips, not the whole
 * markdown tree. While the turn is still streaming, the fence body is by
 * definition incomplete (react-markdown exposes an unclosed fence as a
 * growing code block), so `mermaid.render` is never invoked for it — instead
 * a theme-aware diagram skeleton is shown. Once streaming ends (or on
 * history replay, where content already arrives complete), it mounts
 * `MermaidBlock` as before. Exported for direct testing (see
 * `tests/routes/mermaid-fence-streaming.test.tsx`). */
export function MermaidFence({ code, id }: { code: string; id: string }): JSX.Element {
  const { generating } = useContext(MermaidStreamingContext);
  if (generating) {
    return <MermaidStreamingSkeleton />;
  }
  return <MermaidBlock code={code} id={id} />;
}

/** A question after blank-label filtering — see `UserQuestionsFence`'s
 * adversarial-input comment for why options are identified by position. */
interface SanitizedQuestion {
  id: string;
  question: string;
  header: string | undefined;
  mode: "single" | "multi";
  options: { value: string; label: string }[];
}

/** Local UI state for one question inside `UserQuestionsFence`: which normal
 * options are toggled (multi mode), and whether/what the free-text "Otra…"
 * escape holds. Kept per-question (indexed by position in the fence) so
 * switching tabs in a grouped widget preserves in-progress selections
 * instead of resetting them. */
interface QuestionUiState {
  selected: Set<number>;
  otherOpen: boolean;
  otherText: string;
}

function emptyQuestionUiState(): QuestionUiState {
  return { selected: new Set(), otherOpen: false, otherText: "" };
}

/** The integrated free-text escape ("Otra…") appended after a question's
 * option chips, per the "Selectable Answer Options" design (every rendered
 * question offers a way to answer outside the model's enumeration).
 * Collapsed to a chip; clicking it expands an inline `Input`. `onSubmit` is
 * omitted for multi-mode questions, which fold the typed text into their
 * "Confirmar"/"Siguiente" action instead of submitting straight from here. */
function OtherEscape({
  open,
  text,
  disabled,
  onOpen,
  onTextChange,
  onSubmit,
}: {
  open: boolean;
  text: string;
  disabled: boolean;
  onOpen: () => void;
  onTextChange: (text: string) => void;
  onSubmit?: (() => void) | undefined;
}): JSX.Element {
  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onOpen}>
        Otra…
      </Button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <Input
        value={text}
        disabled={disabled}
        // The input only mounts in direct response to the user clicking the
        // "Otra…" chip, so moving focus into it continues that gesture —
        // without it the user's next keystrokes go nowhere.
        autoFocus
        onChange={(event) => onTextChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
        placeholder="Escribe tu respuesta"
        aria-label="Otra respuesta"
        className="h-8 w-40 text-body-3"
      />
      {onSubmit && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || text.trim().length === 0}
          onClick={onSubmit}
        >
          OK
        </Button>
      )}
    </span>
  );
}

/** Renders a ```user_questions fenced block — the generic selectable-question
 * transport the assistant may emit in any chat turn, generalizing the guided
 * knowledge-training flow's former ```knowledge_options fence (see
 * `openspec/changes/generalize-selectable-user-questions/design.md`).
 * Mirrors `MermaidFence`'s streaming guard: while the enclosing turn is
 * still generating, the fence body is by definition incomplete, so it
 * renders a theme-aware question skeleton instead of being parsed (the user
 * never sees incomplete JSON). Once complete, the body is `JSON.parse`d and
 * Zod-validated (`userQuestionsSchema`, or, when `legacy` is set,
 * `knowledgeOptionsSchema` normalized to a one-question group); any failure
 * falls back to a plain code block rather than throwing.
 *
 * A single question renders inline chips exactly like the legacy component:
 * `mode: "single"` sends an option's label immediately on click; `mode:
 * "multi"` toggles chips and sends the combined selection through an
 * explicit "Confirmar" action. Two or more questions render a tabbed
 * widget: answering a question locally records the answer and auto-advances
 * to the next unanswered tab; revisiting a tab lets the user change its
 * answer; an "Enviar" action, enabled only once every question is answered,
 * sends one combined message. Every question also offers an "Otra…"
 * free-text escape. Exported for direct testing (see
 * `tests/routes/user-questions-fence.test.tsx` and
 * `tests/routes/knowledge-options-fence.test.tsx`). */
export function UserQuestionsFence({
  body,
  interactive = true,
  legacy = false,
}: {
  body: string;
  // Whether the widget can be interacted with. False for a fence in any turn
  // that is no longer the conversation tail — the question(s) were already
  // answered or superseded. This derives inertness from durable turn
  // position instead of the in-memory answer state below, which resets on
  // reload; without it, a historical question's controls become clickable
  // again after a refresh and re-send a stale answer. Defaults to true so
  // the component is interactive when rendered standalone (e.g. in tests).
  interactive?: boolean;
  // True for the legacy ```knowledge_options fence name: parses with
  // `knowledgeOptionsSchema` and normalizes to a one-question group instead
  // of parsing `userQuestionsSchema` directly.
  legacy?: boolean;
}): JSX.Element {
  const { generating } = useContext(MermaidStreamingContext);
  const { sendMessage } = useChatInputContext();

  // Hooks run unconditionally — declared before the streaming/parse-failure
  // early returns below so conditional-hook-call rules stay satisfied no
  // matter which branch this render takes.
  const [uiState, setUiState] = useState<Record<number, QuestionUiState>>({});
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  // Single-question fence only: mirrors the legacy component's inert flag
  // (the grouped widget instead tracks inertness via `submitted` above).
  const [singleAnswered, setSingleAnswered] = useState(false);

  if (generating) {
    return <UserQuestionsStreamingSkeleton />;
  }

  const fallback = (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{body}</code>
  );

  let group: UserQuestions | null = null;
  try {
    const json: unknown = JSON.parse(body);
    if (legacy) {
      const result = knowledgeOptionsSchema.safeParse(json);
      group = result.success ? normalizeKnowledgeOptions(result.data) : null;
    } else {
      const result = userQuestionsSchema.safeParse(json);
      group = result.success ? result.data : null;
    }
  } catch {
    group = null;
  }

  if (group === null) {
    return fallback;
  }

  // The options (and questions) are model-generated, so treat them as
  // adversarial: drop blank-label options (an empty chip is never
  // actionable) and identify options by their position, not their `value`.
  // Duplicate `value`s would otherwise collide as React keys (dev warning +
  // possible dropped chip) and as selection ids (toggling one would select
  // every same-value chip and send all their labels). A question left with
  // zero renderable options makes the *whole* fence fall back — a widget
  // with an unanswerable question is worse than raw JSON.
  const questions: SanitizedQuestion[] = group.questions.map((q) => ({
    id: q.id,
    question: q.question,
    header: q.header,
    mode: q.mode,
    options: q.options.filter((option) => option.label.trim().length > 0),
  }));
  if (questions.some((q) => q.options.length === 0)) {
    return fallback;
  }

  function getUiState(index: number): QuestionUiState {
    return uiState[index] ?? emptyQuestionUiState();
  }

  // Both updaters derive the next state from the updater's `prev` argument —
  // never from the render-scope `uiState` — so two updates batched into the
  // same React flush (e.g. programmatic double-toggle) compose instead of the
  // second overwriting the first with a stale base.
  function updateUiState(index: number, next: Partial<QuestionUiState>): void {
    setUiState((prev) => ({
      ...prev,
      [index]: { ...(prev[index] ?? emptyQuestionUiState()), ...next },
    }));
  }

  function toggleOption(index: number, optionIndex: number): void {
    setUiState((prev) => {
      const state = prev[index] ?? emptyQuestionUiState();
      const nextSelected = new Set(state.selected);
      if (nextSelected.has(optionIndex)) {
        nextSelected.delete(optionIndex);
      } else {
        nextSelected.add(optionIndex);
      }
      return { ...prev, [index]: { ...state, selected: nextSelected } };
    });
  }

  // Combines a multi-mode question's toggled chips plus its "Otra…" text (if
  // expanded and non-empty) into the comma-joined answer string, or `null`
  // when nothing is selected yet (used to gate the confirm/advance action).
  function composeMultiAnswer(index: number): string | null {
    const q = questions[index];
    if (!q) return null;
    const state = getUiState(index);
    const labels = q.options
      .filter((_, optIdx) => state.selected.has(optIdx))
      .map((option) => option.label);
    if (state.otherOpen && state.otherText.trim().length > 0) {
      labels.push(state.otherText.trim());
    }
    return labels.length > 0 ? labels.join(", ") : null;
  }

  function nextUnanswered(
    fromIndex: number,
    finalizedAnswers: Record<number, string>,
  ): number | null {
    for (let offset = 1; offset <= questions.length; offset += 1) {
      const candidate = (fromIndex + offset) % questions.length;
      if (finalizedAnswers[candidate] === undefined) return candidate;
    }
    return null;
  }

  // Records `label` as question `index`'s answer inside the grouped widget.
  // Auto-advances to the next unanswered tab only the first time a question
  // is answered — revisiting an already-answered tab to change its answer
  // (see the "Answers can be revised" scenario) must not yank the user away
  // from the tab they are editing.
  function recordAnswer(index: number, label: string): void {
    // Compute the next answers map here (not inside the updater) so the
    // updater stays pure — StrictMode double-invokes updaters, and calling
    // `setActiveIndex` from inside one is a side effect.
    const wasAnswered = answers[index] !== undefined;
    const next = { ...answers, [index]: label };
    setAnswers(next);
    if (!wasAnswered) {
      const upcoming = nextUnanswered(index, next);
      if (upcoming !== null) setActiveIndex(upcoming);
    }
  }

  const disabledGlobally = !interactive || submitted;

  // --- Single-question fence: identical chip behavior to the legacy
  // component, plus the integrated "Otra…" free-text escape. ---
  if (questions.length === 1) {
    const q = questions[0];
    if (!q) return fallback;
    const state = getUiState(0);
    const disabled = disabledGlobally || singleAnswered;

    function submitSingle(label: string): void {
      if (disabledGlobally || singleAnswered) return;
      setSingleAnswered(true);
      void sendMessage(label);
    }

    if (q.mode === "single") {
      return (
        <fieldset
          className="m-0 flex flex-wrap items-center gap-2 border-0 p-0 my-2"
          aria-label="Opciones"
        >
          {q.options.map((option, index) => (
            <Button
              // biome-ignore lint/suspicious/noArrayIndexKey: options are parsed once per fence and never reordered, so index is a stable identity — and it dedupes duplicate `value`s that would otherwise collide as keys.
              key={index}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => submitSingle(option.label)}
            >
              {option.label}
            </Button>
          ))}
          <OtherEscape
            open={state.otherOpen}
            text={state.otherText}
            disabled={disabled}
            onOpen={() => updateUiState(0, { otherOpen: true })}
            onTextChange={(text) => updateUiState(0, { otherText: text })}
            onSubmit={() => {
              const text = state.otherText.trim();
              if (text.length > 0) submitSingle(text);
            }}
          />
        </fieldset>
      );
    }

    const multiAnswer = composeMultiAnswer(0);
    return (
      <fieldset
        className="m-0 flex flex-wrap items-center gap-2 border-0 p-0 my-2"
        aria-label="Opciones"
      >
        {q.options.map((option, index) => (
          <Button
            // biome-ignore lint/suspicious/noArrayIndexKey: options are parsed once per fence and never reordered, so index is a stable identity — and it dedupes duplicate `value`s that would otherwise collide as keys.
            key={index}
            type="button"
            variant={state.selected.has(index) ? "default" : "outline"}
            size="sm"
            disabled={disabled}
            onClick={() => toggleOption(0, index)}
          >
            {option.label}
          </Button>
        ))}
        <OtherEscape
          open={state.otherOpen}
          text={state.otherText}
          disabled={disabled}
          onOpen={() => updateUiState(0, { otherOpen: true })}
          onTextChange={(text) => updateUiState(0, { otherText: text })}
        />
        <Button
          type="button"
          variant="cta"
          size="sm"
          disabled={disabled || multiAnswer === null}
          onClick={() => {
            if (multiAnswer !== null) submitSingle(multiAnswer);
          }}
        >
          Confirmar
        </Button>
      </fieldset>
    );
  }

  // --- Grouped widget: tabbed multi-question flow, Claude Code style. ---
  const allAnswered = questions.every((_, index) => answers[index] !== undefined);

  function submitGroup(): void {
    if (disabledGlobally || !allAnswered) return;
    // One line per question, `<header or question>: <answer>` — plain text,
    // because `UserTurn` renders the sent message verbatim (no markdown
    // pipeline), so bold markers would show as literal asterisks. Still
    // unambiguous for the model on the next turn.
    const lines = questions.map((q, index) => {
      const label = q.header?.trim() ? q.header.trim() : q.question;
      return `${label}: ${answers[index]}`;
    });
    setSubmitted(true);
    void sendMessage(lines.join("\n"));
  }

  const activeQuestion = questions[activeIndex];
  if (!activeQuestion) return fallback;
  const activeState = getUiState(activeIndex);
  const activeMultiAnswer = composeMultiAnswer(activeIndex);

  return (
    <div className="my-2 rounded-md border border-border">
      <div
        role="tablist"
        aria-label="Preguntas"
        className="flex flex-wrap gap-1 border-b border-border p-1"
      >
        {questions.map((q, index) => {
          // Tab label fallback is index-based ("Pregunta N") — distinct from
          // the combined-message fallback below, which falls back to the
          // full question text instead.
          const label = q.header?.trim() ? q.header.trim() : `Pregunta ${index + 1}`;
          const answered = answers[index] !== undefined;
          return (
            <Button
              // biome-ignore lint/suspicious/noArrayIndexKey: question `id`s are model-generated and carry no uniqueness guarantee — two questions reusing an id would collide as React keys. Position is the stable identity, same hardening as the option chips.
              key={index}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              variant={index === activeIndex ? "default" : "outline"}
              size="sm"
              disabled={disabledGlobally}
              onClick={() => setActiveIndex(index)}
              className="gap-1"
            >
              {answered && <Check className="h-3 w-3" />}
              {label}
            </Button>
          );
        })}
      </div>
      <div className="p-3">
        <p className="mb-2 text-body-2">{activeQuestion.question}</p>
        <fieldset
          className="m-0 flex flex-wrap items-center gap-2 border-0 p-0"
          aria-label="Opciones"
        >
          {activeQuestion.options.map((option, optIndex) => {
            const selected =
              activeQuestion.mode === "single"
                ? answers[activeIndex] === option.label
                : activeState.selected.has(optIndex);
            return (
              <Button
                // biome-ignore lint/suspicious/noArrayIndexKey: options are parsed once per fence and never reordered, so index is a stable identity — and it dedupes duplicate `value`s that would otherwise collide as keys.
                key={optIndex}
                type="button"
                variant={selected ? "default" : "outline"}
                size="sm"
                disabled={disabledGlobally}
                onClick={() => {
                  if (activeQuestion.mode === "single") {
                    recordAnswer(activeIndex, option.label);
                  } else {
                    toggleOption(activeIndex, optIndex);
                  }
                }}
              >
                {option.label}
              </Button>
            );
          })}
          <OtherEscape
            open={activeState.otherOpen}
            text={activeState.otherText}
            disabled={disabledGlobally}
            onOpen={() => updateUiState(activeIndex, { otherOpen: true })}
            onTextChange={(text) => updateUiState(activeIndex, { otherText: text })}
            onSubmit={
              activeQuestion.mode === "single"
                ? () => {
                    const text = activeState.otherText.trim();
                    if (text.length > 0) recordAnswer(activeIndex, text);
                  }
                : undefined
            }
          />
          {activeQuestion.mode === "multi" && (
            <Button
              type="button"
              variant="cta"
              size="sm"
              disabled={disabledGlobally || activeMultiAnswer === null}
              onClick={() => {
                if (activeMultiAnswer !== null) recordAnswer(activeIndex, activeMultiAnswer);
              }}
            >
              Siguiente
            </Button>
          )}
        </fieldset>
      </div>
      <div className="flex justify-end border-t border-border p-2">
        <Button
          type="button"
          variant="cta"
          size="sm"
          disabled={disabledGlobally || !allAnswered}
          onClick={submitGroup}
        >
          Enviar
        </Button>
      </div>
    </div>
  );
}

/** Thin backward-compatible wrapper around `UserQuestionsFence` for the
 * legacy ```knowledge_options fence name: parses with `knowledgeOptionsSchema`
 * and normalizes to a one-question group, so historical guided-training
 * turns keep rendering with unchanged observable behavior. New assistant
 * output uses `UserQuestionsFence` directly (`language-user_questions`). */
export function KnowledgeOptionsFence({
  body,
  interactive = true,
}: {
  body: string;
  interactive?: boolean;
}): JSX.Element {
  return <UserQuestionsFence body={body} interactive={interactive} legacy />;
}

function makeMarkdownComponents(
  citations: Citation[],
  turnId: string,
  // Forwarded to `UserQuestionsFence`/`KnowledgeOptionsFence`: chips are only
  // clickable in the conversation's tail turn (see that component's
  // `interactive` prop).
  interactive: boolean,
): Components {
  // Every text-bearing element gets `withCitationChips`. Originally
  // only `p`/`li`/`td` were wrapped, which left `[self-N]` literal
  // when the backend emitted a token inside a heading, bold run,
  // emphasis, or table header. `code`/`pre` stay untouched on purpose
  // — citation tokens inside a code fence should render verbatim.
  return {
    h1: ({ children, ...rest }) => (
      <h1 className="mb-2 mt-4 text-heading-3 font-bold" {...rest}>
        {withCitationChips(children, citations)}
      </h1>
    ),
    h2: ({ children, ...rest }) => (
      <h2 className="mb-2 mt-3 text-heading-4 font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </h2>
    ),
    h3: ({ children, ...rest }) => (
      <h3 className="mb-1 mt-3 text-body-1 font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </h3>
    ),
    h4: ({ children, ...rest }) => (
      <h4 className="mb-1 mt-2 text-body-2 font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </h4>
    ),
    h5: ({ children, ...rest }) => (
      <h5 className="mb-1 mt-2 text-body-2 font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </h5>
    ),
    h6: ({ children, ...rest }) => (
      <h6 className="mb-1 mt-2 text-body-3 font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </h6>
    ),
    p: ({ children, ...rest }) => (
      <p className="my-2 text-body-2" {...rest}>
        {withCitationChips(children, citations)}
      </p>
    ),
    ul: (props) => <ul className="my-2 list-disc space-y-1 pl-5 text-body-2" {...props} />,
    ol: (props) => <ol className="my-2 list-decimal space-y-1 pl-5 text-body-2" {...props} />,
    li: ({ children, ...rest }) => <li {...rest}>{withCitationChips(children, citations)}</li>,
    strong: ({ children, ...rest }) => (
      <strong className="font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </strong>
    ),
    em: ({ children, ...rest }) => (
      <em className="italic" {...rest}>
        {withCitationChips(children, citations)}
      </em>
    ),
    code: ({ children, className, node, ...rest }) => {
      // Markdown-fence safety net: if the LLM ignores the system prompt
      // and emits a ```mermaid fence in prose (instead of, or in
      // addition to, calling `render_diagram`), still render an SVG —
      // not raw mermaid source. The id combines (a) the turn id, (b)
      // the AST source offset of this fence, and (c) the code hash so
      // two distinct fences with identical bodies don't collide on
      // mermaid's internal id table. Re-renders of the same turn
      // reuse the same id (offset is stable across renders).
      if (className === "language-mermaid") {
        const code = String(children ?? "").trim();
        const offset = node?.position?.start?.offset ?? 0;
        const id = `md-${turnId}-${offset}-${cyrb53(code)}`;
        return <MermaidFence code={code} id={id} />;
      }
      // Generic selectable-question fence (see
      // `openspec/changes/generalize-selectable-user-questions/design.md`):
      // the assistant may emit this in any chat turn, not only guided
      // training. Same fence pattern as ```mermaid above, rendered as
      // clickable chips (one question) or a tabbed widget (2+) instead of
      // raw JSON.
      if (className === "language-user_questions") {
        const body = String(children ?? "").trim();
        const offset = node?.position?.start?.offset ?? 0;
        const key = `md-${turnId}-${offset}-${cyrb53(body)}`;
        return <UserQuestionsFence key={key} body={body} interactive={interactive} />;
      }
      // Legacy guided knowledge-training fence (see
      // `openspec/changes/add-guided-knowledge-training-flow/design.md`,
      // "Selectable Answer Options"). No longer emitted by the prompt — kept
      // so historical turns keep rendering as chips via the normalizing
      // `KnowledgeOptionsFence` wrapper.
      if (className === "language-knowledge_options") {
        const body = String(children ?? "").trim();
        const offset = node?.position?.start?.offset ?? 0;
        const key = `md-${turnId}-${offset}-${cyrb53(body)}`;
        return <KnowledgeOptionsFence key={key} body={body} interactive={interactive} />;
      }
      return (
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]" {...rest}>
          {children}
        </code>
      );
    },
    pre: (props) => (
      <pre className="my-2 overflow-x-auto rounded bg-muted p-3 font-mono text-body-3" {...props} />
    ),
    hr: (props) => <hr className="my-3 border-border" {...props} />,
    a: (props) => (
      <a
        className="text-brand-extra-2-40 underline-offset-2 hover:underline"
        target="_blank"
        rel="noreferrer"
        {...props}
      />
    ),
    // GFM tables. Wrap in a scroll container so wide tables don't blow
    // out the chat width.
    table: (props) => (
      <div className="my-3 overflow-x-auto">
        <table className="w-full border-collapse text-body-3" {...props} />
      </div>
    ),
    thead: (props) => <thead className="bg-muted/50" {...props} />,
    tbody: (props) => <tbody {...props} />,
    tr: (props) => <tr className="border-b border-border last:border-b-0" {...props} />,
    th: ({ children, ...rest }) => (
      <th className="border border-border px-2 py-1 text-left font-semibold" {...rest}>
        {withCitationChips(children, citations)}
      </th>
    ),
    td: ({ children, ...rest }) => (
      <td className="border border-border px-2 py-1 align-top" {...rest}>
        {withCitationChips(children, citations)}
      </td>
    ),
  };
}

function GeneratingIndicator(): JSX.Element {
  return (
    <p
      className="mt-2 flex items-center gap-2 text-body-2 text-muted-foreground"
      aria-live="polite"
    >
      <span className="inline-block size-2 shrink-0 animate-pulse rounded-full bg-brand-extra-2-40" />
      Respondiendo…
    </p>
  );
}

function VoiceSearchIndicator({
  state,
}: {
  state: NonNullable<ChatTurn["voiceSearch"]>;
}): JSX.Element {
  const Icon = state.status === "searching" ? Loader2 : state.status === "done" ? Check : X;
  return (
    <p
      className="mt-2 flex items-center gap-2 text-body-2 text-muted-foreground"
      aria-live="polite"
    >
      <Icon
        className={`size-4 shrink-0 ${state.status === "searching" ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
      {state.message}
    </p>
  );
}

export const AssistantTurn = memo(function AssistantTurn({
  turn,
  streaming,
  sessionId,
  onSourceClick,
  onJumpToResearchTrigger,
  onOpenResearchDetail,
  onOpenReport,
  defaultDetailsExpanded = false,
  hideResearchDetailButton = false,
  isLatestTurn = true,
  originatingQuestion,
}: {
  turn: ChatTurn;
  streaming: boolean;
  sessionId?: string;
  onSourceClick?: (sourceId: string) => void;
  onJumpToResearchTrigger?: () => void;
  onOpenResearchDetail?: (taskId: string) => void;
  onOpenReport?: (report: ReportRef) => void;
  defaultDetailsExpanded?: boolean;
  hideResearchDetailButton?: boolean;
  // Whether this turn is the conversation tail. Only the tail turn's guided
  // `knowledge_options` chips stay clickable; historical turns render them
  // inert so a reload can't reactivate an already-answered question. Defaults
  // to true for standalone renders (tests).
  isLatestTurn?: boolean;
  // The user turn's content that triggered this assistant turn, if known.
  // Only `StageAccessCard` uses it (auto re-send the pending question once
  // access is granted) — undefined when the caller can't establish that
  // pairing (e.g. orphan assistant turns).
  originatingQuestion?: string;
}): JSX.Element {
  const hasContent = turn.content.length > 0;
  const hasThinking = turn.thinking.length > 0;
  // Each turn renders its own `[doc-N]` / `[self-N]` chips against its
  // own citation list — recompute the markdown components whenever the
  // citations grow during streaming.
  const markdownComponents = useMemo(
    () => makeMarkdownComponents(turn.citations, turn.id, isLatestTurn),
    [turn.citations, turn.id, isLatestTurn],
  );
  // Lets the inline citation chips open the source detail on click via the
  // `onSourceClick` callback `ChatInner` owns (`openSourceDetail`). It's
  // referentially stable, so the memoized markdown doesn't re-render.
  const citationCtx = useMemo(() => ({ onSourceClick: onSourceClick ?? null }), [onSourceClick]);
  // Feeds `MermaidStreamingContext` so the fence fallback (`MermaidFence`,
  // inside `markdownComponents` above) knows whether this turn is still
  // streaming, without that memoized components object needing the flag
  // in its own deps. Two distinct signals, one per surface: the live web
  // send path tracks its in-flight turn via `streamingTurnId` and passes it
  // down as the `streaming` prop (`turn.generating` is NEVER set on that
  // path), while rehydrated in-flight turns and voice-training placeholders
  // carry `turn.generating` instead. Memoized so the provider value only
  // changes identity when the effective flag flips — that identity change
  // is what makes the fence re-render from placeholder to SVG at stream end.
  const mermaidStreamingCtx = useMemo(
    () => ({ generating: streaming || turn.generating === true }),
    [streaming, turn.generating],
  );
  // Used by the `render_diagram` `onRendered` callback below to swap
  // the backend shape-check timing (~0ms) for the actual wall-clock
  // the operator waited on (mermaid lazy import + render + layout).
  const { setTurns, sendMessage } = useChatInputContext();
  // `generation_failed` and `rate_limited` both get a retry action — the
  // two codes the backend's generic exception handler emits alongside its
  // never-empty fallback message (a 429 gets its own code so the UI can
  // tell "provider is temporarily busy, retrying should help" apart from
  // an opaque failure — see `harden-self-knowledge-seeding-and-rate-limit-ux`
  // Decision 4). `turn_timeout` and `tool_iteration_cap_exceeded` stay plain
  // notices for now. See `recover-chat-turn-hard-failures`.
  const isRetryableGenerationFailure =
    turn.generationFailed === true ||
    turn.errorCode === "generation_failed" ||
    turn.errorCode === "rate_limited";
  const retryQuestion = isRetryableGenerationFailure ? originatingQuestion : undefined;
  return (
    <div className="group/assistant-turn relative -mx-3 rounded-none px-3 py-2 transition-colors hover:bg-black/[0.025] dark:hover:bg-white/[0.025] sm:-mx-4 sm:rounded-lg sm:px-4">
      <RoleLabel label="beflow" />
      {hasThinking && <ThinkingBlock text={turn.thinking} streaming={!hasContent} />}
      {turn.voiceSearch && <VoiceSearchIndicator state={turn.voiceSearch} />}
      {/* Most tool dispatches now fold under their matching `tool.*`
       * step row (see `PipelineSteps`) — keeping them inline duplicated
       * the visual weight of the conversation. `render_diagram` stays
       * inline because the chart IS the deliverable; collapsing it
       * under a step row would hide the answer the operator asked for. */}
      {(turn.toolCalls ?? [])
        .filter((tc) => tc.name === "render_diagram")
        .map((tc, callIdx) => (
          <ToolCallBlock
            key={tc.id}
            call={tc}
            onRendered={(renderMs) => {
              // The N-th `render_diagram` call pairs with the N-th
              // `tool.render_diagram` step (both walk dispatch order).
              // Backend's `elapsed_ms` for this step is the in-process
              // shape check (~0ms), which under-reports what the
              // operator waited on. Replace it with the visible render
              // wall-clock so the step row's chip reads honestly.
              setTurns((prev) =>
                prev.map((t) => {
                  if (t.id !== turn.id) return t;
                  let counter = 0;
                  let stepIdx = -1;
                  for (let i = 0; i < t.steps.length; i++) {
                    if (t.steps[i]?.id !== "tool.render_diagram") continue;
                    if (counter === callIdx) {
                      stepIdx = i;
                      break;
                    }
                    counter++;
                  }
                  if (stepIdx === -1) return t;
                  return {
                    ...t,
                    steps: t.steps.map((s, i) =>
                      i === stepIdx ? { ...s, elapsedMs: Math.round(renderMs) } : s,
                    ),
                  };
                }),
              );
            }}
          />
        ))}
      {hasContent ? (
        <>
          <MermaidStreamingContext.Provider value={mermaidStreamingCtx}>
            <CitationDetailContext.Provider value={citationCtx}>
              <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                {turn.content}
              </ReactMarkdown>
            </CitationDetailContext.Provider>
          </MermaidStreamingContext.Provider>
          {turn.generating && <GeneratingIndicator />}
        </>
      ) : turn.generating ? (
        <GeneratingIndicator />
      ) : turn.failed || isRetryableGenerationFailure ? (
        <p className="text-body-2 text-muted-foreground">
          {turn.failedError === "plugin_turn_abandoned"
            ? "El cliente MCP no reportó la respuesta de este turno."
            : "Este turno no produjo respuesta."}
        </p>
      ) : !hasThinking && !turn.costLimitNotice && !turn.messageTooLong ? (
        <p className="text-body-2 text-muted-foreground">…</p>
      ) : null}
      {turn.reportRef && onOpenReport ? (
        <ReportCard onOpen={onOpenReport} report={turn.reportRef} />
      ) : null}
      {turn.storyPreparationRef ? (
        <Suspense fallback={null}>
          <StoryPreparationCard preparation={turn.storyPreparationRef} />
        </Suspense>
      ) : null}
      {turn.reportPublishFailed && !turn.reportRef ? (
        <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-body-3 text-amber-100">
          El informe no se publicó (no hay tarjeta de revisión nueva). Podés reintentar con
          «Modificar» o pedir un informe nuevo.
        </p>
      ) : null}
      {turn.responseAssets && turn.responseAssets.length > 0 && sessionId && turn.messageId ? (
        <ResponseAssetGrid
          assets={turn.responseAssets}
          sessionId={sessionId}
          messageId={turn.messageId}
          citations={turn.citations}
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      ) : null}
      {(turn.userMemoryDrafts ?? (turn.userMemoryDraft ? [turn.userMemoryDraft] : [])).map(
        (draft) => (
          <UserMemoryDraftCard key={draft.draft_id} draft={draft} turnId={turn.id} />
        ),
      )}
      {turn.stageAccessRequest ? (
        <StageAccessCard
          request={turn.stageAccessRequest}
          turnId={turn.id}
          {...(originatingQuestion !== undefined ? { originatingQuestion } : {})}
        />
      ) : null}
      {(turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : [])).length > 0 ? (
        (turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : [])).map((proposal) => (
          <FactProposalCardWithContext
            key={proposal.draft_id}
            messageId={turn.id}
            proposal={proposal}
            refining={turn.refining ?? false}
            streaming={streaming}
          />
        ))
      ) : turn.refineKnowledgeCard ? (
        <RefineKnowledgeCardWithContext messageId={turn.id} state={turn.refineKnowledgeCard} />
      ) : (
        turn.learnedFactId && (
          <p className="mt-2 text-label-2 text-brand-feedback-success-00">
            {turn.learnedFactAutoApproved
              ? "dato capturado y procesado automáticamente."
              : "se capturó un nuevo dato para la cola del validador."}
          </p>
        )
      )}
      {turn.error && (
        <p role="alert" className="mt-2 text-label-2 text-destructive">
          {turn.error}
        </p>
      )}
      {retryQuestion !== undefined && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => {
            void sendMessage(retryQuestion);
          }}
        >
          <RotateCw className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          Reintentar
        </Button>
      )}
      {turn.costLimitNotice && (
        <p role="alert" className="mt-2 text-label-2 text-amber-600 dark:text-amber-400">
          {llmCostLimitMessage(turn.costLimitNotice)}
        </p>
      )}
      {turn.messageTooLong && (
        <p role="alert" className="mt-2 text-label-2 text-amber-600 dark:text-amber-400">
          {messageTooLongMessage(turn.messageTooLong.maxChars, turn.messageTooLong.actualChars)}
        </p>
      )}
      {turn.cancelled && (
        <p className="mt-2 text-label-2 text-muted-foreground">◼ Respuesta cancelada</p>
      )}
      {turn.truncated && (
        <output className="mt-2 block text-label-2 text-amber-600 dark:text-amber-400">
          {turn.truncated === "max_tokens"
            ? "⚠ Respuesta truncada: se alcanzó el límite de tokens de salida."
            : "⚠ El modelo rechazó completar esta respuesta."}
        </output>
      )}
      {turn.fabricatedCitations && turn.fabricatedCitations.length > 0 && (
        <output className="mt-2 block text-label-2 text-amber-600 dark:text-amber-400">
          ⚠ El modelo intentó citar {turn.fabricatedCitations.join(", ")}, que no existe
          {turn.fabricatedCitations.length > 1 ? "n" : ""} en el contexto recuperado para este
          turno. Se eliminó del texto; las afirmaciones cercanas quedan sin respaldo verificable.
        </output>
      )}
      {turn.sourceRedactions && turn.sourceRedactions.length > 0 && (
        <output className="mt-2 block text-label-2 text-amber-600 dark:text-amber-400">
          🔒 Se omitió código fuente que el asistente consultó: tu nivel de acceso no permite ver el
          código real. Las explicaciones siguen disponibles.
        </output>
      )}
      {/* While the stream is in flight the live stepper renders above
       * the composer (pinned to the bottom of the viewport) — see
       * `ChatInner` for that slot. Once it completes, `DetailsPanel`
       * folds steps + meta + sources under a single `▸ details` chip
       * here, so the rest position is one collapsed widget instead of
       * three stacked blocks. Rating thumbs live inline between the
       * details chip and copy inside `DetailsPanel`. */}
      {!streaming && (
        <DetailsPanel
          turn={turn}
          hasContent={hasContent}
          defaultExpanded={defaultDetailsExpanded}
          hideResearchDetailButton={hideResearchDetailButton}
          {...(sessionId !== undefined ? { sessionId } : {})}
          {...(onJumpToResearchTrigger ? { onJumpToResearchTrigger } : {})}
          {...(onOpenResearchDetail ? { onOpenResearchDetail } : {})}
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      )}
    </div>
  );
});

type AnswerRatingMutationInput = {
  rating: "up" | "down";
  reason_type?: "other" | null;
  reason?: string | null;
};

type AnswerFeedbackReason = "incorrect" | "outdated" | "contradictory" | "other";

const ANSWER_FEEDBACK_REASONS: readonly { value: AnswerFeedbackReason; label: string }[] = [
  { value: "incorrect", label: "Información incorrecta" },
  { value: "outdated", label: "Información desactualizada" },
  { value: "contradictory", label: "Información contradictoria" },
  { value: "other", label: "Otro problema" },
];

function canShowAnswerRating(
  turn: ChatTurn,
  sessionId: string | undefined,
  hasContent: boolean,
): turn is ChatTurn & { messageId: string } {
  return (
    sessionId !== undefined &&
    hasContent &&
    !turn.generating &&
    !turn.error &&
    turn.messageId !== undefined
  );
}

/** Shared rating state + mutations. Returns thumbs (for the action bar)
 * and an optional reason form (rendered below the bar). */
function useAnswerRating(
  turn: ChatTurn,
  messageId: string,
  sessionId: string,
): {
  thumbs: JSX.Element;
  reasonForm: JSX.Element | null;
} {
  const { setTurns } = useChatInputContext();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [showReason, setShowReason] = useState(turn.answerRating?.rating === "down");
  const [reason, setReason] = useState(turn.answerRating?.reason ?? "");
  const [reasonType, setReasonType] = useState<AnswerFeedbackReason | null>(
    turn.answerRating?.reason_type ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  // Keep the draft reason mirrored to the server value when it changes
  // externally (e.g. the messages query refetches this turn). Form
  // visibility is intentionally NOT re-derived here: it is driven by user
  // intent (open on thumbs-down, close on save/cancel). Re-opening it from
  // the persisted "down" rating would immediately undo the close-on-save.
  useEffect(() => {
    setReason(turn.answerRating?.reason ?? "");
  }, [turn.answerRating?.reason]);

  useEffect(() => {
    setReasonType(turn.answerRating?.reason_type ?? null);
  }, [turn.answerRating?.reason_type]);

  const applyRating = useCallback(
    (next: AnswerRating | undefined): void => {
      setTurns((prev) =>
        prev.map((t) => {
          if (t.id !== turn.id) return t;
          if (next === undefined) {
            const { answerRating: _dropped, ...rest } = t;
            return rest;
          }
          return { ...t, answerRating: next };
        }),
      );
    },
    [setTurns, turn.id],
  );

  const applyKnowledgeFeedback = useCallback(
    (next: ChatKnowledgeFeedback): void => {
      setTurns((prev) =>
        prev.map((candidate) =>
          candidate.id === turn.id ? { ...candidate, knowledgeFeedback: next } : candidate,
        ),
      );
    },
    [setTurns, turn.id],
  );

  const mutation = useMutation({
    mutationFn: (input: AnswerRatingMutationInput) => rateAnswer(messageId, input),
    onMutate: (input) => {
      setError(null);
      const previous = turn.answerRating;
      const now = new Date().toISOString();
      applyRating({
        message_id: messageId,
        rating: input.rating,
        reason_type: input.rating === "down" ? (input.reason_type ?? null) : null,
        reason: input.rating === "down" ? (input.reason ?? null) : null,
        created_at: previous?.created_at ?? now,
        updated_at: now,
      });
      return { previous };
    },
    onSuccess: (data) => applyRating(data),
    onError: (e, _input, context) => {
      // Roll back the optimistic vote so a failed POST doesn't leave the
      // thumb selected (which would route the retry click to DELETE).
      applyRating(context?.previous);
      setError(describeError(e));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: messagesQueryKey(sessionId) });
    },
  });

  const knowledgeMutation = useMutation({
    mutationFn: (input: {
      issue_type: Exclude<AnswerFeedbackReason, "other">;
      explanation: string;
    }) => submitKnowledgeFeedback(messageId, input),
    onMutate: () => setError(null),
    onSuccess: (data) => {
      applyRating(data.rating);
      applyKnowledgeFeedback({
        report_id: data.report_id,
        analysis_status: data.analysis_status,
        verdict: null,
      });
      setShowReason(false);
      showToast({ kind: "success", message: "Reporte enviado para revisión." });
    },
    onError: (e) => setError(describeError(e)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: messagesQueryKey(sessionId) });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => unrateAnswer(messageId),
    onMutate: () => {
      setError(null);
      const previous = turn.answerRating;
      setShowReason(false);
      applyRating(undefined);
      return { previous };
    },
    onError: (e, _input, context) => {
      // Restore the vote the server still holds when the DELETE fails.
      applyRating(context?.previous);
      setError(describeError(e));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: messagesQueryKey(sessionId) });
    },
  });

  const selected = turn.answerRating?.rating;
  const knowledgeReportId =
    turn.knowledgeFeedback?.report_id ?? turn.answerRating?.knowledge_report_id;
  const busy = mutation.isPending || knowledgeMutation.isPending || removeMutation.isPending;

  const submitUp = (): void => {
    if (selected === "up") {
      removeMutation.mutate();
      return;
    }
    setShowReason(false);
    mutation.mutate({ rating: "up", reason_type: null, reason: null });
  };
  const submitDown = (): void => {
    if (reasonType === null) {
      setError("Selecciona el tipo de problema.");
      return;
    }
    if (knowledgeReportId && reasonType !== "other") {
      setError("El conocimiento ya fue reportado; puedes calificar otro problema.");
      return;
    }
    if (reasonType !== "other") {
      const explanation = reason.trim();
      if (!explanation) {
        setError("Describe brevemente qué conocimiento está mal.");
        return;
      }
      knowledgeMutation.mutate({ issue_type: reasonType, explanation });
      return;
    }
    mutation.mutate(
      { rating: "down", reason_type: "other", reason: reason.trim() || null },
      {
        onSuccess: () => {
          // Collapse the input + buttons and confirm with a toast — the vote
          // is persisted, so there's nothing left to edit inline.
          setShowReason(false);
          showToast({ kind: "success", message: "¡Gracias! Aplicamos tu feedback." });
        },
      },
    );
  };
  const selectDown = (): void => {
    if (selected === "down") {
      removeMutation.mutate();
      return;
    }
    setShowReason(true);
  };
  const cancelReason = (): void => {
    // Dismiss the draft form without submitting. If a down vote was already
    // persisted, keep it and restore the saved reason text.
    setShowReason(false);
    setReason(turn.answerRating?.reason ?? "");
    setReasonType(turn.answerRating?.reason_type ?? null);
    setError(null);
  };

  const thumbClass = (active: boolean, activeTone: string): string =>
    cn(
      "size-7 shrink-0 rounded-md p-0 text-muted-foreground hover:bg-muted hover:text-foreground",
      active && cn("bg-muted", activeTone),
    );

  const thumbs = (
    <div className="flex items-center gap-0.5" data-testid="answer-rating-thumbs">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={
          selected === "up" ? "Quitar calificacion positiva" : "Marcar respuesta como util"
        }
        title={selected === "up" ? "Quitar calificacion positiva" : "Marcar respuesta como util"}
        aria-pressed={selected === "up"}
        disabled={busy}
        onClick={submitUp}
        className={thumbClass(selected === "up", "text-brand-feedback-success-00")}
      >
        <ThumbsUp className="size-3.5" aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={
          selected === "down" ? "Quitar calificacion negativa" : "Marcar respuesta como no util"
        }
        title={
          selected === "down" ? "Quitar calificacion negativa" : "Marcar respuesta como no util"
        }
        aria-pressed={selected === "down"}
        disabled={busy}
        onClick={selectDown}
        className={thumbClass(selected === "down", "text-destructive")}
      >
        <ThumbsDown className="size-3.5" aria-hidden="true" />
      </Button>
    </div>
  );

  const reasonForm =
    showReason || error ? (
      <div className="flex max-w-xl flex-col gap-2 text-body-3" data-testid="answer-rating-reason">
        {showReason && (
          <>
            {knowledgeReportId && (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground">
                Este conocimiento ya fue reportado. Puedes seguir su estado en Mis feedbacks o
                calificar otro problema de la respuesta.
              </p>
            )}
            <fieldset className="grid gap-1.5">
              <legend className="mb-1 font-medium text-foreground">
                ¿Qué problema tiene la respuesta?
              </legend>
              {ANSWER_FEEDBACK_REASONS.filter(
                (option) => !knowledgeReportId || option.value === "other",
              ).map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-foreground">
                  <input
                    type="radio"
                    name={`answer-feedback-${messageId}`}
                    value={option.value}
                    checked={reasonType === option.value}
                    disabled={busy}
                    onChange={() => {
                      setReasonType(option.value);
                      setError(null);
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
            <textarea
              aria-label="Motivo de la calificacion"
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              maxLength={reasonType === "other" ? 2000 : 500}
              required={reasonType !== null && reasonType !== "other"}
              placeholder={
                reasonType === "other"
                  ? "Detalle opcional"
                  : "Describe brevemente qué dato está mal y por qué"
              }
              rows={2}
              className="min-h-16 w-full resize-y rounded-md border border-border bg-background px-2 py-1 text-body-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={submitDown}
              >
                {reasonType === "other" || knowledgeReportId
                  ? "Guardar calificacion"
                  : "Enviar reporte"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={cancelReason}
              >
                Cancelar
              </Button>
            </div>
          </>
        )}
        {error && (
          <p role="alert" className="text-label-2 text-destructive">
            {error}
          </p>
        )}
      </div>
    ) : null;

  return { thumbs, reasonForm };
}

/** Host for rating UI inside DetailsPanel: thumbs go in the action row;
 * the reason form (if open) renders below. */
function AnswerRatingHost({
  turn,
  messageId,
  sessionId,
  children,
}: {
  turn: ChatTurn;
  messageId: string;
  sessionId: string;
  children: (parts: {
    thumbs: JSX.Element;
    reasonForm: JSX.Element | null;
  }) => JSX.Element;
}): JSX.Element {
  const parts = useAnswerRating(turn, messageId, sessionId);
  return children(parts);
}

function FactProposalCardWithContext({
  messageId,
  proposal,
  refining,
  streaming,
}: {
  messageId: string;
  proposal: FactProposalState;
  refining: boolean;
  streaming: boolean;
}): JSX.Element {
  const { setInput, setTurns } = useChatInputContext();
  // Use the real chat_messages.id for lifecycle endpoint calls.
  // Fallback to messageId is defensive: rows persisted before this field
  // was added can't carry assistant_message_id, but they also can't be in
  // "pending" state, so no lifecycle call will fire for them.
  const realMessageId = proposal.assistant_message_id ?? messageId;
  return (
    <FactProposalCard
      messageId={realMessageId}
      proposal={proposal}
      refining={refining}
      lifecycleDisabled={shouldDisableFactProposalLifecycle({
        streaming,
        status: proposal.status,
        assistantMessageId: proposal.assistant_message_id,
      })}
      onUpdate={(next) =>
        // Keep using the local turn id as the join key for setTurns — that's
        // how React tracks which turn to update in the turns array.
        setTurns((prev) =>
          prev.map((t) => (t.id === messageId ? updateFactProposalInTurn(t, next) : t)),
        )
      }
      onRefineClick={() => setInput(`[refinando ${proposal.draft_id}] `)}
    />
  );
}

export function shouldDisableFactProposalLifecycle({
  streaming,
  status,
  assistantMessageId,
}: {
  streaming: boolean;
  status: FactProposalState["status"];
  assistantMessageId: string | undefined;
}): boolean {
  return streaming && status === "pending" && assistantMessageId === undefined;
}

function SessionStageGrantsBadges({ sessionId }: { sessionId: string }): JSX.Element | null {
  const grantsQuery = useQuery({
    queryKey: ["chat", "stage-grants", sessionId],
    queryFn: () => listStageGrants(sessionId),
  });
  const stages = (grantsQuery.data?.stages ?? []).filter((s) => s === "demo" || s === "dev");
  if (stages.length === 0) return null;
  return (
    <div
      className="mx-auto flex max-w-3xl flex-wrap items-center gap-2 px-3 pb-2 sm:px-6"
      data-testid="session-stage-grants"
    >
      <span className="text-body-3 text-muted-foreground">acceso de stage activo:</span>
      {stages.map((stage) => (
        <Badge key={stage} variant="info">
          {stage}
        </Badge>
      ))}
    </div>
  );
}

function findStageGrantConfirmation(
  messages: ChatMessageRead[],
  stage: "demo" | "dev",
): ChatMessageRead | null {
  return (
    messages.find((message) => {
      const grant = message.metadata?.stage_grant;
      return (
        typeof grant === "object" && grant !== null && "stage" in grant && grant.stage === stage
      );
    }) ?? null
  );
}

function StageAccessCard({
  request,
  turnId,
  originatingQuestion,
}: {
  request: { stage: "demo" | "dev"; status: "pending" | "granted" };
  turnId: string;
  // The user's original question, if known — re-sent automatically once a
  // fresh grant lands so the operator doesn't have to retype it.
  originatingQuestion?: string;
}): JSX.Element {
  const { setTurns, sessionId, sendMessage } = useChatInputContext();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const grantMutation = useMutation({
    mutationFn: () => {
      if (!sessionId) throw new Error("missing session");
      return grantStageAccess(sessionId, request.stage);
    },
    onMutate: async () => {
      if (sessionId) {
        await queryClient.cancelQueries({ queryKey: messagesQueryKey(sessionId) });
      }
    },
    onSuccess: async (result) => {
      let confirmation = result.confirmation_message;
      if (confirmation === null && sessionId) {
        try {
          const messages = await getMessages(sessionId);
          queryClient.setQueryData(messagesQueryKey(sessionId), messages);
          confirmation = findStageGrantConfirmation(messages, request.stage);
        } catch {
          // The grant succeeded; cache invalidation below retries reconciliation later.
        }
      }
      setTurns((prev) => {
        if (!prev.some((turn) => turn.id === turnId)) return prev;
        const updated = prev.map((t) =>
          t.id === turnId
            ? { ...t, stageAccessRequest: { stage: request.stage, status: "granted" as const } }
            : t,
        );
        if (confirmation === null || updated.some((turn) => turn.id === confirmation.id)) {
          return updated;
        }
        return [...updated, ...messageToTurns(confirmation)];
      });
      if (sessionId) {
        if (confirmation !== null) {
          queryClient.setQueryData<ChatMessageRead[]>(messagesQueryKey(sessionId), (messages) => {
            if (
              messages === undefined ||
              messages.some((message) => message.id === confirmation.id)
            ) {
              return messages;
            }
            return [...messages, confirmation];
          });
        }
        void queryClient.invalidateQueries({ queryKey: ["chat", "stage-grants", sessionId] });
        void queryClient.invalidateQueries({ queryKey: messagesQueryKey(sessionId) });
      }
      // Re-send the pending question now that access is live — only for a
      // fresh grant (`already_granted` means the operator re-clicked a stale
      // card; the question was presumably answered already).
      if (result.status === "granted" && originatingQuestion) {
        void sendMessage(originatingQuestion);
      }
    },
    onError: (e) => setError(describeError(e)),
  });
  const pending = request.status === "pending";
  return (
    <section
      className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-body-2"
      data-testid="stage-access-card"
    >
      <p className="text-label-2 font-medium text-foreground">
        Acceso a stage <span className="uppercase">{request.stage}</span>
      </p>
      <p className="mt-1 text-body-3 text-muted-foreground">
        El asistente pide autorizar consultas en vivo a {request.stage} solo para esta conversación.
        La documentación indexada sigue describiendo producción.
      </p>
      {error ? (
        <p className="mt-2 text-body-3 text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {pending ? (
          <Button
            type="button"
            size="sm"
            disabled={grantMutation.isPending || !sessionId}
            onClick={() => grantMutation.mutate()}
          >
            {grantMutation.isPending ? "autorizando…" : "Autorizar"}
          </Button>
        ) : (
          <Badge variant="success">autorizado</Badge>
        )}
      </div>
    </section>
  );
}

/** The memory a draft changes against the text the person is typing in the inline
 * editor: the trimmed text, because that is what confirming stores. The diff
 * follows the typing through a deferred copy, so computing it never holds a
 * keystroke up. It lives in its own component, mounted when the editor opens, so
 * the deferred copy starts at the text the editor was seeded with and never shows
 * what was typed in an earlier session. */
function TypedMemoryDiff({
  before,
  typedText,
}: {
  before: string;
  typedText: string;
}): JSX.Element {
  const deferredText = useDeferredValue(typedText.trim());
  return (
    <DraftDiff
      before={before}
      after={deferredText}
      beforeLabel="Memoria actual"
      afterLabel="Memoria propuesta"
      label="Diferencias entre la memoria actual y la propuesta"
    />
  );
}

function UserMemoryDraftCard({
  draft,
  turnId,
}: {
  draft: UserMemoryDraftState;
  turnId: string;
}): JSX.Element {
  const { setTurns } = useChatInputContext();
  const queryClient = useQueryClient();
  const pending = draft.status === "pending";
  const editable = draft.operation !== "delete";
  const targetText = draft.target_text ?? draft.target_id ?? null;
  const proposedText =
    draft.operation === "delete" ? targetText : (draft.text ?? targetText ?? "Memoria sin texto");

  const [editing, setEditing] = useState(false);
  // Client-side draft text: a manual inline edit stays here until the user
  // confirms; it is sent as the override on approve (the draft is not
  // re-persisted, so a reload reverts to the original — by design).
  const [editedText, setEditedText] = useState(draft.text ?? "");
  // The text of the memory an `update` draft changes, when the server delivered it: the draft is
  // then shown as a diff against it. `target_id` alone is a UUID, not a text to compare with.
  const memoryNow = draft.operation === "update" && draft.target_text ? draft.target_text : null;
  const [error, setError] = useState<string | null>(null);

  // The real chat_messages.id the lifecycle endpoint targets. Drafts always
  // carry it (set on the SSE event); without it (defensive) actions are
  // disabled rather than silently no-op.
  const messageId = draft.assistant_message_id ?? null;

  const applyDraft = (next: UserMemoryDraftState): void => {
    setTurns((prev) => prev.map((t) => (t.id === turnId ? addUserMemoryDraftToTurn(t, next) : t)));
    // The panel's memory list / archived list may have changed.
    void queryClient.invalidateQueries({ queryKey: ["settings", "user-memory"] });
  };

  const approveMutation = useMutation({
    mutationFn: (text: string | undefined) => {
      if (messageId === null) throw new Error("missing message id");
      return approveUserMemoryDraft(messageId, draft.draft_id, text);
    },
    onSuccess: (data) => {
      setEditing(false);
      applyDraft({
        ...draft,
        status: "approved",
        decided_at: new Date().toISOString(),
        ...(editing && editedText.trim() ? { text: editedText.trim() } : {}),
        ...(data.memory_item_id ? { memory_item_id: data.memory_item_id } : {}),
      });
    },
    onError: (e) => setError(describeError(e)),
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (messageId === null) throw new Error("missing message id");
      return cancelUserMemoryDraft(messageId, draft.draft_id);
    },
    onSuccess: () =>
      applyDraft({ ...draft, status: "cancelled", decided_at: new Date().toISOString() }),
    onError: (e) => setError(describeError(e)),
  });

  const busy = approveMutation.isPending || cancelMutation.isPending;
  const confirm = (): void =>
    approveMutation.mutate(editing && editedText.trim() ? editedText.trim() : undefined);

  return (
    <section
      className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-body-2"
      data-testid="user-memory-draft"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-label-2 font-medium text-foreground">
            {userMemoryDraftOperationLabel(draft.operation)}
          </p>
          {editing ? (
            <textarea
              aria-label="Texto de la memoria"
              className="mt-1 min-h-20 w-full rounded-md border border-border bg-background px-2 py-1 text-body-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              maxLength={2000}
            />
          ) : memoryNow === null ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-foreground">{proposedText}</p>
          ) : null}
          {memoryNow !== null ? (
            <div className="mt-2">
              {editing ? (
                <TypedMemoryDiff before={memoryNow} typedText={editedText} />
              ) : (
                <DraftDiff
                  before={memoryNow}
                  after={draft.text ?? memoryNow}
                  beforeLabel="Memoria actual"
                  afterLabel="Memoria propuesta"
                  label="Diferencias entre la memoria actual y la propuesta"
                />
              )}
            </div>
          ) : null}
          {draft.operation === "update" && memoryNow === null && targetText ? (
            <p className="mt-1 text-label-2 text-muted-foreground">Actualiza: {targetText}</p>
          ) : null}
          <p className="mt-2 text-label-2 text-muted-foreground">
            Estado: {userMemoryDraftStatusLabel(draft.status)}
          </p>
          {error ? <p className="mt-1 text-label-2 text-destructive">{error}</p> : null}
        </div>
        {pending && messageId !== null ? (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label="Confirmar memoria"
              disabled={busy || (editing && editedText.trim() === "")}
              className="rounded-full border border-border bg-background p-1.5 text-brand-feedback-success-00 shadow-sm hover:bg-muted disabled:opacity-50"
              onClick={confirm}
            >
              <Check size={16} />
            </button>
            {editable ? (
              <button
                type="button"
                aria-label={editing ? "Dejar de editar memoria" : "Editar memoria"}
                disabled={busy}
                className="rounded-full border border-border bg-background p-1.5 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground disabled:opacity-50"
                onClick={() => {
                  setError(null);
                  setEditing((v) => {
                    // Seed the textarea from the proposed text when opening.
                    if (!v) setEditedText(draft.text ?? "");
                    return !v;
                  });
                }}
              >
                <Pencil size={16} />
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Cancelar memoria"
              disabled={busy}
              className="rounded-full border border-border bg-background p-1.5 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground disabled:opacity-50"
              onClick={() => cancelMutation.mutate()}
            >
              <X size={16} />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function userMemoryDraftOperationLabel(operation: UserMemoryDraftState["operation"]): string {
  if (operation === "create") return "Guardar en mi memoria";
  if (operation === "update") return "Modificar mi memoria";
  return "Eliminar de mi memoria";
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const payload = error.payload as { detail?: { message?: string } } | undefined;
    return payload?.detail?.message ?? error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

function userMemoryDraftStatusLabel(status: UserMemoryDraftState["status"]): string {
  if (status === "approved") return "aprobada";
  if (status === "cancelled") return "cancelada";
  return "pendiente";
}

function updateFactProposalInTurn(turn: ChatTurn, next: FactProposalState): ChatTurn {
  const factProposal = turn.factProposal?.draft_id === next.draft_id ? next : turn.factProposal;
  const factProposals = turn.factProposals?.map((proposal) =>
    proposal.draft_id === next.draft_id ? next : proposal,
  );
  return {
    ...turn,
    ...(factProposal !== undefined ? { factProposal } : {}),
    ...(factProposals !== undefined ? { factProposals } : {}),
  };
}

/** A successful `approve_knowledge` tool call decides a proposal whose
 * card usually lives in an EARLIER turn: flip it to approved across all
 * turns so the UI reflects the decision without a page reload. Exported
 * for tests. */
export function approveFactProposalAcrossTurns(turns: ChatTurn[], draftId: string): ChatTurn[] {
  return turns.map((turn) => {
    const current = turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : []);
    if (!current.some((p) => p.draft_id === draftId && p.status === "pending")) {
      return turn;
    }
    const factProposals = current.map((p) =>
      p.draft_id === draftId && p.status === "pending"
        ? { ...p, status: "approved" as const, decided_at: new Date().toISOString() }
        : p,
    );
    const first = factProposals[0];
    return {
      ...turn,
      ...(first !== undefined ? { factProposal: first } : {}),
      factProposals,
    };
  });
}

/** Conversational `cancel_knowledge` decided one or more proposals whose
 * cards usually live in earlier turns: flip every matching pending one to
 * cancelled. Exported for tests. */
export function cancelFactProposalsAcrossTurns(turns: ChatTurn[], draftIds: string[]): ChatTurn[] {
  const targets = new Set(draftIds);
  if (targets.size === 0) return turns;
  return turns.map((turn) => {
    const current = turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : []);
    if (!current.some((p) => targets.has(p.draft_id) && p.status === "pending")) {
      return turn;
    }
    const factProposals = current.map((p) =>
      targets.has(p.draft_id) && p.status === "pending"
        ? { ...p, status: "cancelled" as const, decided_at: new Date().toISOString() }
        : p,
    );
    const first = factProposals[0];
    return {
      ...turn,
      ...(first !== undefined ? { factProposal: first } : {}),
      factProposals,
    };
  });
}

/** Conversational `refine_knowledge` rewrote a pending proposal's text in
 * place (same draft_id, still pending): reflect the new text on its card.
 * Exported for tests. */
export function refineFactProposalAcrossTurns(
  turns: ChatTurn[],
  draftId: string,
  text: string,
): ChatTurn[] {
  return turns.map((turn) => {
    const current = turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : []);
    if (!current.some((p) => p.draft_id === draftId && p.status === "pending")) {
      return turn;
    }
    const factProposals = current.map((p) =>
      p.draft_id === draftId && p.status === "pending" ? withRefinedText(p, text) : p,
    );
    const first = factProposals[0];
    return {
      ...turn,
      ...(first !== undefined ? { factProposal: first } : {}),
      factProposals,
    };
  });
}

/** Route a knowledge_lifecycle tool-result outcome (cancel/refine) to the
 * matching cross-turn card update. Exported for tests. */
export function applyKnowledgeLifecycleAcrossTurns(
  turns: ChatTurn[],
  lifecycle: Record<string, unknown>,
): ChatTurn[] {
  if (lifecycle.action === "cancel") {
    const raw = lifecycle.cancelled_draft_ids;
    const ids = Array.isArray(raw)
      ? raw.filter((id): id is string => typeof id === "string" && id.length > 0)
      : [];
    return ids.length > 0 ? cancelFactProposalsAcrossTurns(turns, ids) : turns;
  }
  if (lifecycle.action === "refine") {
    const draftId = lifecycle.draft_id;
    const text = lifecycle.text;
    if (typeof draftId === "string" && draftId && typeof text === "string" && text) {
      return refineFactProposalAcrossTurns(turns, draftId, text);
    }
  }
  return turns;
}

function addFactProposalToTurn(turn: ChatTurn, next: FactProposalState): ChatTurn {
  const current = turn.factProposals ?? (turn.factProposal ? [turn.factProposal] : []);
  const factProposals = current.some((proposal) => proposal.draft_id === next.draft_id)
    ? current.map((proposal) => (proposal.draft_id === next.draft_id ? next : proposal))
    : [...current, next];
  const first = factProposals[0] ?? next;
  return {
    ...turn,
    factProposal: first,
    factProposals,
  };
}

function addUserMemoryDraftToTurn(turn: ChatTurn, next: UserMemoryDraftState): ChatTurn {
  const current = turn.userMemoryDrafts ?? (turn.userMemoryDraft ? [turn.userMemoryDraft] : []);
  const userMemoryDrafts = current.some((draft) => draft.draft_id === next.draft_id)
    ? current.map((draft) => (draft.draft_id === next.draft_id ? next : draft))
    : [...current, next];
  const first = userMemoryDrafts[0] ?? next;
  return {
    ...turn,
    userMemoryDraft: first,
    userMemoryDrafts,
  };
}

function RefineKnowledgeCardWithContext({
  messageId,
  state,
}: {
  messageId: string;
  state: RefineKnowledgeCardState;
}): JSX.Element {
  const { setTurns, onRefineKnowledgeApproved } = useChatInputContext();
  return (
    <RefineKnowledgeCard
      state={state}
      onUpdate={(next) => {
        setTurns((prev) =>
          prev.map((t) => (t.id === messageId ? { ...t, refineKnowledgeCard: next } : t)),
        );
        if (next.status === "approved") onRefineKnowledgeApproved?.(next.itemId, next.refinedText);
      }}
    />
  );
}

/** Post-stream consolidated panel: a single collapsed chip
 * (`▸ details · X s · Y sources`) that, when expanded, surfaces the
 * step tree, the meta strip (model / tokens / cost / retrieval) and a
 * nested sources sub-collapsible — the three blocks that used to
 * float free under every assistant turn.
 *
 * Why one widget instead of three: the operator's eye was being
 * tugged across three separate toggles for the same conceptual thing
 * ("how did this answer get built?"). Consolidating reduces visual
 * noise and keeps the conversation flow obvious; the underlying
 * components (`StepsTree`, `MetaStrip`, `SourcesBlock`) are reused
 * verbatim once the panel is open.
 *
 * Legacy fallback: turns persisted before `metadata.steps` existed
 * carry no step history. In that case there's nothing to summarize
 * under "details", so the meta strip and sources block render free-
 * standing the way they used to — the user still sees the bits of
 * context we do have. */
function DetailsPanel({
  turn,
  hasContent,
  onSourceClick,
  onJumpToResearchTrigger,
  onOpenResearchDetail,
  defaultExpanded = false,
  hideResearchDetailButton = false,
  sessionId,
}: {
  turn: ChatTurn;
  hasContent: boolean;
  onSourceClick?: (sourceId: string) => void;
  onJumpToResearchTrigger?: () => void;
  onOpenResearchDetail?: (taskId: string) => void;
  defaultExpanded?: boolean;
  hideResearchDetailButton?: boolean;
  sessionId?: string;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sourcesCount = turn.citations.length;
  const anyStepRunning = turn.steps.some((s) => s.status === "running");
  const [, setLiveTick] = useState(0);
  useEffect(() => {
    if (!anyStepRunning) return;
    const id = window.setInterval(() => setLiveTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(id);
  }, [anyStepRunning]);
  const hasMeta = !!(
    turn.model ||
    turn.retrieval ||
    turn.usage ||
    turn.reasoningLevel ||
    turn.backgroundUsage
  );
  const researchTaskId = turn.backgroundTaskId;
  const researchIcon =
    researchTaskId !== undefined && !hideResearchDetailButton ? (
      onOpenResearchDetail ? (
        <button
          type="button"
          onClick={() => onOpenResearchDetail(researchTaskId)}
          aria-label="abrir detalle de investigacion"
          title="Abrir detalle de investigacion"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Microscope aria-hidden="true" className="h-4 w-4" />
        </button>
      ) : (
        <Microscope
          aria-label="investigacion en segundo plano"
          className="h-4 w-4 text-muted-foreground"
        />
      )
    ) : null;

  const ratingMessageId =
    canShowAnswerRating(turn, sessionId, hasContent) && sessionId !== undefined
      ? turn.messageId
      : undefined;
  const wrapWithRating = (
    render: (thumbs: JSX.Element | null, reasonForm: JSX.Element | null) => JSX.Element,
  ): JSX.Element => {
    if (ratingMessageId === undefined || sessionId === undefined) return render(null, null);
    return (
      <AnswerRatingHost turn={turn} messageId={ratingMessageId} sessionId={sessionId}>
        {({ thumbs, reasonForm }) => render(thumbs, reasonForm)}
      </AnswerRatingHost>
    );
  };

  if (turn.steps.length === 0) {
    // Legacy / step-less turn — render the old layout inline so we
    // don't lose meta or sources. Right cluster: when · thumbs · copy.
    return wrapWithRating((thumbs, reasonForm) => (
      <div className="mt-3 flex flex-col gap-2">
        {hasContent && hasMeta && <MetaStrip turn={turn} />}
        {hasContent && sourcesCount > 0 && (
          <SourcesBlock citations={turn.citations} {...(onSourceClick ? { onSourceClick } : {})} />
        )}
        <div
          className={cn(
            "flex w-full items-center justify-end gap-1.5 opacity-0 transition-opacity",
            "group-hover/assistant-turn:opacity-100 group-focus-within/assistant-turn:opacity-100",
          )}
        >
          {hasContent && <TurnWhen iso={turn.created_at} />}
          {thumbs}
          {hasContent && <CopyButton text={turn.content} />}
          {hasContent && (
            <ShareTurnButton
              sessionId={sessionId}
              messageId={turn.messageId}
              responseAssets={turn.responseAssets}
            />
          )}
          {hasContent && <TurnIdMenu kind="answer" messageId={turn.messageId} />}
        </div>
        {reasonForm}
      </div>
    ));
  }

  // Same top-level-only sum used by the live stepper, so the rest
  // chip reads the same total the user saw streaming above the
  // composer.
  const stepIds = new Set(turn.steps.map((s) => s.id));
  const backendTotalMs = turn.steps
    .filter((s) => {
      const parent = parentOf(s.id);
      return parent === null || !stepIds.has(parent);
    })
    .reduce((acc, s) => acc + (s.elapsedMs ?? 0), 0);
  const resolvedBackendTotalMs =
    turn.backgroundElapsedSeconds !== undefined
      ? Math.round(turn.backgroundElapsedSeconds * 1000)
      : backendTotalMs;
  const liveAnchor =
    turn.startedAt ?? Math.min(...turn.steps.map((s) => s.startedAt).filter(Boolean));
  const totalMs =
    anyStepRunning && Number.isFinite(liveAnchor)
      ? Math.max(resolvedBackendTotalMs, performance.now() - liveAnchor)
      : resolvedBackendTotalMs;

  if (!expanded) {
    return wrapWithRating((thumbs, reasonForm) => (
      <div className="mt-3 flex flex-col gap-2">
        <div
          className={cn(
            "flex w-full items-center gap-1.5 opacity-0 transition-opacity",
            "group-hover/assistant-turn:opacity-100 group-focus-within/assistant-turn:opacity-100",
          )}
        >
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-body-3 text-muted-foreground hover:bg-black/[0.025] dark:hover:bg-white/[0.025]"
          >
            <span>
              ▸ details · {formatTotalElapsed(totalMs)} · {sourcesCount} sources
            </span>
          </button>
          {researchIcon}
          <div className="ml-auto flex items-center gap-1.5">
            {hasContent && <TurnWhen iso={turn.created_at} />}
            {thumbs}
            {hasContent && <CopyButton text={turn.content} />}
            {hasContent && (
              <ShareTurnButton
                sessionId={sessionId}
                messageId={turn.messageId}
                responseAssets={turn.responseAssets}
              />
            )}
            {hasContent && <TurnIdMenu kind="answer" messageId={turn.messageId} />}
          </div>
        </div>
        {reasonForm}
      </div>
    ));
  }

  return wrapWithRating((thumbs, reasonForm) => (
    <div className="mt-3 flex flex-col gap-0.5 text-label-2">
      <div className="mb-0.5 flex flex-col gap-2">
        <div className="flex w-full items-center gap-1.5">
          <button
            type="button"
            onClick={() => setExpanded(false)}
            aria-label="hide details"
            className="inline-flex w-fit items-center gap-1 rounded px-1 py-px text-body-3 text-muted-foreground hover:bg-black/[0.025] hover:text-foreground dark:hover:bg-white/[0.025]"
          >
            <span>
              ▾ details · {formatTotalElapsed(totalMs)} · {sourcesCount} sources
            </span>
          </button>
          {researchIcon}
          <div className="ml-auto flex items-center gap-1.5">
            {hasContent && <TurnWhen iso={turn.created_at} />}
            {thumbs}
            {hasContent && <CopyButton text={turn.content} />}
            {hasContent && (
              <ShareTurnButton
                sessionId={sessionId}
                messageId={turn.messageId}
                responseAssets={turn.responseAssets}
              />
            )}
            {hasContent && <TurnIdMenu kind="answer" messageId={turn.messageId} />}
          </div>
        </div>
        {reasonForm}
      </div>
      {/* Same bounded, scrollable viewport as the live stepper: a long turn
          (many tool calls) would otherwise grow the tree unbounded and push
          the meta/sources below it out of reach. `scrollbar-gutter: stable`
          keeps the right-aligned elapsed times from jumping when the
          scrollbar appears. */}
      <div
        data-testid="details-stepper-scroll"
        className="flex max-h-[40vh] flex-col gap-0.5 overflow-y-auto [scrollbar-gutter:stable]"
      >
        <StepsTree
          steps={turn.steps}
          streaming={anyStepRunning}
          {...(turn.toolCalls ? { toolCalls: turn.toolCalls } : {})}
          {...(turn.reasoningLevel ? { reasoningLevel: turn.reasoningLevel } : {})}
        />
      </div>
      {hasContent && hasMeta && <MetaStrip turn={turn} embedded />}
      {turn.memoryApplied !== undefined &&
        turn.memoryApplied > 0 &&
        (turn.memoryAppliedTexts && turn.memoryAppliedTexts.length > 0 ? (
          <details
            className="group mt-1 text-label-2 text-muted-foreground"
            data-testid="memory-applied"
          >
            <summary className="cursor-pointer list-none hover:text-foreground">
              <span className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
              <MemoryAppliedLabel count={turn.memoryApplied} />
            </summary>
            {/* Snapshot of the texts as injected into THIS turn — deliberately
             * not resolved against the live memory (historical fidelity). */}
            <ul className="mt-1 list-disc space-y-0.5 pl-6">
              {turn.memoryAppliedTexts.map((text, i) => (
                // Index keys are fine: the snapshot list is static per turn.
                // biome-ignore lint/suspicious/noArrayIndexKey: static snapshot
                <li key={i} className="whitespace-pre-wrap break-words">
                  {text}
                </li>
              ))}
            </ul>
          </details>
        ) : (
          // Turns persisted before the snapshot existed: count-only, no expansion.
          <p className="mt-1 text-label-2 text-muted-foreground" data-testid="memory-applied">
            <MemoryAppliedLabel count={turn.memoryApplied} />
          </p>
        ))}
      {onJumpToResearchTrigger ? (
        <button
          type="button"
          onClick={onJumpToResearchTrigger}
          className="mt-1 w-fit rounded px-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          ↑ ver consulta original
        </button>
      ) : null}
      {hasContent && sourcesCount > 0 && (
        <SourcesBlock
          citations={turn.citations}
          compact
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      )}
    </div>
  ));
}

// Shared by the expandable (snapshot) and count-only variants of the memory
// indicator so the copy and the link target cannot drift between them.
function MemoryAppliedLabel({ count }: { count: number }): JSX.Element {
  return (
    <>
      Memoria: {count} {count === 1 ? "preferencia aplicada" : "preferencias aplicadas"} ·{" "}
      <Link
        to="/account/memory"
        className="underline-offset-2 hover:text-foreground hover:underline"
      >
        ver memoria
      </Link>
    </>
  );
}

function MetaStrip({
  turn,
  embedded = false,
}: {
  turn: ChatTurn;
  /** When rendered inside `DetailsPanel`, the dashed top border that
   * normally separates the strip from the answer prose reads as a
   * stray divider between two halves of the same widget — the
   * `details` chip already establishes the panel boundary. Drop it
   * (and tighten the top margin) when embedded. The legacy fallback
   * path that mounts the strip free-standing still uses the border so
   * it doesn't visually collide with the answer text above it. */
  embedded?: boolean;
}): JSX.Element {
  return (
    <div className={embedded ? "mt-2" : "mt-4 border-t border-dashed border-border pt-2"}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-label-2 text-muted-foreground">
        {turn.model && (
          <>
            <span className="font-medium text-foreground">
              {turn.model.display_name ?? turn.model.model_id}
            </span>
            {turn.model.fallback_used && (
              <Badge variant="muted" className="rounded-sm px-1.5 py-0 text-label-2">
                fallback
              </Badge>
            )}
            <span>·</span>
            <span>{turn.model.provider}</span>
          </>
        )}
        {!turn.model && turn.backgroundUsage && (
          <span className="font-medium text-foreground">{turn.backgroundUsage.model}</span>
        )}
        {(() => {
          // Pipeline total: sum the per-step usage so the strip reflects
          // the whole turn's cost (router + answer + fact_capture), not
          // just the answer LLM. Fall back to the answer-only `turn.usage`
          // for turns persisted before per-step usage existed. Effort
          // level is intentionally omitted here — it's shown in the
          // stepper's `eval effort` row.
          const stepTotals = sumStepsUsage(turn.steps);
          const totals =
            stepTotals ??
            (turn.backgroundUsage
              ? {
                  promptTokens: turn.backgroundUsage.promptTokens,
                  completionTokens: turn.backgroundUsage.completionTokens,
                  costUsd: turn.backgroundUsage.costUsd,
                }
              : null) ??
            (turn.usage
              ? {
                  promptTokens: turn.usage.prompt_tokens,
                  completionTokens: turn.usage.completion_tokens,
                  costUsd: turn.usage.cost_usd ?? 0,
                }
              : null);
          if (!totals) return null;
          // Prompt-cache reads are summed across the per-step usage (each
          // already summed across that step's tool-loop rounds), matching
          // how in/out/cost are summed — the per-turn `usage` event only
          // held the LAST round's count and under-reported on multi-round
          // turns. Fall back to `turn.usage` only when there are no steps
          // (the same fallback the in/out/cost total uses above).
          const cacheRead = stepTotals?.cacheRead ?? turn.usage?.cache_read_input_tokens ?? 0;
          return (
            <>
              {(turn.model || turn.backgroundUsage) && <span>·</span>}
              <UsagePills totals={{ ...totals, cacheRead }} />
            </>
          );
        })()}
      </div>
    </div>
  );
}

const TOKEN_COUNT_FORMAT = new Intl.NumberFormat("en-US");

function UsagePills({
  totals,
}: {
  totals: {
    promptTokens: number;
    completionTokens: number;
    costUsd: number;
    /** Per-turn prompt-cache read tokens; renders a `cached` pill when > 0. */
    cacheRead?: number;
  };
}): JSX.Element {
  const pill = "rounded-sm border border-border px-1.5 py-px text-label-2 leading-none";
  const cacheRead = totals.cacheRead ?? 0;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className={pill}>{TOKEN_COUNT_FORMAT.format(totals.promptTokens)} in</span>
      <span className={pill}>{TOKEN_COUNT_FORMAT.format(totals.completionTokens)} out</span>
      {cacheRead > 0 && <span className={pill}>{TOKEN_COUNT_FORMAT.format(cacheRead)} cached</span>}
      <span className={pill}>{formatCost(totals.costUsd)}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Background-research turn affordances (`add-background-research-agent` L4)
// ---------------------------------------------------------------------------

/** Bidirectional links between a background report and the turn that delegated
 * it, keyed by task id. A report turn carries `backgroundTaskId`; the trigger
 * turn carries the same id as a `delegate_research` tool-call `ref`. */
export interface ResearchLinks {
  /** task id → id of the report turn (the persisted assistant message). */
  reportTurnIdByTask: Map<string, string>;
  /** task id → the report's terminal status, so the trigger turn can decide
   * whether a "ver informe" link is worth showing. */
  reportStatusByTask: Map<string, NonNullable<ChatTurn["backgroundStatus"]>>;
  /** task id → id of the trigger turn (the one that called delegate_research). */
  triggerTurnIdByTask: Map<string, string>;
}

export function deriveResearchLinks(turns: ChatTurn[]): ResearchLinks {
  const reportTurnIdByTask = new Map<string, string>();
  const reportStatusByTask = new Map<string, NonNullable<ChatTurn["backgroundStatus"]>>();
  const triggerTurnIdByTask = new Map<string, string>();
  for (const turn of turns) {
    if (turn.backgroundTaskId) {
      reportTurnIdByTask.set(turn.backgroundTaskId, turn.id);
      if (turn.backgroundStatus)
        reportStatusByTask.set(turn.backgroundTaskId, turn.backgroundStatus);
    }
    for (const tc of turn.toolCalls ?? []) {
      if (tc.name === "delegate_research" && tc.ref) {
        triggerTurnIdByTask.set(tc.ref, turn.id);
      }
    }
  }
  return { reportTurnIdByTask, reportStatusByTask, triggerTurnIdByTask };
}

export function terminalBackgroundTaskIds(turns: ChatTurn[]): Set<string> {
  const ids = new Set<string>();
  for (const turn of turns) {
    if (turn.backgroundTaskId) ids.add(turn.backgroundTaskId);
  }
  return ids;
}

export function activeBackgroundTaskIds(turns: ChatTurn[]): Set<string> {
  const terminalIds = terminalBackgroundTaskIds(turns);
  const ids = new Set<string>();
  for (const turn of turns) {
    for (const taskId of delegatedTaskIds(turn)) {
      if (!terminalIds.has(taskId)) ids.add(taskId);
    }
  }
  return ids;
}

export function shouldShowResumeBanner(
  activeSession: ActiveSession,
  sessionId: string | undefined,
  dismissedResumeId: string | null,
): boolean {
  return (
    activeSession !== null &&
    activeSession.reason === "in_flight" &&
    activeSession.session_id !== sessionId &&
    activeSession.session_id !== dismissedResumeId
  );
}

/** One delegated investigation of the session, for the index bar above the
 * composer. `status` is `running` until its report lands, then mirrors the
 * report's terminal status (so a cancelled/failed task isn't shown as a
 * successful "done"). `scrollTargetId` is the report turn (terminal) or the
 * triggering turn (running). */
export interface SessionInvestigation {
  taskId: string;
  label: string;
  status: "running" | "done" | "truncated" | "cancelled" | "failed";
  scrollTargetId: string;
  /** Final run wall-clock (seconds), set once the report lands. */
  elapsedSeconds?: number;
  /** Epoch ms the task started, for the live counter. Derived from the
   * triggering turn's `created_at` when available (survives reload). */
  startedAtMs?: number;
}

function parseEpochMs(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function elapsedSecondsBetween(
  startMs: number | undefined,
  endMs: number | undefined,
): number | undefined {
  if (startMs === undefined || endMs === undefined || endMs < startMs) return undefined;
  return (endMs - startMs) / 1000;
}

function maxElapsedSeconds(...values: (number | undefined)[]): number | undefined {
  const valid = values.filter((value): value is number => value !== undefined);
  if (valid.length === 0) return undefined;
  return Math.max(...valid);
}

function performanceAnchorFromEpochStart(startedAtMs: number | undefined): number {
  const now = performance.now();
  if (startedAtMs === undefined || Number.isNaN(startedAtMs)) return now;
  return now - Math.max(0, Date.now() - startedAtMs);
}

/** Derive the session's investigations from its turns: one per
 * `delegate_research` tool-call (deduped by task id), resolved against the
 * report turn that carries the same task id. Pure — no fetch. */
export function deriveSessionInvestigations(turns: ChatTurn[]): SessionInvestigation[] {
  const reportByTask = new Map<string, ChatTurn>();
  for (const t of turns) {
    if (t.backgroundTaskId) reportByTask.set(t.backgroundTaskId, t);
  }
  const out: SessionInvestigation[] = [];
  const seen = new Set<string>();
  for (const turn of turns) {
    for (const tc of turn.toolCalls ?? []) {
      if (tc.name !== "delegate_research" || !tc.ref || seen.has(tc.ref)) continue;
      seen.add(tc.ref);
      const label =
        typeof tc.input.instruction === "string" && tc.input.instruction.trim().length > 0
          ? tc.input.instruction.trim()
          : "Investigación en segundo plano";
      const report = reportByTask.get(tc.ref);
      const startedAtMs = parseEpochMs(turn.created_at);
      if (report) {
        const visibleElapsedSeconds = elapsedSecondsBetween(
          startedAtMs,
          parseEpochMs(report.created_at),
        );
        const elapsedSeconds = maxElapsedSeconds(
          report.backgroundElapsedSeconds,
          visibleElapsedSeconds,
        );
        out.push({
          taskId: tc.ref,
          label,
          status: report.backgroundStatus ?? "done",
          scrollTargetId: report.id,
          ...(elapsedSeconds !== undefined ? { elapsedSeconds } : {}),
          ...(startedAtMs !== undefined ? { startedAtMs } : {}),
        });
      } else {
        out.push({
          taskId: tc.ref,
          label,
          status: "running",
          scrollTargetId: turn.id,
          ...(startedAtMs !== undefined ? { startedAtMs } : {}),
        });
      }
    }
  }
  return out;
}

/** Whether a terminal background task produced a report worth linking to.
 * A `cancelled` (and `failed`) run returns from the sub-agent loop without the
 * final synthesis turn, so its "report" is an empty stub or a mid-investigation
 * preamble — never a real report. Only `done` / `truncated` synthesize one. */
function hasRealReport(status: NonNullable<ChatTurn["backgroundStatus"]> | undefined): boolean {
  return status === "done" || status === "truncated";
}

/** The task ids a turn delegated via `delegate_research` (the tool-call refs). */
function delegatedTaskIds(turn: ChatTurn): string[] {
  return (turn.toolCalls ?? []).flatMap((tc) =>
    tc.name === "delegate_research" && tc.ref ? [tc.ref] : [],
  );
}

/** Research-specific extras rendered under an assistant turn, outside the
 * memoized `AssistantTurn` so the cross-turn link maps stay in `ChatInner`:
 * - a report turn gets a model/cost footer + "jump to the originating turn";
 * - a trigger turn gets a "researching…" chip (with cancel) per in-flight task,
 *   replaced by a "jump to the report" link once the report lands. */
function ResearchTurnExtras({
  turn,
  links,
  onJump,
  onCancel,
}: {
  turn: ChatTurn;
  links: ResearchLinks;
  onJump: (turnId: string) => void;
  onCancel: (taskId: string) => Promise<void>;
}): JSX.Element | null {
  if (turn.backgroundTaskId) {
    return null;
  }
  const taskIds = delegatedTaskIds(turn);
  if (taskIds.length === 0) return null;
  const rows = taskIds.flatMap((taskId) => {
    const reportId = links.reportTurnIdByTask.get(taskId);
    // Still in flight → "researching…" chip with cancel.
    if (!reportId) {
      return [<ResearchingChip key={taskId} onCancel={() => onCancel(taskId)} />];
    }
    // Report landed, but a cancelled/failed run produced no real report — no
    // point linking to an empty stub.
    if (!hasRealReport(links.reportStatusByTask.get(taskId))) return [];
    return [<ResearchReportLink key={taskId} onJump={() => onJump(reportId)} />];
  });
  if (rows.length === 0) return null;
  return <div className="mt-2 flex flex-col items-start gap-1.5">{rows}</div>;
}

function ResearchingChip({ onCancel }: { onCancel: () => Promise<void> }): JSX.Element {
  const [cancelling, setCancelling] = useState(false);
  return (
    <div className="inline-flex w-fit items-center gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2 text-body-3 text-foreground">
      <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin text-muted-foreground" />
      <span className="font-medium">investigando en segundo plano…</span>
      <button
        type="button"
        disabled={cancelling}
        onClick={() => {
          setCancelling(true);
          void onCancel();
        }}
        className="rounded px-1.5 py-0.5 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
      >
        {cancelling ? "cancelando…" : "cancelar"}
      </button>
    </div>
  );
}

function ResearchReportLink({ onJump }: { onJump: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onJump}
      className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-2 py-1 text-label-2 text-muted-foreground hover:bg-black/[0.025] hover:text-foreground dark:hover:bg-white/[0.025]"
    >
      ver informe ↓
    </button>
  );
}

interface ResearchDetail {
  taskId: string;
  label: string;
  status: SessionInvestigation["status"];
  triggerTurn: ChatTurn | null;
  reportTurn: ChatTurn | null;
  progressTurn: ChatTurn | null;
  elapsedSeconds?: number;
  startedAtMs?: number;
}

function researchInstructionFromTrigger(triggerTurn: ChatTurn | null, taskId: string): string {
  for (const call of triggerTurn?.toolCalls ?? []) {
    if (call.name !== "delegate_research" || call.ref !== taskId) continue;
    const instruction = call.input.instruction;
    if (typeof instruction === "string" && instruction.trim().length > 0) {
      return instruction.trim();
    }
  }
  return "Investigacion en segundo plano";
}

export function researchDetailForTask(
  turns: ChatTurn[],
  links: ResearchLinks,
  taskId: string | null,
  progressByTask: Map<string, BackgroundProgressEvent> = new Map(),
): ResearchDetail | null {
  if (taskId === null) return null;
  const triggerId = links.triggerTurnIdByTask.get(taskId);
  const reportId = links.reportTurnIdByTask.get(taskId);
  const triggerTurn = triggerId ? (turns.find((turn) => turn.id === triggerId) ?? null) : null;
  const reportTurn = reportId ? (turns.find((turn) => turn.id === reportId) ?? null) : null;
  const progress = reportTurn === null ? progressByTask.get(taskId) : undefined;
  const status = reportTurn?.backgroundStatus ?? "running";
  const label = researchInstructionFromTrigger(triggerTurn, taskId);
  const startedAtMs = parseEpochMs(triggerTurn?.created_at);
  const visibleElapsedSeconds = elapsedSecondsBetween(
    startedAtMs,
    parseEpochMs(reportTurn?.created_at),
  );
  const elapsedSeconds = maxElapsedSeconds(
    reportTurn?.backgroundElapsedSeconds,
    visibleElapsedSeconds,
  );
  return {
    taskId,
    label,
    status,
    triggerTurn,
    reportTurn,
    progressTurn: progress ? researchProgressTurn(progress, startedAtMs) : null,
    ...(elapsedSeconds !== undefined ? { elapsedSeconds } : {}),
    ...(startedAtMs !== undefined ? { startedAtMs } : {}),
  };
}

function progressStepDetail(
  detail: BackgroundProgressEvent["steps"][number]["detail"],
): StepState["detail"] | undefined {
  if (detail === undefined) return undefined;
  const out: NonNullable<StepState["detail"]> = {};
  if (detail.hits !== undefined) out.hits = detail.hits;
  if (detail.bytes !== undefined) out.bytes = detail.bytes;
  if (detail.stage !== undefined) out.stage = detail.stage;
  if (detail.fused !== undefined) out.fused = detail.fused;
  if (detail.error !== undefined) out.error = detail.error;
  if (detail.decision !== undefined) out.decision = detail.decision;
  if (detail.reason !== undefined) out.reason = detail.reason;
  return Object.keys(out).length > 0 ? out : undefined;
}

function researchProgressTurn(
  progress: BackgroundProgressEvent,
  startedAtMs: number | undefined,
): ChatTurn {
  const startedAt = performanceAnchorFromEpochStart(startedAtMs);
  const separator = progress.model?.indexOf(":") ?? -1;
  const model =
    progress.model && separator > 0
      ? {
          provider: progress.model.slice(0, separator),
          model_id: progress.model.slice(separator + 1),
        }
      : undefined;
  return {
    id: `research-detail-progress-${progress.task_id}`,
    role: "assistant",
    content:
      progress.content.trim().length > 0
        ? progress.content
        : "La investigacion esta en curso. El proceso se actualiza en vivo.",
    thinking: "",
    citations: progress.citations.map((citation) => ({
      id: citation.id,
      title: citation.title,
      preview: citation.preview,
      user_metadata: citation.user_metadata ?? null,
      ...(citation.source !== undefined ? { source: citation.source } : {}),
    })),
    steps: progress.steps.map((step): StepState => {
      const elapsed = step.elapsed_ms;
      const detail = progressStepDetail(step.detail);
      return {
        id: step.id,
        status: step.status,
        startedAt: step.status === "running" ? startedAt : 0,
        ...(elapsed !== undefined ? { elapsedMs: elapsed } : {}),
        ...(detail !== undefined ? { detail } : {}),
      };
    }),
    ...(progress.tool_calls.length > 0
      ? {
          toolCalls: progress.tool_calls.map((call) => ({
            id: call.id,
            name: call.name,
            input: call.input,
            ...(call.ok !== undefined ? { ok: call.ok } : {}),
            ...(call.bytes !== undefined ? { bytes: call.bytes } : {}),
            ...(call.content_preview !== undefined
              ? { content_preview: call.content_preview }
              : {}),
            ...(call.error !== undefined ? { error: call.error } : {}),
            ...(call.ref !== undefined ? { ref: call.ref } : {}),
            ...(call.stage !== undefined ? { stage: call.stage } : {}),
            ...(call.ref_fallback !== undefined ? { ref_fallback: call.ref_fallback } : {}),
            ...(call.tried_refs !== undefined ? { tried_refs: call.tried_refs } : {}),
          })),
        }
      : {}),
    ...(model ? { model } : {}),
    backgroundTaskId: progress.task_id,
    startedAt,
  };
}

function researchInstructionTurn(detail: ResearchDetail): ChatTurn {
  return {
    id: `research-detail-user-${detail.taskId}`,
    role: "user",
    content: detail.label,
    thinking: "",
    citations: [],
    steps: [],
  };
}

function researchPendingTurn(detail: ResearchDetail): ChatTurn {
  const statusText =
    detail.status === "running"
      ? "La investigacion sigue en curso. El informe aparecera aqui cuando termine."
      : "La investigacion no produjo un informe final disponible.";
  return {
    id: `research-detail-status-${detail.taskId}`,
    role: "assistant",
    content: statusText,
    thinking: "",
    citations: [],
    steps: [],
  };
}

function researchStatusLabel(status: ResearchDetail["status"]): string {
  switch (status) {
    case "running":
      return "en curso";
    case "done":
      return "completa";
    case "truncated":
      return "truncada";
    case "cancelled":
      return "cancelada";
    case "failed":
      return "fallida";
  }
}

function fullReportForTurn(turn: ChatTurn): {
  body: string;
  status: "complete" | "truncated";
  reason?: string;
} | null {
  const metadataReport = turn.backgroundReport;
  const body = stripFullReportHeading(metadataReport?.fullReport ?? turn.content);
  if (!body) return null;
  const status =
    metadataReport?.fullReportStatus ??
    (turn.backgroundStatus === "truncated" ? "truncated" : "complete");
  return {
    body,
    status,
    ...(metadataReport?.truncationReason ? { reason: metadataReport.truncationReason } : {}),
  };
}

function stripFullReportHeading(report: string): string {
  const lines = report.trim().split(/\r?\n/);
  const first = lines[0]?.trim().toLowerCase();
  if (
    first === "# informe completo" ||
    first === "## informe completo" ||
    first === "informe completo:"
  ) {
    return lines.slice(1).join("\n").trim();
  }
  return report.trim();
}

function ResearchFullReportSection({
  turn,
  onSourceClick,
}: { turn: ChatTurn; onSourceClick?: (sourceId: string) => void }): JSX.Element | null {
  const fullReport = fullReportForTurn(turn);
  const markdownComponents = useMemo(
    // A rendered research report is read-only history — never a live guided
    // question — so its chips (if any) are inert.
    () => makeMarkdownComponents(turn.citations, `${turn.id}-full-report`, false),
    [turn.citations, turn.id],
  );
  const citationCtx = useMemo(() => ({ onSourceClick: onSourceClick ?? null }), [onSourceClick]);
  // A completed research report is never streaming — the fence fallback
  // should render its SVG immediately, same as history replay.
  const mermaidStreamingCtx = useMemo(() => ({ generating: false }), []);
  if (fullReport === null) return null;

  return (
    <section className="border-t border-border pt-4">
      <h3 className="mb-2 text-heading-4 font-semibold">Informe completo</h3>
      {fullReport.status === "truncated" ? (
        <p className="mb-3 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-body-3 text-amber-200">
          Informe parcial: la investigacion se corto antes de completar el reporte.
          {fullReport.reason ? ` Motivo: ${fullReport.reason}.` : ""}
        </p>
      ) : null}
      <div className="prose prose-invert max-w-none text-body-2">
        <MermaidStreamingContext.Provider value={mermaidStreamingCtx}>
          <CitationDetailContext.Provider value={citationCtx}>
            <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
              {fullReport.body}
            </ReactMarkdown>
          </CitationDetailContext.Provider>
        </MermaidStreamingContext.Provider>
      </div>
    </section>
  );
}

// Research detail body (selection logic + status + turns). Shared by the dock
// detail view (>= xl) and the ResearchDetailSheet lightbox (< xl / expanded).
export function ResearchDetailContent({
  taskId,
  turns,
  links,
  progressByTask = new Map(),
  onSourceClick,
}: {
  taskId: string | null;
  turns: ChatTurn[];
  links: ResearchLinks;
  progressByTask?: Map<string, BackgroundProgressEvent>;
  onSourceClick?: (sourceId: string) => void;
}): JSX.Element {
  const detail = researchDetailForTask(turns, links, taskId, progressByTask);
  const reportTurn = detail?.reportTurn ?? null;
  const progressTurn = detail?.progressTurn ?? null;
  const terminalWithReport = reportTurn !== null && hasRealReport(reportTurn.backgroundStatus);
  const assistantTurn = terminalWithReport
    ? reportTurn
    : reportTurn !== null && detail !== null
      ? researchPendingTurn(detail)
      : progressTurn !== null
        ? progressTurn
        : detail !== null
          ? researchPendingTurn(detail)
          : null;
  const hasLiveProgress = !terminalWithReport && progressTurn !== null;
  const elapsedText =
    detail?.elapsedSeconds !== undefined ? formatTotalElapsed(detail.elapsedSeconds * 1000) : null;

  if (detail === null || assistantTurn === null) {
    return (
      <p className="text-body-3 text-muted-foreground">
        No se encontro el detalle de esta investigacion.
      </p>
    );
  }
  return (
    <div className="mx-auto max-w-xl space-y-6 py-2">
      <p className="text-body-3 text-muted-foreground">
        {researchStatusLabel(detail.status)}
        {elapsedText ? ` · ${elapsedText}` : ""}
      </p>
      {/* Same pairing as main chat so the sticky user card can pin over the answer. */}
      <div className="space-y-4" data-turn-group="research-detail">
        <UserTurn turn={researchInstructionTurn(detail)} />
        <AssistantTurn
          turn={assistantTurn}
          streaming={false}
          isLatestTurn={false}
          defaultDetailsExpanded={terminalWithReport || hasLiveProgress}
          hideResearchDetailButton
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      </div>
      {terminalWithReport ? (
        <ResearchFullReportSection
          turn={reportTurn}
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      ) : null}
    </div>
  );
}

export function ResearchDetailSheet({
  open,
  onOpenChange,
  taskId,
  turns,
  links,
  progressByTask = new Map(),
  onSourceClick,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string | null;
  turns: ChatTurn[];
  links: ResearchLinks;
  progressByTask?: Map<string, BackgroundProgressEvent>;
  onSourceClick?: (sourceId: string) => void;
  wide?: boolean;
}): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} ariaLabel="Detalle de investigacion">
      <SheetContent
        side="right"
        onClose={() => onOpenChange(false)}
        className={wide ? "w-full sm:max-w-3xl" : "w-full sm:max-w-2xl"}
      >
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Microscope aria-hidden="true" className="size-4 text-muted-foreground" />
            <SheetTitle>Investigacion</SheetTitle>
          </div>
        </SheetHeader>
        <SheetBody className="bg-background px-3 sm:px-4">
          <ResearchDetailContent
            taskId={taskId}
            turns={turns}
            links={links}
            progressByTask={progressByTask}
            {...(onSourceClick ? { onSourceClick } : {})}
          />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function ThinkingBlock({ text, streaming }: { text: string; streaming: boolean }): JSX.Element {
  return (
    <details className="group mb-3 text-label-2">
      <summary className="cursor-pointer list-none text-muted-foreground hover:text-foreground">
        <span className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
        <span className="uppercase tracking-wider">razonamiento</span>
        {streaming && <span className="ml-2 italic text-muted-foreground">pensando…</span>}
      </summary>
      <div className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap border-l-2 border-border pl-3 text-body-3 italic text-muted-foreground">
        {text}
      </div>
    </details>
  );
}

function ToolCallBlock({
  call,
  onRendered,
}: {
  call: ToolCall;
  /** Forwarded to `MermaidBlock` for `render_diagram` calls so the
   * step row can swap the backend's near-zero shape-check timing for
   * the wall-clock the operator actually waited on. */
  onRendered?: (renderMs: number) => void;
}): JSX.Element {
  const display = describeTool(call);
  const pending = call.ok === undefined;
  const isDiagram = call.name === "render_diagram";
  const showDiagram = isDiagram && call.ok === true && !!call.content_preview;
  return (
    // Diagrams default open — the chart IS the deliverable, collapsing it
    // would hide what the tool was called to produce. Other tool blocks
    // stay collapsed.
    <details className="group mb-3 text-label-2" open={isDiagram || undefined}>
      <summary className="cursor-pointer list-none text-muted-foreground hover:text-foreground">
        <span className="mr-1 inline-block transition-transform group-open:rotate-90">▸</span>
        <span className="mr-1">{display.icon}</span>
        <span className="font-mono">
          {display.label}
          {display.target ? `: ${display.target}` : ""}
        </span>
        {pending && <span className="ml-2 italic">consultando…</span>}
        {!pending && call.ok === false && (
          <span className="ml-2 text-destructive">· {call.error ?? "error"}</span>
        )}
        {!pending && call.ok && display.sizeText && (
          <span className="ml-2 italic">· {display.sizeText}</span>
        )}
        {!pending && display.refText && <span className="ml-2 italic">· {display.refText}</span>}
      </summary>
      {showDiagram ? (
        <MermaidBlock
          code={call.content_preview ?? ""}
          id={`tool-${call.id}`}
          {...(onRendered ? { onRendered } : {})}
        />
      ) : (
        <div className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap border-l-2 border-border pl-3 font-mono text-body-3 text-muted-foreground">
          {call.ok === false ? `${call.error ?? "unknown error"}` : (call.content_preview ?? "")}
        </div>
      )}
    </details>
  );
}

/** The `model_routing` step reasons under which the backend actually swaps
 * the answering LLM away from the allowlist default (see
 * `resolve_routed_model` / `orchestrator.py`). Mirrors that condition set so
 * the live label only ever moves when the routed model is what actually
 * answers the turn. */
const APPLIED_ROUTING_REASONS: ReadonlySet<string> = new Set(["routed", "small_talk", "skip_late"]);

type StepStreamEvent = Extract<ChatStreamEvent, { type: "step" }>;

/** Resolve the model a `model_routing` step should patch onto the live turn,
 * or `null` when the decision keeps the optimistic default — shadow mode, an
 * override, `already_default`, or any fallback reason (`model_unavailable`,
 * `tier_unassigned`, `router_error`, `flag_disabled`, ...). `display_name`
 * prefers the catalog-sourced value the backend stamps on `selected` (so the
 * live label equals the post-reload label, which resolves the same catalog
 * name); it falls back to the user's chat allowlist, then to the raw
 * `model_id` (see fix-live-routed-model-visibility). Exported as a pure
 * function so the applied/not-applied decision is unit-testable without
 * `setTurns` plumbing. */
export function resolveRoutedTurnModel(
  detail: StepStreamEvent["detail"],
  allowed: readonly ChatAllowedForUser[],
): TurnModel | null {
  if (!detail || detail.shadow !== false) return null;
  if (detail.reason === undefined || !APPLIED_ROUTING_REASONS.has(detail.reason)) return null;
  const selected = detail.selected;
  if (!selected) return null;
  const displayName =
    selected.display_name ??
    findAllowlistDisplayName(selected.provider, selected.model_id, allowed);
  return {
    provider: selected.provider,
    model_id: selected.model_id,
    ...(displayName ? { display_name: displayName } : {}),
  };
}

/** Resolve the turn's served model from the `answer` step's usage payload
 * (`step:answer:done`). Delegates the `provider:model_id` parsing (split at
 * the FIRST colon — model ids contain `/` and may contain `:`) and the
 * allowlist display-name lookup to `resolveTurnModel`, adding a stricter
 * malformed-input guard (empty provider or model_id → null, so callers keep
 * the existing model) and the `fallback_used` flag when the backend stamped
 * it — the live meta strip then renders the badge at stream completion.
 * Accepted limitation: a served model outside the user's allowlist with no
 * prior display_name renders its raw `model_id` live (the backend catalog
 * name is only available after a rehydration). Exported for unit tests. */
export function resolveServedTurnModel(
  usage: StepUsageWire,
  allowed: readonly ChatAllowedForUser[],
): TurnModel | null {
  const idx = usage.model.indexOf(":");
  if (idx <= 0 || idx === usage.model.length - 1) return null;
  const resolved = resolveTurnModel(usage.model, allowed);
  if (!resolved) return null;
  return {
    ...resolved,
    ...(usage.fallback_used ? { fallback_used: true } : {}),
  };
}

/** Reduce one `step` SSE event onto a single turn.
 *
 * `start` opens a running row (deduped, except repeatable `tool.<name>`
 * dispatches, which always get a fresh row). `done`/`error` closes the
 * matching running row — or, when there is none, appends a new completed row
 * instead of discarding the event: single-shot steps like `model_routing`
 * and `retrieval.skipped` are emitted done-only and would otherwise be
 * silently dropped live (they already survive a reload via the persisted
 * `metadata.steps` snapshot). `model_routing` steps additionally patch the
 * turn's live `model` via `resolveRoutedTurnModel`. Exported so the reducer
 * is unit-testable without `setTurns` plumbing. */
export function applyStepEventToTurn(
  turn: ChatTurn,
  event: StepStreamEvent,
  allowed: readonly ChatAllowedForUser[],
): ChatTurn {
  const existing = turn.steps;
  const isToolStep = event.id.startsWith("tool.");
  const routedModel =
    event.id === "model_routing" ? resolveRoutedTurnModel(event.detail, allowed) : null;
  // The `answer` step's usage names the leg that ACTUALLY served (including
  // failover fallbacks), so its patch overrides the earlier model_routing
  // patch — which only carries the routing DECISION (`selected`). It arrives
  // later in the stream, so a plain per-event patch suffices.
  const servedResolved =
    event.id === "answer" && event.status === "done" && event.usage !== undefined
      ? resolveServedTurnModel(event.usage, allowed)
      : null;
  // Never downgrade an already-known label for the SAME model: the routing
  // patch carries the backend-stamped catalog display_name (see
  // fix-live-routed-model-visibility), which the served resolution can't see
  // (it only has the user's allowlist). The served patch wins on identity +
  // fallback_used, but keeps the existing display_name when its own lookup
  // found none and the turn's model is the same provider/model_id.
  const servedModel =
    servedResolved &&
    !servedResolved.display_name &&
    turn.model?.display_name &&
    turn.model.provider === servedResolved.provider &&
    turn.model.model_id === servedResolved.model_id
      ? { ...servedResolved, display_name: turn.model.display_name }
      : servedResolved;
  const patchModel = servedModel ?? routedModel;
  const modelPatch = patchModel ? { model: patchModel } : {};

  if (event.status === "start") {
    // Tool dispatches CAN repeat the same id (the LLM may invoke
    // `github_list_dir` 5 times in a turn) — always append a fresh
    // entry. Other ids should never repeat in a single turn; stay
    // defensive and ignore duplicate starts.
    if (!isToolStep && existing.some((s) => s.id === event.id)) return turn;
    return {
      ...turn,
      ...modelPatch,
      steps: [
        ...existing,
        {
          id: event.id,
          status: "running" as const,
          startedAt: performance.now(),
        },
      ],
    };
  }
  // status === "done" | "error". Match the LAST `running` entry with
  // this id so successive tool dispatches resolve to their own row.
  let targetIdx = -1;
  for (let i = existing.length - 1; i >= 0; i--) {
    const candidate = existing[i];
    if (candidate?.id === event.id && candidate.status === "running") {
      targetIdx = i;
      break;
    }
  }
  const status = event.status === "done" ? ("done" as const) : ("error" as const);
  if (targetIdx === -1) {
    // No matching `start` — a single-shot step. Append it as completed,
    // preserving arrival order, instead of dropping the event.
    return {
      ...turn,
      ...modelPatch,
      steps: [
        ...existing,
        {
          id: event.id,
          status,
          startedAt: 0,
          ...(event.elapsed_ms !== undefined ? { elapsedMs: event.elapsed_ms } : {}),
          ...(event.detail !== undefined ? { detail: event.detail } : {}),
          ...(event.usage !== undefined ? { usage: parseStepUsage(event.usage) } : {}),
        },
      ],
    };
  }
  return {
    ...turn,
    ...modelPatch,
    steps: existing.map((s, i) =>
      i === targetIdx
        ? {
            ...s,
            status,
            ...(event.elapsed_ms !== undefined ? { elapsedMs: event.elapsed_ms } : {}),
            ...(event.detail !== undefined ? { detail: event.detail } : {}),
            ...(event.usage !== undefined ? { usage: parseStepUsage(event.usage) } : {}),
          }
        : s,
    ),
  };
}

function applyEvent(
  assistantId: string,
  event: ChatStreamEvent,
  setSessionId: (id: string) => void,
  setTurns: (updater: (prev: ChatTurn[]) => ChatTurn[]) => void,
  allowed: readonly ChatAllowedForUser[],
): void {
  setTurns((prev) =>
    prev.map((turn) => {
      if (turn.id !== assistantId) return turn;
      switch (event.type) {
        case "token":
          return { ...turn, content: turn.content + event.content };
        case "thinking":
          return { ...turn, thinking: turn.thinking + event.content };
        case "reasoning_level":
          return { ...turn, reasoningLevel: event.level };
        case "usage":
          return {
            ...turn,
            usage: {
              prompt_tokens: event.prompt_tokens,
              completion_tokens: event.completion_tokens,
              total_tokens: event.total_tokens,
              ...(event.cost_usd !== undefined ? { cost_usd: event.cost_usd } : {}),
              ...(event.cache_read_input_tokens !== undefined
                ? { cache_read_input_tokens: event.cache_read_input_tokens }
                : {}),
              ...(event.cache_creation_input_tokens !== undefined
                ? { cache_creation_input_tokens: event.cache_creation_input_tokens }
                : {}),
            },
          };
        case "citation":
          return {
            ...turn,
            citations: [
              ...turn.citations,
              {
                id: event.id,
                title: event.title,
                preview: event.preview,
                user_metadata: event.user_metadata ?? null,
                ...(event.source !== undefined ? { source: event.source } : {}),
                ...(event.source_ref !== undefined ? { source_ref: event.source_ref } : {}),
                ...(event.external_url !== undefined ? { external_url: event.external_url } : {}),
              },
            ],
          };
        case "retrieval_summary":
          return {
            ...turn,
            retrieval: {
              vector: event.vector_hits,
              bm25: event.bm25_hits,
              graph: event.graph_hits,
              sparse: event.sparse_hits,
              fused: event.fused_count,
            },
          };
        case "fact_proposal":
          return addFactProposalToTurn(turn, {
            draft_id: event.draft_id,
            text: event.text,
            status: "pending",
            detected_at: new Date().toISOString(),
            assistant_message_id: event.assistant_message_id,
          });
        case "user_memory_draft":
          return addUserMemoryDraftToTurn(turn, {
            draft_id: event.draft_id,
            operation: event.operation,
            status: "pending",
            detected_at: new Date().toISOString(),
            assistant_message_id: event.assistant_message_id,
            ...(event.text !== undefined ? { text: event.text } : {}),
            ...(event.target_id !== undefined ? { target_id: event.target_id } : {}),
            ...(event.target_text !== undefined ? { target_text: event.target_text } : {}),
          });
        case "stage_access_request":
          return {
            ...turn,
            stageAccessRequest: { stage: event.stage, status: "pending" },
          };
        case "learned_fact":
          return {
            ...turn,
            learnedFactId: event.knowledge_item_id,
            learnedFactAutoApproved: event.auto_approved ?? false,
          };
        case "tool_use":
          return {
            ...turn,
            toolCalls: [
              ...(turn.toolCalls ?? []),
              { id: event.id, name: event.name, input: event.input },
            ],
          };
        case "tool_result": {
          const updated = (turn.toolCalls ?? []).map((tc) =>
            tc.id === event.tool_use_id
              ? {
                  ...tc,
                  ok: event.ok,
                  bytes: event.bytes,
                  content_preview: event.content_preview,
                  ...(event.error ? { error: event.error } : {}),
                  ...(event.ref ? { ref: event.ref } : {}),
                  ...(event.stage ? { stage: event.stage } : {}),
                  ...(event.ref_fallback !== undefined ? { ref_fallback: event.ref_fallback } : {}),
                  ...(event.tried_refs ? { tried_refs: event.tried_refs } : {}),
                }
              : tc,
          );
          return { ...turn, toolCalls: updated };
        }
        case "citation_warning":
          return {
            ...turn,
            fabricatedCitations: [...(turn.fabricatedCitations ?? []), ...event.fabricated_markers],
          };
        case "source_redaction_warning":
          return {
            ...turn,
            sourceRedactions: [...(turn.sourceRedactions ?? []), ...event.redacted_spans],
          };
        case "user_memory_applied":
          return {
            ...turn,
            memoryApplied: event.count,
            ...(event.texts && event.texts.length > 0 ? { memoryAppliedTexts: event.texts } : {}),
          };
        case "truncated":
          return { ...turn, truncated: event.reason };
        case "report_ready": {
          const { reportPublishFailed: _cleared, ...rest } = turn;
          return {
            ...rest,
            messageId: event.assistant_message_id,
            reportRef: {
              report_id: event.report_id,
              revision_number: event.revision_number,
              title: event.title,
              status: event.status,
            },
          };
        }
        case "report_publish_failed":
          return {
            ...turn,
            reportPublishFailed: true,
          };
        case "story_preparation_ready":
          return {
            ...turn,
            storyPreparationRef: {
              preparation_id: event.preparation_id,
              revision_number: event.revision_number,
              state: event.state,
              title: event.title,
              operation: event.operation,
              target_story_public_id: event.target_story_public_id,
            },
          };
        case "response_assets":
          // Sibling field, never concatenated into `turn.content` — this is
          // what keeps `CopyButton` (fed `turn.content`) and the rendered
          // markdown free of asset data. Also captures `assistant_message_id`
          // here (not only on `done`): `response_assets` commits and streams
          // before `done` per the backend contract, and the byte routes the
          // grid calls need the persisted message id to resolve relations.
          return {
            ...turn,
            messageId: event.assistant_message_id,
            responseAssets: event.assets,
          };
        case "done":
          // The terminal event ends this foreground turn even when a
          // provider omitted a step's finish event. Preserve explicit errors
          // and measured timings; do not invent durations for missing ones.
          return {
            ...turn,
            ...(event.assistant_message_id !== undefined
              ? { messageId: event.assistant_message_id }
              : {}),
            steps: turn.steps.map((step) =>
              step.status === "running" ? { ...step, status: "done", startedAt: 0 } : step,
            ),
          };
        case "error":
          return {
            ...turn,
            error: event.message,
            ...(event.code !== undefined ? { errorCode: event.code } : {}),
          };
        case "step":
          return applyStepEventToTurn(turn, event, allowed);
        default:
          return turn;
      }
    }),
  );
  if (event.type === "session") setSessionId(event.session_id);
}

/** Convert a persisted message payload into one or more in-memory turns.
 * Currently 1:1, kept as a list shape so future grouping (e.g. assistant
 * with thinking blocks) is a one-line change. */
interface MessageToTurnsOptions {
  rehydrateGenerating?: boolean;
}

export function messageToTurns(
  msg: ChatMessageRead,
  options: MessageToTurnsOptions = {},
): ChatTurn[] {
  if (msg.role === "system") return []; // not rendered in the chat view
  const rehydrateGenerating = options.rehydrateGenerating ?? true;
  // Rehydrate the assistant-side meta strip (model / usage / retrieval /
  // reasoning) from the persisted `metadata` blob. safeParse so a future
  // backend shape change degrades gracefully instead of breaking history.
  const metaParse = msg.metadata ? assistantMetadataSchema.safeParse(msg.metadata) : null;
  const meta = metaParse?.success ? metaParse.data : null;
  const persistedFactProposals =
    meta?.fact_proposals ?? (meta?.fact_proposal ? [meta.fact_proposal] : []);
  const factProposals: FactProposalState[] = persistedFactProposals.map((proposal) => ({
    draft_id: proposal.draft_id,
    text: proposal.text,
    status: proposal.status,
    detected_at: proposal.detected_at,
    assistant_message_id: msg.id,
    ...(proposal.kind !== undefined ? { kind: proposal.kind } : {}),
    ...(proposal.scope !== undefined ? { scope: proposal.scope } : {}),
    ...(proposal.decided_at ? { decided_at: proposal.decided_at } : {}),
    ...(proposal.knowledge_item_id ? { knowledge_item_id: proposal.knowledge_item_id } : {}),
  }));
  const firstFactProposal = factProposals[0];
  const persistedUserMemoryDrafts =
    meta?.user_memory_drafts ?? (meta?.user_memory_draft ? [meta.user_memory_draft] : []);
  const userMemoryDrafts: UserMemoryDraftState[] = persistedUserMemoryDrafts.map((draft) => ({
    draft_id: draft.draft_id,
    operation: draft.operation,
    status: draft.status,
    detected_at: draft.detected_at,
    assistant_message_id: msg.id,
    ...(draft.text !== undefined ? { text: draft.text } : {}),
    ...(draft.target_id !== undefined ? { target_id: draft.target_id } : {}),
    ...(draft.target_text !== undefined ? { target_text: draft.target_text } : {}),
    ...(draft.decided_at ? { decided_at: draft.decided_at } : {}),
    ...(draft.memory_item_id ? { memory_item_id: draft.memory_item_id } : {}),
  }));
  const firstUserMemoryDraft = userMemoryDrafts[0];
  return [
    {
      id: msg.id,
      messageId: msg.id,
      role: msg.role,
      content: msg.content,
      thinking: "",
      minimap_label: msg.minimap_label ?? null,
      created_at: msg.created_at,
      ...(msg.answer_rating ? { answerRating: msg.answer_rating } : {}),
      ...(msg.knowledge_feedback ? { knowledgeFeedback: msg.knowledge_feedback } : {}),
      citations: (msg.citations ?? []).map((c) => ({
        id: c.id,
        title: c.title,
        preview: c.preview ?? "",
        user_metadata: c.user_metadata ?? null,
        ...(c.source !== undefined ? { source: c.source } : {}),
        ...(c.source_ref !== undefined ? { source_ref: c.source_ref } : {}),
        ...(c.external_url !== undefined ? { external_url: c.external_url } : {}),
      })),
      ...(meta?.model
        ? {
            model: {
              provider: meta.model.provider,
              model_id: meta.model.model_id,
              ...(meta.model.display_name ? { display_name: meta.model.display_name } : {}),
              ...(meta.model.fallback_used ? { fallback_used: true } : {}),
            },
          }
        : {}),
      ...(meta?.usage
        ? {
            usage: {
              prompt_tokens: meta.usage.prompt_tokens,
              completion_tokens: meta.usage.completion_tokens,
              total_tokens: meta.usage.total_tokens,
              ...(meta.usage.cost_usd !== undefined ? { cost_usd: meta.usage.cost_usd } : {}),
              ...(meta.usage.cache_read_input_tokens !== undefined
                ? { cache_read_input_tokens: meta.usage.cache_read_input_tokens }
                : {}),
              ...(meta.usage.cache_creation_input_tokens !== undefined
                ? { cache_creation_input_tokens: meta.usage.cache_creation_input_tokens }
                : {}),
            },
          }
        : {}),
      ...(meta?.retrieval ? { retrieval: meta.retrieval } : {}),
      ...(meta?.reasoning_level ? { reasoningLevel: meta.reasoning_level } : {}),
      ...(meta?.reasoning_preference ? { reasoningPreference: meta.reasoning_preference } : {}),
      ...(meta?.tool_calls && meta.tool_calls.length > 0
        ? {
            toolCalls: meta.tool_calls.map((tc) => ({
              id: tc.id,
              name: tc.name,
              input: tc.input,
              ok: tc.ok,
              bytes: tc.bytes,
              content_preview: tc.content_preview,
              ...(tc.error ? { error: tc.error } : {}),
              ...(tc.ref ? { ref: tc.ref } : {}),
              ...(tc.stage ? { stage: tc.stage } : {}),
              ...(tc.ref_fallback !== undefined ? { ref_fallback: tc.ref_fallback } : {}),
              ...(tc.tried_refs ? { tried_refs: tc.tried_refs } : {}),
            })),
          }
        : {}),
      ...(meta?.fabricated_citations && meta.fabricated_citations.length > 0
        ? { fabricatedCitations: meta.fabricated_citations }
        : {}),
      ...(meta?.source_redactions && meta.source_redactions.length > 0
        ? { sourceRedactions: meta.source_redactions }
        : {}),
      ...(meta?.user_memory_applied ? { memoryApplied: meta.user_memory_applied } : {}),
      ...(meta?.user_memory_applied_texts && meta.user_memory_applied_texts.length > 0
        ? { memoryAppliedTexts: meta.user_memory_applied_texts }
        : {}),
      ...(meta?.truncated ? { truncated: meta.truncated } : {}),
      ...(meta?.cancelled ? { cancelled: true } : {}),
      ...(meta?.failed ? { failed: true } : {}),
      ...(meta?.error ? { failedError: meta.error } : {}),
      ...(meta?.generation_failed ? { generationFailed: true } : {}),
      ...(meta?.generation_failed && meta?.reason ? { generationFailedReason: meta.reason } : {}),
      ...(rehydrateGenerating && meta?.generating ? { generating: true } : {}),
      ...(meta?.background_task_id ? { backgroundTaskId: meta.background_task_id } : {}),
      ...(meta?.background_status ? { backgroundStatus: meta.background_status } : {}),
      ...(meta?.report_ref ? { reportRef: meta.report_ref } : {}),
      ...(meta?.story_preparation ? { storyPreparationRef: meta.story_preparation } : {}),
      ...(meta?.background_report
        ? {
            backgroundReport: {
              summary: meta.background_report.summary,
              fullReport: meta.background_report.full_report,
              fullReportStatus: meta.background_report.full_report_status,
              ...(meta.background_report.truncation_reason
                ? { truncationReason: meta.background_report.truncation_reason }
                : {}),
            },
          }
        : {}),
      ...(meta?.background_elapsed_seconds !== undefined
        ? { backgroundElapsedSeconds: meta.background_elapsed_seconds }
        : {}),
      ...(meta?.background_usage
        ? {
            backgroundUsage: {
              model: meta.background_usage.model,
              promptTokens: meta.background_usage.prompt_tokens,
              completionTokens: meta.background_usage.completion_tokens,
              costUsd: meta.background_usage.cost_usd,
            },
          }
        : {}),
      steps: (meta?.steps ?? []).map(
        (s): StepState => ({
          id: s.id,
          status: s.status,
          startedAt: 0,
          ...(s.elapsed_ms !== undefined ? { elapsedMs: s.elapsed_ms } : {}),
          ...(s.detail !== undefined
            ? {
                detail: {
                  ...(s.detail.hits !== undefined ? { hits: s.detail.hits } : {}),
                  ...(s.detail.bytes !== undefined ? { bytes: s.detail.bytes } : {}),
                  ...(s.detail.fused !== undefined ? { fused: s.detail.fused } : {}),
                  ...(s.detail.error !== undefined ? { error: s.detail.error } : {}),
                  // Decision-style steps (e.g. retrieval.graph_routing) carry
                  // their verdict here; propagate it so the ran/skipped chip
                  // and its reason tooltip survive a session reload, matching
                  // the live SSE path.
                  ...(s.detail.decision !== undefined ? { decision: s.detail.decision } : {}),
                  ...(s.detail.reason !== undefined ? { reason: s.detail.reason } : {}),
                  // model_routing carries its tier / selected model / shadow
                  // flag here; propagate them so the routing detail chips
                  // survive a session reload, matching the live SSE path.
                  ...(s.detail.tier !== undefined ? { tier: s.detail.tier } : {}),
                  ...(s.detail.selected !== undefined
                    ? {
                        selected: {
                          provider: s.detail.selected.provider,
                          model_id: s.detail.selected.model_id,
                          // Copied per-field (not wholesale) so an absent
                          // display_name stays absent under
                          // exactOptionalPropertyTypes rather than becoming
                          // `undefined`.
                          ...(s.detail.selected.display_name !== undefined
                            ? { display_name: s.detail.selected.display_name }
                            : {}),
                        },
                      }
                    : {}),
                  ...(s.detail.shadow !== undefined ? { shadow: s.detail.shadow } : {}),
                  // question_classification's intent chip survives a reload.
                  ...(s.detail.intent !== undefined ? { intent: s.detail.intent } : {}),
                  ...(s.detail.decided_by !== undefined ? { decided_by: s.detail.decided_by } : {}),
                },
              }
            : {}),
          ...(s.usage !== undefined ? { usage: parseStepUsage(s.usage) } : {}),
        }),
      ),
      ...(meta?.fact_proposal
        ? {
            factProposal: {
              draft_id: meta.fact_proposal.draft_id,
              text: meta.fact_proposal.text,
              status: meta.fact_proposal.status,
              detected_at: meta.fact_proposal.detected_at,
              // msg.id IS the assistant_message_id — the persisted row's
              // primary key. Hydrate it here so the card can use the real
              // DB id for lifecycle endpoint calls on reload.
              assistant_message_id: msg.id,
              ...(meta.fact_proposal.decided_at
                ? { decided_at: meta.fact_proposal.decided_at }
                : {}),
              ...(meta.fact_proposal.knowledge_item_id
                ? { knowledge_item_id: meta.fact_proposal.knowledge_item_id }
                : {}),
            },
          }
        : {}),
      ...(firstFactProposal !== undefined
        ? { factProposal: firstFactProposal, factProposals }
        : {}),
      ...(firstUserMemoryDraft !== undefined
        ? { userMemoryDraft: firstUserMemoryDraft, userMemoryDrafts }
        : {}),
      ...(msg.attachments && msg.attachments.length > 0 ? { attachments: msg.attachments } : {}),
      ...(msg.response_assets && msg.response_assets.length > 0
        ? { responseAssets: msg.response_assets }
        : {}),
    },
  ];
}

function parseSelectionToOverride(
  value: string,
): { provider: string; model_identifier: string } | null {
  if (value === AUTO_VALUE) return null;
  const idx = value.indexOf(":");
  if (idx === -1) return null;
  return {
    provider: value.slice(0, idx),
    model_identifier: value.slice(idx + 1),
  };
}

/** Look up the allowlist `display_name` for a provider/model pair, or
 * `undefined` when the pair isn't present in the user's allowlist (callers
 * then fall back to the raw `model_id`). Shared by `resolveTurnModel`
 * (composer selection) and `resolveRoutedTurnModel` (live `model_routing`
 * step decisions) — both resolve the same allowlist shape. */
function findAllowlistDisplayName(
  provider: string,
  model_id: string,
  allowed: readonly ChatAllowedForUser[],
): string | undefined {
  return (
    allowed.find((m) => m.provider === provider && m.model_id === model_id)?.display_name ??
    undefined
  );
}

function resolveTurnModel(
  selected: string,
  allowed: readonly ChatAllowedForUser[],
): TurnModel | null {
  if (selected === AUTO_VALUE) {
    const def = allowed.find((m) => m.is_default);
    if (!def) return null;
    return {
      provider: def.provider,
      model_id: def.model_id,
      ...(def.display_name ? { display_name: def.display_name } : {}),
    };
  }
  const idx = selected.indexOf(":");
  if (idx === -1) return null;
  const provider = selected.slice(0, idx);
  const model_id = selected.slice(idx + 1);
  const displayName = findAllowlistDisplayName(provider, model_id, allowed);
  return {
    provider,
    model_id,
    ...(displayName ? { display_name: displayName } : {}),
  };
}

export default function ChatPage(): JSX.Element {
  return (
    <RequireRole roles={ROLES}>
      <ChatInner />
    </RequireRole>
  );
}
